import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import WalletRechargePanel from '../components/wallet/WalletRechargePanel';
import ClientTransactionHistory from '../components/wallet/ClientTransactionHistory';
import useSessionTimeout from '../hooks/useSessionTimeout';
import { useLanguage } from '../context/LanguageContext';
import { formatTranslation } from '../i18n/formatTranslation';
import LanguageSelector from '../components/LanguageSelector';
import { generatePremiumClientInvoice } from '../utils/ledger/generatePremiumClientInvoice';
import { generatePremiumClientBookingReport } from '../utils/ledger/generatePremiumClientBookingReport';
import { generateClientBookingHistoryPDF } from '../utils/ledger/generateClientBookingHistoryPDF';
import MobileNav from '../components/MobileNav';
import BookingCalendar from '../components/BookingCalendar';
import { useWindowSize } from '../hooks/useWindowSize';
import { useDeviceLayout } from '../hooks/useDeviceLayout';
import { PanelLeft, PanelLeftClose } from 'lucide-react';
import { logoutAndRedirect } from '../utils/authLogout';
import { formatClientType } from '../utils/clientTypeUtils';
import { sendBookingConfirmation } from '../emailService';
import { sendBookingNotifications, sendWalletNotification } from '../whatsappService';
import { getMachines, createBooking, createContractQuotation, acceptContractQuotationTerms, cancelContractQuotation, clientEsignAndPayQuotation, getContractQuotation, confirmClientArrival, getWalletBalance, getMyBookingsPage, getMyTransactionsPage, submitBookingFeedback, cancelClientBooking, acknowledgeTerms, getSavedAddresses, saveAddress, secureFetch } from '../supabaseService';
import { appendUniqueById } from '../utils/pagination';
import { buildMachineLookup, getBookingMachineCode, getBookingMachineLabel } from '../utils/bookingDisplay';
import { hasBookingFeedback, mergeBookingFeedbackFields, saveLocalBookingFeedback } from '../utils/bookingFeedback';
import BookingFlowV2Card from '../components/booking/BookingFlowV2Card';
import DurationHmPicker from '../components/booking/DurationHmPicker';
import { bookingNeedsLiveSync, runningElapsedMs } from '../services/bookingFlowApi';
import PushNotificationToggle from '../components/notifications/PushNotificationToggle';
import NotificationCenter from '../components/NotificationCenter';
import {
  computeBookingQuote,
  attachmentMinHoursNotice,
  attachmentUsageHoursGateError,
  attachmentDurationFeasibilityError,
  readAttachmentMinHours,
  bookedJobHoursEquiv,
  spotHourlyTotalFloorError,
  ATTACHMENT_HOURS_SANITY_MAX,
} from '../utils/bookingPayment';
import { computeBookingTaxQuote } from '../utils/gst/gstRules';
import { formatHoursHm } from '../utils/durationHours';
import { ATTACHMENT_LABEL_BY_ID } from '../utils/machineRegistrationConfig';
import PhoneChangeModal from '../components/PhoneChangeModal';
import PortalAvatar from '../components/PortalAvatar';
import ClientKycForm from '../components/ClientKycForm';
import ContractBookingStatus from '../components/booking/ContractBookingStatus';
import { deriveContractMoneyBreakdown, formatContractInr } from '../utils/contractBookingDisplay';
import { loadProfilePhoto } from '../utils/profilePhotoStorage';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** Calendar-month add (UTC date-only). Mirrors server contractBooking.addCalendarMonths. */
function addCalendarMonths(date, months) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const targetFirst = new Date(Date.UTC(year, month + months, 1));
  const targetLastDay = new Date(Date.UTC(
    targetFirst.getUTCFullYear(),
    targetFirst.getUTCMonth() + 1,
    0,
  )).getUTCDate();
  return new Date(Date.UTC(
    targetFirst.getUTCFullYear(),
    targetFirst.getUTCMonth(),
    Math.min(day, targetLastDay),
  ));
}

function addContractDays(date, days) {
  return new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() + days,
  ));
}

function getClientTier(clientType) {
  if (['individual_residential', 'farmer', 'well_digging'].includes(clientType)) return 1;
  if (['industrial', 'road_construction', 'canal_irrigation', 'railway', 'rera_builder'].includes(clientType)) return 2;
  if (['pwd_government', 'msme_commercial'].includes(clientType)) return 3;
  return 1;
}

function defaultBillingBasisFromClientType(clientType) {
  const type = String(clientType || '').trim().toLowerCase();
  if (type === 'individual_residential' || type === 'farmer' || type === 'well_digging') {
    return 'calendar_month';
  }
  if (
    type === 'industrial'
    || type === 'road_construction'
    || type === 'canal_irrigation'
    || type === 'railway'
    || type === 'rera_builder'
    || type === 'pwd_government'
    || type === 'msme_commercial'
  ) {
    return 'working_days_26';
  }
  return 'calendar_month';
}

function billingBasisChipLabel(basis) {
  return String(basis || '') === 'working_days_26'
    ? '🏗 26 Working Days'
    : '📅 Calendar Month';
}

function parseContractDateOnly(value) {
  const text = String(value || '').trim().slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

function savedAddressOptionLabel(row) {
  const address = String(row?.address || '').trim();
  const label = String(row?.label || '').trim();
  const pin = String(row?.pincode || '').trim();
  const main = label || address || 'Saved address';
  const extra = label && address ? address : pin;
  const text = extra ? `${main} — ${extra}` : main;
  return text.length > 80 ? `${text.slice(0, 77)}…` : text;
}

function countContractCycles(startDateValue, endDateValue, cycle, billingBasis) {
  const start = parseContractDateOnly(startDateValue);
  const end = parseContractDateOnly(endDateValue);
  if (!start || !end || end < start) return null;
  const basis = String(billingBasis || 'calendar_month').trim().toLowerCase();
  if (basis === 'working_days_26') {
    const daysPer = String(cycle || '').toLowerCase() === 'quarterly' ? 78 : 26;
    let cycles = 1;
    while (addContractDays(start, (cycles + 1) * daysPer) <= end) {
      cycles += 1;
    }
    return cycles;
  }
  const cycleMonths = String(cycle || '').toLowerCase() === 'quarterly' ? 3 : 1;
  let cycles = 1;
  while (addCalendarMonths(start, (cycles + 1) * cycleMonths) <= end) {
    cycles += 1;
  }
  return cycles;
}

/** Suggested attachment_rate_per_cycle from machines.attachment_rates (₹/hr extra) × GMH. */
function suggestAttachmentRatePerCycle(machine, attachmentId, guaranteedHours) {
  if (!attachmentId || !machine) return null;
  const rates = machine.attachmentRates && typeof machine.attachmentRates === 'object'
    ? machine.attachmentRates
    : {};
  const hourly = Number(rates[attachmentId]);
  const gmh = Number(guaranteedHours);
  if (!(hourly > 0) || !(gmh > 0)) return null;
  return Math.ceil(hourly * gmh);
}

function formatGstPctLabel(rate) {
  const n = Number(rate);
  const pct = Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 18;
  return `GST @ ${pct}%`;
}

const CONTACT_NUMBER_RE = /(\+91|91)?[6-9]\d{9}/;
const PINCODE_RE = /^\d{6}$/;
const SITE_CONTACT_MSG = "Please don't share contact numbers — all communication happens through the platform.";

function getJobsBadge(count) {
  const n = Number(count) || 0;
  if (n >= 20) return { label: `★ ${n} jobs`, color: '#C9A84C' };
  if (n >= 5) return { label: `${n} jobs`, color: '#e8e0d0' };
  return { label: 'New', color: '#8896a8' };
}

function MachineListingCard({
  m,
  s,
  t,
  clientKycBookingBlocked,
  clientKycBookingBlockMessage,
  selectMachineForBooking,
  showType = false,
}) {
  const isAvailable = Boolean(m.available);
  const busy = !isAvailable;
  const best = Boolean(m.isBestMatch);
  const jobsBadge = getJobsBadge(m.settledJobsCount);
  const perHour = Number(m.rates?.perHour) || 0;
  const perMonth = Number(m.rates?.perMonth) || 0;
  const distance = Number.isFinite(Number(m.distanceKm)) ? Number(m.distanceKm) : null;

  return (
    <div
      className="box-border flex w-full min-w-0 flex-col justify-between"
      style={{
        ...s.machineCard,
        padding: '14px',
        border: best ? '2px solid #C9A84C' : '1px solid rgba(201,168,76,0.18)',
        opacity: busy ? 0.7 : 1,
      }}
    >
      <div>
        {best ? (
          <span style={{
            display: 'inline-block',
            marginBottom: 8,
            padding: '2px 8px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            background: 'rgba(201,168,76,0.15)',
            color: '#C9A84C',
            border: '1px solid rgba(201,168,76,0.45)',
          }}
          >
            🏆 Best match
          </span>
        ) : null}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
          {distance !== null ? (
            <span style={{ fontSize: 11, color: '#8896a8' }}>📍 {distance} km</span>
          ) : null}
          <span style={{ fontSize: 11, color: jobsBadge.color, fontWeight: 600 }}>
            ✅ {jobsBadge.label}
          </span>
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: isAvailable ? '#4CAF50' : '#FF9800',
          }}
          >
            {isAvailable ? '● ' : '◉ '}
            {isAvailable ? t('available') : 'On Job'}
          </span>
        </div>

        <p style={{ margin: 0, color: '#e8e0d0', fontSize: 15, fontWeight: 500, lineHeight: 1.3 }}>
          {m.name}
        </p>
        <p style={{ margin: '2px 0 0', color: '#8896a8', fontSize: 12 }}>
          {[m.regNo || m.code, m.year].filter(Boolean).join(' · ') || '—'}
          {showType && m.type ? ` · ${m.type}` : ''}
        </p>

        <div style={{ height: 1, background: 'rgba(201,168,76,0.18)', margin: '10px 0' }} />

        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
        }}
        >
          <span style={{ color: '#e8e0d0', fontSize: 18, fontWeight: 500 }}>
            ₹{perHour.toLocaleString('en-IN')}
            <span style={{ fontSize: 12, color: '#8896a8' }}>/hr</span>
          </span>
          {perMonth > 0 ? (
            <span style={{ color: '#8896a8', fontSize: 12 }}>
              ₹{perMonth.toLocaleString('en-IN')}/month
            </span>
          ) : null}
        </div>

        <p style={{ margin: '8px 0 0', color: '#8896a8', fontSize: 12, lineHeight: 1.4 }}>
          ⏱ Minimum {m.minimumHours || 3} hrs
          {m.operatorIncluded !== false
            ? ' · Operator included'
            : (m.operatorDailyRate
              ? ` · Operator ₹${Number(m.operatorDailyRate).toLocaleString('en-IN')}/day extra`
              : ' · Operator not included')}
        </p>

        {m.below_minimum_hours && (
          <div style={{
            fontSize: 11,
            color: '#FF9800',
            marginTop: 8,
            padding: '4px 8px',
            background: 'rgba(255,152,0,0.08)',
            borderRadius: 6,
            border: '1px solid rgba(255,152,0,0.2)',
          }}>
            ⚠ {t('minimumHoursWarning') === 'minimumHoursWarning'
              ? `Minimum ${m.minimumHours || m.minimum_hours} hours required for this machine`
              : t('minimumHoursWarning')}
          </div>
        )}
      </div>

      {isAvailable ? (
        <button
          type="button"
          style={{
            marginTop: 12,
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: best ? 700 : 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            ...(best
              ? { background: 'linear-gradient(135deg, #c9a84c, #8b6914)', color: '#0a1628', border: 'none' }
              : { background: 'rgba(201,168,76,0.12)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.5)' }),
            ...(clientKycBookingBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          }}
          disabled={clientKycBookingBlocked}
          title={clientKycBookingBlocked ? clientKycBookingBlockMessage : undefined}
          onClick={() => selectMachineForBooking(m)}
        >
          {best ? 'Book this machine →' : 'View details'}
        </button>
      ) : (
        <button
          type="button"
          disabled
          style={{
            ...s.selectBtn,
            marginTop: '12px',
            padding: '9px',
            opacity: 0.55,
            cursor: 'not-allowed',
            background: 'rgba(136,150,168,0.25)',
            color: '#8896a8',
          }}
        >
          Currently Busy
        </button>
      )}
    </div>
  );
}

function AccountStatusPill({ status }) {
  const key = String(status || 'active').toLowerCase();
  const tones = {
    active: { background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)', label: '● Active' },
    pending: { background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', label: '● Pending' },
    rejected: { background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', label: '● Rejected' },
    inactive: { background: 'rgba(156,163,175,0.15)', color: '#9ca3af', border: '1px solid rgba(156,163,175,0.3)', label: '● Inactive' },
  };
  const tone = tones[key] || tones.active;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        marginLeft: '8px',
        background: tone.background,
        color: tone.color,
        border: tone.border,
        whiteSpace: 'nowrap',
      }}
    >
      {tone.label}
    </span>
  );
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const CLIENT_CHART_GOLD = '#C9A84C';
const CLIENT_CHART_GOLD_LIGHT = '#f5e6b8';
const CLIENT_CHART_GRID = 'rgba(201, 168, 76, 0.15)';
const CLIENT_CHART_AXIS = '#8896a8';
const CLIENT_CHART_HEIGHT = 280;

function formatClientChartSpend(value) {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
  return `₹${Number(value || 0).toLocaleString('en-IN')}`;
}

function fmtClientInr(amount) {
  const n = Number(amount) || 0;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatBookingAttachmentSummary(b) {
  if (!b?.selected_attachment) return '';
  const name = ATTACHMENT_LABEL_BY_ID[b.selected_attachment] || b.selected_attachment;
  const parts = [name];
  if (b.attachment_usage_hours) parts.push(formatHoursHm(b.attachment_usage_hours));
  if (b.attachment_hourly_rate) parts.push(`${fmtClientInr(b.attachment_hourly_rate)}/hr`);
  return parts.join(' · ');
}

function useClientChartWidth() {
  const [width, setWidth] = useState(320);
  const ref = React.useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect?.width || 0);
      if (next > 0) setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, width: Math.max(width, 280) };
}

function ClientAnalyticsTooltip({ active, payload, label, bookingsLabel }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      style={{
        background: 'linear-gradient(145deg, #0f2040, #0D1B2A)',
        border: '1px solid rgba(201, 168, 76, 0.4)',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}
    >
      <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, color: 'rgba(201,168,76,0.8)', textTransform: 'uppercase' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontSize: '16px', fontWeight: 700, color: '#c9a84c' }}>
        {formatClientChartSpend(row.spend)}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#8896a8' }}>
        {bookingsLabel}: {row.bookings ?? 0}
      </p>
    </div>
  );
}

const MACHINES = [];

const TRANSACTIONS = [
  { type: 'credit', desc: 'Wallet Recharge — UPI/PhonePe', date: '10 Apr 2026, 9:15 AM', amount: 100000, ref: 'TXN9823741' },
  { type: 'debit', desc: 'JCB-001 — Daily Booking Advance', date: '09 Apr 2026, 7:30 AM', amount: 4200, ref: 'BK001-ADV' },
  { type: 'debit', desc: 'JCB-001 — Daily Settlement', date: '09 Apr 2026, 6:00 PM', amount: 8190, ref: 'BK001-SET' },
  { type: 'debit', desc: 'EXC-002 — Daily Booking Advance', date: '07 Apr 2026, 8:00 AM', amount: 6600, ref: 'BK002-ADV' },
  { type: 'debit', desc: 'EXC-002 — Daily Settlement', date: '07 Apr 2026, 5:30 PM', amount: 12870, ref: 'BK002-SET' },
  { type: 'credit', desc: 'Wallet Recharge — NEFT', date: '01 Apr 2026, 11:00 AM', amount: 200000, ref: 'TXN8712340' },
];

const bookingQtyDisplay = (b, t) => {
  const q = (b.booking_type !== 'contract' && b.booked_hours)
    ? b.booked_hours
    : (b.quantity || b.hours);
  if (b.booking_type === 'milestone') {
    const completed = Number(b.total_units_completed || 0);
    const estimated = Number(b.total_units_estimated || 0);
    const unit = b.booking_unit_label || 'units';
    return `${completed.toLocaleString('en-IN')} / ${estimated.toLocaleString('en-IN')} ${unit}`;
  }
  if (b.site_work_type) {
    const completed = Number(b.total_depth_completed || 0);
    const estimated = Number(b.estimated_depth || 0);
    const unit = b.billing_basis === 'per_meter' ? 'm' : 'ft';
    return `${completed.toLocaleString('en-IN')} / ${estimated.toLocaleString('en-IN')} ${unit}`;
  }
  if (!q) return t('naLabel');
  if (b.booking_type === 'contract') {
    return `${q} ${q === 1 ? 'cycle' : 'cycles'}`;
  }
  const type = b.booking_unit || b.booking_type || b.type || '';
  if (type === 'hourly') {
    const n = Number(q);
    if (Number.isFinite(n) && n > 0) return formatHoursHm(n);
  }
  let unit = t('moUnit');
  if (type === 'hourly') unit = t('hrsUnit');
  else if (type === 'daily') unit = t('daysUnit');
  else if (type === 'weekly') unit = t('wksUnit');
  return `${q} ${unit}`;
};

const bookingTypeDisplay = (booking) => {
  if (booking?.site_work_type) {
    return String(booking.site_work_type).replace(/_/g, ' ');
  }
  if (booking?.booking_type === 'contract') return 'Contract';
  if (booking?.booking_type === 'milestone') return 'Milestone';
  return booking?.booking_unit || booking?.booking_type || booking?.type || '—';
};

const parseBookingDate = (b) => {
  const raw = b?.start_date || b?.created_at;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (b?.date) {
    const d = new Date(b.date);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
};

const toDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isBookingCancelled = (b) => String(b?.status || '').toLowerCase().includes('cancel');

const isBookingCompletedStatus = (b) => {
  if (isBookingCancelled(b)) return false;
  const status = String(b?.status || '').toLowerCase();
  return status === 'completed' || status === 'done';
};

const PENDING_REPORT_STATUSES = new Set([
  'Active',
  'Dispatched',
  'Extension_Pending',
  'Completing',
  'Completing_PendingPayment',
]);

const matchesReportStatusFilter = (row, filter) => {
  if (filter === 'all') return true;
  const raw = row?.raw || row;
  const status = String(raw?.status || '');
  if (filter === 'completed') {
    return isBookingCompletedStatus(raw) || status === 'Completed';
  }
  if (filter === 'cancelled') {
    return isBookingCancelled(raw)
      || status.toLowerCase().includes('cancel')
      || String(row?.status || '').toLowerCase().includes('cancel');
  }
  if (filter === 'pending') {
    return PENDING_REPORT_STATUSES.has(status);
  }
  if (filter === 'disputed') {
    return status === 'Disputed';
  }
  return true;
};

const DAY_SECTION_ORDER = [
  'sectionNew',
  'sectionAccepted',
  'sectionActive',
  'sectionDispatched',
  'sectionCompleting',
  'sectionDisputed',
  'sectionCompleted',
  'sectionClosed',
];

const getDaySectionBucket = (b) => {
  const status = String(b?.status || '');
  if (
    status === 'pending'
    || (
      !b.owner_approved
      && !isBookingCancelled(b)
      && status !== 'Active'
      && status !== 'Dispatched'
      && status !== 'Extension_Pending'
      && status !== 'Completing'
      && status !== 'Completing_PendingPayment'
      && status !== 'Completed'
      && status !== 'Disputed'
    )
  ) {
    return 'sectionNew';
  }
  if (b.owner_approved === true && status === 'Active' && status !== 'Dispatched') {
    return 'sectionAccepted';
  }
  if (status === 'Active' || status === 'Extension_Pending') {
    return 'sectionActive';
  }
  if (status === 'Dispatched') return 'sectionDispatched';
  if (status === 'Completing' || status === 'Completing_PendingPayment') {
    return 'sectionCompleting';
  }
  if (status === 'Disputed') return 'sectionDisputed';
  if (isBookingCompletedStatus(b)) return 'sectionCompleted';
  if (isBookingCancelled(b)) return 'sectionClosed';
  return 'sectionNew';
};

const groupBookingsByMonth = (bookings) => {
  const monthMap = new Map();
  bookings.forEach((b) => {
    const d = parseBookingDate(b);
    const monthKey = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const dayKey = d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' });
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, { sortTime: d.getTime(), days: new Map() });
    }
    const monthEntry = monthMap.get(monthKey);
    monthEntry.sortTime = Math.max(monthEntry.sortTime, d.getTime());
    if (!monthEntry.days.has(dayKey)) {
      monthEntry.days.set(dayKey, { sortTime: d.getTime(), bookings: [] });
    }
    const dayEntry = monthEntry.days.get(dayKey);
    dayEntry.sortTime = Math.max(dayEntry.sortTime, d.getTime());
    dayEntry.bookings.push(b);
  });
  return Array.from(monthMap.entries())
    .sort((a, b) => b[1].sortTime - a[1].sortTime)
    .map(([month, { days }]) => ({
      month,
      dayGroups: Array.from(days.entries())
        .sort((a, b) => b[1].sortTime - a[1].sortTime)
        .map(([day, { bookings: dayBookings }]) => ({ day, bookings: dayBookings })),
    }));
};

const groupReportRowsByMonth = (rows) => {
  const monthMap = new Map();
  (rows || []).forEach((row) => {
    const d = parseBookingDate(row?.raw || row);
    const monthKey = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const sortTime = d.getTime();
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, { sortTime, items: [] });
    }
    const entry = monthMap.get(monthKey);
    entry.sortTime = Math.max(entry.sortTime, sortTime);
    entry.items.push({ row, sortTime });
  });
  return Array.from(monthMap.entries())
    .sort((a, b) => b[1].sortTime - a[1].sortTime)
    .map(([month, { items }]) => {
      const sortedRows = items
        .slice()
        .sort((a, b) => b.sortTime - a.sortTime)
        .map((x) => x.row);
      return {
        month,
        rows: sortedRows,
        total: sortedRows.reduce((sum, r) => sum + Number(r.total || 0), 0),
      };
    });
};

const isBookingPendingDispatch = (b) => Boolean(b) && !b.owner_approved && !isBookingCancelled(b);

const getBookingDispatchDisplay = (b, t) => {
  if (isBookingCancelled(b)) {
    return { label: t('bookingClosed'), color: '#e94560', pending: false };
  }
  if (b.booking_type === 'contract') {
    return { label: 'Contract active', color: '#c9a84c', pending: false };
  }
  if (b.owner_approved) {
    return { label: t('dispatched'), color: '#4CAF50', pending: false };
  }
  return { label: t('awaitingDispatch'), color: '#FF9800', pending: true };
};

function getOperatorStatusBadgeConfig(operatorStatus) {
  const status = String(operatorStatus || '').trim();
  if (status === 'on_the_way') {
    return {
      label: `${String.fromCodePoint(0x1F697)} Machine On The Way`,
      background: 'rgba(255,193,7,0.12)',
      border: '1px solid rgba(255,193,7,0.45)',
      color: '#FFC107',
    };
  }
  if (status === 'arrived') {
    return {
      label: `${String.fromCodePoint(0x1F4CD)} Machine Arrived at Site`,
      background: 'rgba(76,175,80,0.12)',
      border: '1px solid rgba(76,175,80,0.45)',
      color: '#4CAF50',
    };
  }
  if (status === 'on_site') {
    return {
      label: `${String.fromCodePoint(0x1F527)} Work In Progress`,
      background: 'rgba(76,175,80,0.12)',
      border: '1px solid rgba(76,175,80,0.45)',
      color: '#4CAF50',
    };
  }
  return null;
}

function ClientOperatorStatusBadge({ booking }) {
  const badge = getOperatorStatusBadgeConfig(booking?.operator_status);
  if (!badge) return null;
  return (
    <div style={{
      padding: '8px 10px',
      background: badge.background,
      border: badge.border,
      borderRadius: '8px',
      textAlign: 'center',
      marginTop: '8px',
    }}
    >
      <span style={{ color: badge.color, fontSize: '11px', fontWeight: 700 }}>{badge.label}</span>
    </div>
  );
}

const normalizeReportBooking = (b, lookup, t) => {
  const base = Number(b.base_amount ?? b.baseAmt ?? 0);
  const taxQuote = computeBookingTaxQuote(base, {
    recipientGstin: b.client_gstin,
    siteStateCode: b.site_state_code,
  });
  const gst = Number(b.gst_amount ?? b.gst ?? taxQuote.gst);
  const total = Number(b.total_amount ?? b.total ?? taxQuote.grandTotal);
  const hours = Number(b.quantity ?? b.hours ?? 0);
  const dateStr = parseBookingDate(b).toLocaleDateString('en-IN');
  const machineLabel = getBookingMachineLabel(b, lookup);
  const machineName = b.machines?.name || b.machineFull || lookup?.get(String(b?.machine_id))?.name || b.machine || t('machineDefaultLabel');
  const ref = b.booking_ref || b.id || 'BK';
  const statusRaw = String(b.status || '').toLowerCase();
  const statusLabel = statusRaw.includes('cancel')
    ? t('statusCancelled')
    : (b.owner_approved || statusRaw === 'completed' || statusRaw === 'done' ? t('statusCompleted') : t('active'));
  return {
    id: ref,
    date: dateStr,
    machine: machineLabel,
    machineName,
    type: bookingTypeDisplay(b) || t('naLabel'),
    hours,
    baseAmt: base,
    gst,
    total,
    status: statusLabel,
    raw: b,
  };
};

const isGstInvoiceEligible = (b) => {
  const base = Number(b.base_amount ?? b.baseAmt ?? 0);
  if (base <= 0) return false;
  const status = String(b.status || '').toLowerCase();
  if (status.includes('cancel')) return false;
  return status === 'completed' || status === 'done';
};

const buildGstInvoicePayload = (b, user, t) => {
  const row = normalizeReportBooking(b, null, t);
  const now = new Date();
  const yyyymm = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const bookingRef = String(b.booking_ref || row.id || '');
  const seq = (bookingRef.replace(/\D/g, '').slice(-4) || '0001').padStart(4, '0').slice(-4);
  const hours = Number(
    b.final_hours
    || b.operator_actual_hours
    || b.quantity
    || b.hours
    || 1
  );
  const machine = Array.isArray(b.machines) ? b.machines[0] : b.machines;
  const clientGstin = String(user.gstin || '').trim();
  return {
    invoiceNo: `INV/${yyyymm}/${seq}`,
    bookingId: row.id,
    bookingRef,
    clientName: user.company || user.name || t('clientRole'),
    clientAddr: user.address || `${user.location || 'Karad, Satara'} - 415110`,
    clientGST: clientGstin,
    recipientStateCode: clientGstin.length >= 2 ? clientGstin.slice(0, 2) : null,
    clientPhone: user.phone ? (String(user.phone).startsWith('+') ? user.phone : `+91-${user.phone}`) : '',
    clientEmail: user.email || '',
    machineName: row.machineName,
    regNo: b.machine_reg_no || machine?.reg_no || '',
    operator: (b.operator && typeof b.operator === 'object' ? b.operator.name : b.operator)
      || t('assignedOperator'),
    location: b.site_address || b.location || 'Karad, Satara',
    siteAddress: b.site_address || '',
    sitePincode: b.site_pincode || '',
    workPeriod: row.date,
    hours,
    ratePerHour: Number(b.rate_per_hour || 0) > 0
      ? Number(b.rate_per_hour)
      : hours > 0
        ? Math.round(
          (Number(b.base_amount || row.baseAmt || 0))
            / hours
        )
        : 0,
    baseAmount: Number(
      b.gross_amount_final
      || b.total_amount
      || row.baseAmt
      || 0
    ) / (1 + 0.18),
    gstAmount: b.gst_amount != null ? Number(b.gst_amount) : null,
    cgstAmount: b.cgst_amount != null ? Number(b.cgst_amount) : null,
    sgstAmount: b.sgst_amount != null ? Number(b.sgst_amount) : null,
    igstAmount: b.igst_amount != null ? Number(b.igst_amount) : null,
    placeOfSupply: b.place_of_supply || null,
    supplyType: b.supply_type || null,
    advancePaid: Number(b.advance_paid ?? b.advancePaid ?? 0),
    extensionAdvances: Number(
      b.extension_advances_sum || 0
    ),
    bookingType: b.booking_type || b.bookingType || null,
    selectedAttachment: b.selected_attachment || b.selectedAttachment || null,
    attachmentUsageHours: b.attachment_usage_hours != null
      ? Number(b.attachment_usage_hours)
      : null,
    attachmentHourlyRate: (() => {
      const snap = Number(b.attachment_hourly_rate ?? b.attachmentHourlyRate);
      if (Number.isFinite(snap) && snap > 0) return snap;
      const att = b.selected_attachment || b.selectedAttachment;
      const rates = b.machines?.attachment_rates || b.attachment_rates;
      if (!att || !rates || typeof rates !== 'object') return 0;
      const n = Number(rates[att] || 0);
      return n > 0 ? n : 0;
    })(),
    attachmentLabel: b.selected_attachment || b.selectedAttachment || null,
    mobilizationTransportBy: b.mobilization_transport_by || b.mobilizationTransportBy || null,
    mobilizationTransportAmount: b.mobilization_transport_amount ?? b.mobilizationTransportAmount ?? null,
  };
};

const stripTxnDescTags = (description) => String(description || '')
  .replace(/\[[^\]]*\]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const cleanDesc = (tx, t, isCredit) => {
  const raw = String(tx?.description || '');
  const hay = raw.toLowerCase();
  const stripped = stripTxnDescTags(raw);
  let label = '';
  if (hay.includes('wallet_recharge') || hay.includes('recharge')) {
    label = t('txRecharge');
  } else if (hay.includes('booking_advance') || hay.includes('advance')) {
    label = t('txBookingAdvance');
  } else if (hay.includes('booking_payment') || hay.includes('payment')) {
    label = t('txBookingPayment');
  } else if (hay.includes('settlement') || hay.includes('settled')) {
    label = t('txSettlement');
  } else if (hay.includes('refund') || hay.includes('reversed')) {
    label = t('txRefund');
  } else if (hay.includes('penalty')) {
    label = t('txPenalty');
  } else if (hay.includes('commission')) {
    label = t('txCommission');
  } else {
    label = stripped || (isCredit ? t('walletCredit') : t('walletDebit'));
  }
  const ref = String(tx?.reference || '').trim();
  if (ref && ref !== t('naLabel')) {
    label = `${label} · ${ref.slice(-8)}`;
  }
  return label;
};

const formatTransactionRow = (tx, t) => {
  const isCredit = String(tx?.type || '').toLowerCase() === 'credit';
  const amount = Number(tx?.amount || 0);
  const createdAt = tx?.created_at ? new Date(tx.created_at) : null;
  const text = String(tx?.description || '').toLowerCase();
  let status = 'captured';
  if (text.includes('[failed]')) status = 'failed';
  else if (text.includes('[reversed]') || text.includes('refund')) status = 'reversed';
  const validDate = createdAt && !Number.isNaN(createdAt.getTime()) ? createdAt : null;
  return {
    id: tx?.id,
    type: isCredit ? 'credit' : 'debit',
    desc: cleanDesc(tx, t, isCredit),
    date: validDate ? validDate.toLocaleString('en-IN') : t('naLabel'),
    createdAt: validDate,
    sortTime: validDate ? validDate.getTime() : 0,
    timeLabel: validDate
      ? validDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      : t('naLabel'),
    amount,
    ref: tx?.reference || t('naLabel'),
    status,
  };
};

const NAV = [
  { id: 'dashboard', icon: '🏠', label: 'Dashboard', i18nKey: 'dashboardTab' },
  { id: 'newbooking', icon: String.fromCodePoint(0x1F69C), label: 'Book Machine', i18nKey: 'newBooking' },
  { id: 'mybookings', icon: String.fromCodePoint(0x1F4CB), label: 'My Bookings', i18nKey: 'myBookings' },
  { id: 'wallet', icon: String.fromCodePoint(0x1F4B3), label: 'Wallet', i18nKey: 'wallet' },
  { id: 'account', icon: '⚙️', label: 'Account', i18nKey: 'accountTab' },
];

const CLIENT_TAB_ORDER = ['dashboard', 'newbooking', 'mybookings', 'tracking', 'wallet', 'kyc', 'alerts', 'terms', 'support', 'account'];

const OFF_NAV_TABS = [
  { id: 'tracking', icon: String.fromCodePoint(0x1F4CD), label: 'Live Tracking', i18nKey: 'liveTracking' },
  { id: 'kyc', icon: '🪪', label: 'KYC', i18nKey: 'kycVerification' },
  { id: 'alerts', icon: String.fromCodePoint(0x1F514), label: 'Alerts & Notifications', i18nKey: 'alertsAndNotifications' },
  { id: 'terms', icon: String.fromCodePoint(0x1F4DC), label: 'Terms & Conditions', i18nKey: 'tacTitle' },
  { id: 'support', icon: String.fromCodePoint(0x1F198), label: 'Support', i18nKey: 'support' },
];

const BOOKING_STEPS = [
  { step: 1, icon: String.fromCodePoint(0x1F69C), i18nKey: 'bookingStepChoose' },
  { step: 2, icon: String.fromCodePoint(0x1F4B0), i18nKey: 'bookingStepCost' },
  { step: 3, icon: String.fromCodePoint(0x2705), i18nKey: 'bookingStepConfirm' },
];

const BookingStepper = ({ currentStep, isMobile, t, onStepChange, canGoToStep2, canGoToStep3 }) => (
  <div style={{
    display: 'flex',
    alignItems: 'stretch',
    gap: isMobile ? '4px' : '8px',
    marginBottom: '16px',
    padding: isMobile ? '10px 8px' : '12px 14px',
    background: 'rgba(201,168,76,0.06)',
    border: '1px solid rgba(201,168,76,0.22)',
    borderRadius: '12px',
  }}>
    {BOOKING_STEPS.map(({ step, icon, i18nKey }, idx) => {
      const done = currentStep > step;
      const active = currentStep === step;
      const disabled = (step === 2 && !canGoToStep2) || (step === 3 && !canGoToStep3);
      return (
        <React.Fragment key={step}>
          <button
            type="button"
            disabled={disabled && !done}
            onClick={() => { if (!disabled || done) onStepChange(step); }}
            style={{
              flex: 1,
              minWidth: 0,
              border: active ? '1px solid rgba(201,168,76,0.5)' : '1px solid transparent',
              background: active ? 'rgba(201,168,76,0.14)' : done ? 'rgba(76,175,80,0.08)' : 'transparent',
              borderRadius: '10px',
              padding: isMobile ? '8px 4px' : '10px 8px',
              cursor: disabled && !done ? 'default' : 'pointer',
              opacity: disabled && !done ? 0.45 : 1,
            }}
          >
            <p style={{ margin: '0 0 4px', fontSize: isMobile ? '14px' : '16px' }}>{done ? String.fromCodePoint(0x2705) : icon}</p>
            <p style={{
              margin: 0,
              fontSize: isMobile ? '8px' : '10px',
              fontWeight: active ? 700 : 600,
              color: active ? '#c9a84c' : done ? '#4CAF50' : '#8896a8',
              lineHeight: 1.25,
            }}>
              {isMobile ? `${step}. ` : ''}{t(i18nKey)}
            </p>
          </button>
          {idx < BOOKING_STEPS.length - 1 && (
            <div style={{
              alignSelf: 'center',
              width: isMobile ? '8px' : '16px',
              height: '2px',
              background: done ? 'rgba(76,175,80,0.5)' : 'rgba(201,168,76,0.2)',
              flexShrink: 0,
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const BookingFeedbackPanel = ({ booking, t, onSubmit, submitting }) => {
  const [rating, setRating] = React.useState(booking.client_operator_rating || 0);
  const [satisfied, setSatisfied] = React.useState(
    booking.client_satisfied === true ? 'yes' : booking.client_satisfied === false ? 'no' : '',
  );
  const [suggestion, setSuggestion] = React.useState(booking.client_suggestion || '');

  React.useEffect(() => {
    setRating(booking.client_operator_rating || 0);
    setSatisfied(booking.client_satisfied === true ? 'yes' : booking.client_satisfied === false ? 'no' : '');
    setSuggestion(booking.client_suggestion || '');
  }, [booking.id, booking.client_operator_rating, booking.client_satisfied, booking.client_suggestion]);

  if (!booking.owner_approved) {
    return (
      <div style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(255,152,0,0.08)', border: '1px solid rgba(255,152,0,0.25)', borderRadius: '8px' }}>
        <p style={{ color: '#FF9800', fontSize: '11px', margin: 0, fontWeight: 600 }}>{t('operatorFeedbackTitle')}</p>
        <p style={{ color: '#8896a8', fontSize: '10px', margin: '4px 0 0' }}>{t('feedbackAfterDispatch')}</p>
      </div>
    );
  }

  if (hasBookingFeedback(booking)) {
    return (
      <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px' }}>
        <p style={{ color: '#c9a84c', fontSize: '11px', fontWeight: '700', margin: '0 0 8px' }}>{t('operatorFeedbackSubmitted')}</p>
        <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 4px' }}>
          {String.fromCodePoint(0x2B50)} {booking.client_operator_rating}/5 · {booking.client_satisfied ? t('satisfiedYes') : t('satisfiedNo')}
        </p>
        {booking.client_suggestion ? (
          <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>&ldquo;{booking.client_suggestion}&rdquo;</p>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px' }}>
      <p style={{ color: '#c9a84c', fontSize: '11px', fontWeight: '700', margin: '0 0 8px' }}>{t('operatorFeedbackTitle')}</p>
      <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 6px' }}>{t('rateOperatorWork')}</p>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            style={{
              background: rating >= star ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)',
              border: rating >= star ? '1px solid #c9a84c' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '6px 10px',
              cursor: 'pointer',
              color: rating >= star ? '#c9a84c' : '#8896a8',
              fontSize: '14px',
            }}
          >
            {String.fromCodePoint(0x2B50)}
          </button>
        ))}
      </div>
      <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 6px' }}>{t('operatorSatisfactionQuestion')}</p>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        {[
          { id: 'yes', label: t('satisfiedYes'), color: '#4CAF50' },
          { id: 'no', label: t('satisfiedNo'), color: '#e94560' },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setSatisfied(opt.id)}
            style={{
              flex: '1 1 120px',
              padding: '8px',
              borderRadius: '8px',
              cursor: 'pointer',
              border: satisfied === opt.id ? `1px solid ${opt.color}` : '1px solid rgba(255,255,255,0.12)',
              background: satisfied === opt.id ? `${opt.color}22` : 'transparent',
              color: satisfied === opt.id ? opt.color : '#8896a8',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <textarea
        value={suggestion}
        onChange={(e) => setSuggestion(e.target.value)}
        placeholder={t('operatorSuggestionPlaceholder')}
        rows={3}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          background: 'rgba(0,0,0,0.35)',
          border: '1px solid rgba(201,168,76,0.25)',
          borderRadius: '8px',
          color: '#e8e0d0',
          padding: '10px',
          fontSize: '12px',
          marginBottom: '10px',
          resize: 'vertical',
        }}
      />
      <button
        type="button"
        disabled={submitting || rating < 1 || !satisfied}
        onClick={() => onSubmit(booking.id, {
          operatorRating: rating,
          satisfied: satisfied === 'yes',
          suggestion,
        })}
        style={{
          width: '100%',
          padding: '10px',
          border: 'none',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #a07830, #e2c97e)',
          color: '#0a1628',
          fontWeight: 700,
          fontSize: '12px',
          cursor: submitting || rating < 1 || !satisfied ? 'not-allowed' : 'pointer',
          opacity: submitting || rating < 1 || !satisfied ? 0.6 : 1,
        }}
      >
        {submitting ? t('loading') : t('submitOperatorFeedback')}
      </button>
    </div>
  );
};

/** Phase 11: client confirms metered attachment usage logs. */
function ClientAttachmentUsagePanel({ booking }) {
  const attachmentId = booking?.selected_attachment
    ? String(booking.selected_attachment).trim()
    : '';
  const [pendingLogs, setPendingLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const load = useCallback(async () => {
    if (!booking?.id || !attachmentId) return;
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch(
        `/api/client/bookings/${encodeURIComponent(booking.id)}/attachment/usage-logs`,
      );
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load attachment usage');
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      setPendingLogs(items.filter((row) => (
        row.client_confirmed !== true && !row.auto_confirmed_at
      )));
    } catch (err) {
      setError(err?.message || 'Could not load attachment usage');
      setPendingLogs([]);
    } finally {
      setLoading(false);
    }
  }, [booking?.id, attachmentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!booking?.id || !attachmentId) return null;

  const handleConfirm = async (logId) => {
    if (!logId || busyId) return;
    setBusyId(logId);
    setError('');
    setSuccessMsg('');
    try {
      const result = await secureFetch(
        `/api/client/bookings/${encodeURIComponent(booking.id)}/attachment/confirm-usage`,
        {
          method: 'POST',
          body: JSON.stringify({ logId }),
        },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to confirm usage');
      }
      setSuccessMsg('Usage confirmed ✅');
      setPendingLogs((prev) => prev.filter((row) => row.id !== logId));
    } catch (err) {
      setError(err?.message || 'Failed to confirm usage');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      marginTop: '10px',
      padding: '12px',
      borderRadius: '10px',
      border: '1px solid rgba(201,168,76,0.25)',
      background: 'rgba(201,168,76,0.06)',
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800, margin: 0 }}>
          Attachment usage confirmations
        </p>
        <button
          type="button"
          onClick={load}
          style={{
            background: 'transparent',
            border: '1px solid rgba(201,168,76,0.35)',
            color: '#c9a84c',
            borderRadius: '8px',
            padding: '3px 8px',
            fontSize: '10px',
            cursor: 'pointer',
          }}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 8px' }}>{error}</p>
      )}
      {successMsg && (
        <p style={{ color: '#4CAF50', fontSize: '11px', fontWeight: 700, margin: '0 0 8px', textAlign: 'center' }}>
          {successMsg}
        </p>
      )}

      {pendingLogs.length === 0 && !loading ? (
        <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>
          No attachment usage waiting for confirmation.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {pendingLogs.map((log) => {
            const name = ATTACHMENT_LABEL_BY_ID[log.attachment_id]
              || log.attachment_id
              || attachmentId;
            return (
              <div
                key={log.id}
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(201,168,76,0.18)',
                  borderRadius: '8px',
                  padding: '10px',
                }}
              >
                <p style={{ color: '#e8e0d0', fontSize: '11px', margin: '0 0 4px' }}>
                  {log.log_date || '—'} · {name}
                </p>
                <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800, margin: '0 0 8px' }}>
                  Billed {Number(log.billed_hours || 0).toFixed(2)} hr
                </p>
                <button
                  type="button"
                  disabled={busyId === log.id}
                  onClick={() => handleConfirm(log.id)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                    color: '#030810',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 800,
                    fontSize: '12px',
                    cursor: busyId === log.id ? 'wait' : 'pointer',
                    opacity: busyId === log.id ? 0.7 : 1,
                  }}
                >
                  {busyId === log.id ? 'Confirming…' : 'Confirm Attachment Usage'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ClientMilestoneUnitsPanel({ booking }) {
  const [pendingLogs, setPendingLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const load = useCallback(async () => {
    if (!booking?.id || booking.booking_type !== 'milestone') return;
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch(
        `/api/client/bookings/${encodeURIComponent(booking.id)}/milestone/logs?pendingOnly=1`,
      );
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load pending units');
      }
      setPendingLogs(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || 'Could not load pending units');
      setPendingLogs([]);
    } finally {
      setLoading(false);
    }
  }, [booking?.id, booking?.booking_type]);

  useEffect(() => {
    load();
  }, [load]);

  if (!booking || booking.booking_type !== 'milestone') return null;

  const unit = booking.booking_unit_label || 'units';
  const completed = Number(booking.total_units_completed || 0);
  const estimated = Number(booking.total_units_estimated || 0);
  const rate = Number(booking.rate_per_unit || 0);

  const handleConfirm = async (logId) => {
    if (!logId || busyId) return;
    setBusyId(logId);
    setError('');
    setSuccessMsg('');
    try {
      const result = await secureFetch(
        `/api/client/bookings/${encodeURIComponent(booking.id)}/milestone/confirm-units`,
        {
          method: 'POST',
          body: JSON.stringify({ logId }),
        },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to confirm units');
      }
      setSuccessMsg('Units confirmed ✅');
      setPendingLogs((prev) => prev.filter((row) => row.id !== logId));
    } catch (err) {
      setError(err?.message || 'Failed to confirm units');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      marginTop: '4px',
      padding: '12px',
      borderRadius: '10px',
      border: '1px solid rgba(201,168,76,0.25)',
      background: 'rgba(201,168,76,0.06)',
    }}
    >
      <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800, margin: '0 0 8px' }}>
        Milestone / unit-rate
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
        <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
          <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Unit</p>
          <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>{unit}</p>
        </div>
        <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
          <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Rate / unit</p>
          <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
            ₹{rate.toLocaleString('en-IN')}
          </p>
        </div>
        <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px', gridColumn: '1 / -1' }}>
          <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Progress</p>
          <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
            {completed.toLocaleString('en-IN')} / {estimated.toLocaleString('en-IN')} {unit}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <p style={{ color: '#8896a8', fontSize: '11px', margin: 0, fontWeight: 700 }}>
          Pending confirmations
        </p>
        <button
          type="button"
          onClick={load}
          style={{
            background: 'transparent',
            border: '1px solid rgba(201,168,76,0.35)',
            color: '#c9a84c',
            borderRadius: '8px',
            padding: '3px 8px',
            fontSize: '10px',
            cursor: 'pointer',
          }}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 8px' }}>{error}</p>
      )}
      {successMsg && (
        <p style={{ color: '#4CAF50', fontSize: '11px', fontWeight: 700, margin: '0 0 8px', textAlign: 'center' }}>
          {successMsg}
        </p>
      )}

      {pendingLogs.length === 0 && !loading ? (
        <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>No units waiting for confirmation.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {pendingLogs.map((log) => (
            <div
              key={log.id}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(201,168,76,0.18)',
                borderRadius: '8px',
                padding: '10px',
              }}
            >
              <p style={{ color: '#e8e0d0', fontSize: '11px', margin: '0 0 4px' }}>
                {log.log_date || '—'} · {Number(log.units_completed || 0).toLocaleString('en-IN')} {unit}
              </p>
              <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 8px' }}>
                Operator: {log.operator_name || '—'}
              </p>
              <button
                type="button"
                disabled={busyId === log.id}
                onClick={() => handleConfirm(log.id)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                  color: '#030810',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 800,
                  fontSize: '12px',
                  cursor: busyId === log.id ? 'wait' : 'pointer',
                  opacity: busyId === log.id ? 0.7 : 1,
                }}
              >
                {busyId === log.id ? 'Confirming…' : 'Confirm Units'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const NO_WATER_CLAUSE_TEXT = {
  en: 'I understand that if water is not found (dry bore / no water), charges for depth already completed remain payable as per agreed strata rates. No refund for dry well.',
  hi: 'मैं समझता/समझती हूँ कि यदि पानी नहीं मिला (सूखा बोर), तो पूरी की गई गहराई का शुल्क सहमत स्ट्रेटा दरों के अनुसार देय रहेगा। सूखे कुएँ पर कोई रिफंड नहीं।',
  mr: 'मी समजतो/समजते की पाणी न मिळाल्यास (कोरडा बोअर), पूर्ण केलेल्या खोलीचे शुल्क ठरलेल्या स्ट्रॅटा दरानुसार देय राहील. कोरड्या विहिरीसाठी परतावा नाही.',
};

function ClientSiteWorkPanel({ booking }) {
  const [pendingLogs, setPendingLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const load = useCallback(async () => {
    if (!booking?.id || !booking?.site_work_type) return;
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch(
        `/api/client/site-work/${encodeURIComponent(booking.id)}/strata-logs?pendingOnly=1`,
      );
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load strata logs');
      }
      setPendingLogs(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || 'Could not load strata logs');
      setPendingLogs([]);
    } finally {
      setLoading(false);
    }
  }, [booking?.id, booking?.site_work_type]);

  useEffect(() => {
    load();
  }, [load]);

  if (!booking?.site_work_type) return null;

  const depthDone = Number(booking.total_depth_completed || 0);
  const depthEst = Number(booking.estimated_depth || 0);
  const unit = booking.billing_basis === 'per_meter' ? 'm' : 'ft';

  const handleConfirm = async (logId) => {
    if (!logId || busyId) return;
    setBusyId(logId);
    setError('');
    setSuccessMsg('');
    try {
      const result = await secureFetch(
        `/api/client/site-work/${encodeURIComponent(logId)}/confirm-strata`,
        {
          method: 'POST',
          body: JSON.stringify({ logId }),
        },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to confirm depth');
      }
      setSuccessMsg('Depth confirmed');
      setPendingLogs((prev) => prev.filter((row) => row.id !== logId));
    } catch (err) {
      setError(err?.message || 'Failed to confirm depth');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{
      marginTop: '4px',
      padding: '12px',
      borderRadius: '10px',
      border: '1px solid rgba(201,168,76,0.25)',
      background: 'rgba(201,168,76,0.06)',
    }}
    >
      <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 800, margin: '0 0 8px' }}>
        Site Work — {String(booking.site_work_type).replace(/_/g, ' ')}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
        <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
          <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Billing</p>
          <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
            {String(booking.billing_basis || '—').replace(/_/g, ' ')}
          </p>
        </div>
        <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
          <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>Depth</p>
          <p style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: 700, margin: 0 }}>
            {depthDone.toLocaleString('en-IN')} / {depthEst.toLocaleString('en-IN')} {unit}
          </p>
        </div>
      </div>
      {error && <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 8px' }}>{error}</p>}
      {successMsg && <p style={{ color: '#4CAF50', fontSize: '11px', fontWeight: 700, margin: '0 0 8px' }}>{successMsg}</p>}
      {pendingLogs.length === 0 && !loading ? (
        <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>No strata logs waiting for confirmation.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {pendingLogs.map((log) => (
            <div
              key={log.id}
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(201,168,76,0.18)',
                borderRadius: '8px',
                padding: '10px',
              }}
            >
              <p style={{ color: '#e8e0d0', fontSize: '11px', margin: '0 0 4px' }}>
                {log.log_date || '—'} · {Number(log.depth_from || 0)}–{Number(log.depth_to || 0)} {unit}
                {' · '}
                {log.strata_type || '—'}
              </p>
              <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 8px' }}>
                Amount due: ₹{Number(log.amount_due || 0).toLocaleString('en-IN')}
              </p>
              <button
                type="button"
                disabled={busyId === log.id}
                onClick={() => handleConfirm(log.id)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                  color: '#030810',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 800,
                  fontSize: '12px',
                  cursor: busyId === log.id ? 'wait' : 'pointer',
                  opacity: busyId === log.id ? 0.7 : 1,
                }}
              >
                {busyId === log.id ? 'Confirming…' : 'Confirm Depth'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ClientMonthAccordion = ({
  month, dayGroups, isMobile, bookingGridCols, s, handleCancelRequest, t, machineLookup,
  onSubmitFeedback, feedbackSubmittingId, onBookingRefresh, walletBalance, onRecharge,
  onDownloadInvoice, onBookAgain, walletUser,
  pendingTerminations = {},
  onRequestTermination,
  onRequestOwnerDefault,
  onOpenArrivalMilestone,
  arrivalMilestoneBusyId = null,
}) => {
  const [open, setOpen] = React.useState(false);
  const total = dayGroups.reduce((sum, group) => sum + group.bookings.length, 0);
  const pending = dayGroups.reduce((sum, group) => sum + group.bookings.filter((b) => isBookingPendingDispatch(b)).length, 0);
  const monthBookings = dayGroups.flatMap((group) => group.bookings);
  const monthMachines = [...new Set(monthBookings.map((b) => getBookingMachineCode(b, machineLookup)))];
  const monthTotal = monthBookings.reduce((sum, b) => sum + Number(b.total_amount || b.total || 0), 0);
  return (
    <div style={{ marginBottom: '12px', border: pending > 0 ? '1px solid rgba(255,152,0,0.3)' : '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', overflow: 'hidden' }}>
      <div onClick={() => setOpen(!open)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: isMobile ? '12px 14px' : '14px 18px', background: 'linear-gradient(135deg, #0f2040, #0a1628)', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', minWidth: 0 }}>
          <span style={{ fontSize: '18px' }}>{String.fromCodePoint(0x1F4C5)}</span>
          <span style={{ color: '#c9a84c', fontWeight: '700', fontSize: '15px' }}>{month}</span>
          <span style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '2px 10px', borderRadius: '20px', fontSize: '11px' }}>{t('bookingsCount').replace('{count}', total)}</span>
          {pending > 0 && <span style={{ background: 'rgba(255,152,0,0.15)', border: '1px solid #FF9800', color: '#FF9800', padding: '2px 10px', borderRadius: '20px', fontSize: '11px' }}>{t('pendingCount').replace('{count}', pending)}</span>}
        </div>
        <span style={{ color: '#c9a84c', fontSize: '18px' }}>{open ? String.fromCodePoint(0x25B2) : String.fromCodePoint(0x25BC)}</span>
      </div>
      {open && (
        <div style={{ padding: '10px 14px 0', borderBottom: '1px solid rgba(201,168,76,0.1)' }}>
          <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 8px' }}>
            {t('monthMachineSummary')}: {monthMachines.join(' · ') || '—'} · {t('monthTotalLabel')}: ₹{monthTotal.toLocaleString('en-IN')}
          </p>
        </div>
      )}
      {open && (
        <div style={{ padding: '10px' }}>
          {dayGroups.map(({ day, bookings }) => {
            const dayMachines = [...new Set(bookings.map((b) => getBookingMachineCode(b, machineLookup)))];
            const dayTotal = bookings.reduce((sum, b) => sum + Number(b.total_amount || b.total || 0), 0);
            const dayPending = bookings.filter((b) => isBookingPendingDispatch(b)).length;
            return (
            <div key={day} style={{ marginBottom: '10px', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '10px', overflow: 'hidden', background: 'rgba(0,0,0,0.15)' }}>
              <div style={{ padding: '12px 14px', background: 'rgba(201,168,76,0.08)', borderBottom: '1px solid rgba(201,168,76,0.12)' }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ color: '#c9a84c', fontSize: '13px', fontWeight: 700 }}>{String.fromCodePoint(0x1F4C6)} {day}</span>
                  <span style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', color: '#4CAF50', padding: '1px 8px', borderRadius: '20px', fontSize: '10px' }}>{t('bookingsCount').replace('{count}', bookings.length)}</span>
                  {dayPending > 0 && <span style={{ background: 'rgba(255,152,0,0.15)', border: '1px solid #FF9800', color: '#FF9800', padding: '1px 8px', borderRadius: '20px', fontSize: '10px' }}>{t('pendingCount').replace('{count}', dayPending)}</span>}
                </div>
                <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>
                  {t('dayMachinesLabel')}: {dayMachines.join(' · ') || '—'}
                </p>
                <p style={{ color: '#e8e0d0', fontSize: '10px', margin: 0 }}>
                  {t('scheduleColTotal')}: ₹{dayTotal.toLocaleString('en-IN')}
                </p>
              </div>
              <div style={{ padding: '8px' }}>
                {DAY_SECTION_ORDER.map((sectionKey) => {
                  const sectionBookings = bookings.filter((b) => getDaySectionBucket(b) === sectionKey);
                  if (sectionBookings.length === 0) return null;
                  return (
                    <div key={sectionKey}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderLeft: '3px solid #C9A84C',
                        padding: '4px 12px',
                        marginTop: '8px',
                      }}
                      >
                        <span style={{ color: '#C9A84C', fontSize: '12px', fontWeight: 700 }}>{t(sectionKey)}</span>
                        <span style={{ color: '#8896a8', fontSize: '11px' }}>{sectionBookings.length}</span>
                      </div>
                {sectionBookings.map((b, i) => {
                  const bookingAt = parseBookingDate(b);
                  const dispatch = getBookingDispatchDisplay(b, t);
                  return (
                    <div key={b.id || i} style={{ background: 'linear-gradient(135deg, #0a1628, #060e1c)', border: isBookingCancelled(b) ? '1px solid rgba(233,69,96,0.35)' : (b.owner_approved ? '1px solid rgba(76,175,80,0.25)' : '1px solid rgba(255,152,0,0.25)'), borderRadius: '10px', padding: '14px', marginBottom: '8px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: bookingGridCols || (isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))'), gap: '8px', marginBottom: '12px', padding: '10px', background: 'rgba(201,168,76,0.05)', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.1)' }}>
                        {[
                          { label: t('machineLabel'), val: getBookingMachineCode(b, machineLookup) },
                          { label: t('scheduleColType'), val: bookingTypeDisplay(b) },
                          { label: b.booking_type === 'contract' ? t('cycles') : t('duration'), val: bookingQtyDisplay(b, t) },
                          {
                            label: b.booking_type === 'contract' ? 'Paid at booking' : t('advancePaidLabel'),
                            val: `₹${Number(b.advance_paid || b.advancePaid || 0).toLocaleString('en-IN')}`,
                          },
                          { label: t('scheduleColTotal'), val: `₹${Number(b.total_amount || b.total || 0).toLocaleString('en-IN')}` },
                        ].map((cell, j) => (
                          <div key={j} style={{ minWidth: 0 }}>
                            <p style={{ color: '#556070', fontSize: '9px', margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{cell.label}</p>
                            <p style={{ color: cell.color || '#e8e0d0', fontSize: '11px', fontWeight: 600, margin: 0, wordBreak: 'break-word', textTransform: j === 1 ? 'capitalize' : 'none' }}>{cell.val}</p>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>{b.booking_ref || b.id}</span>
                          <span style={{ color: '#8896a8', fontSize: '11px' }}>{bookingAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <span style={{ background: `${dispatch.color}22`, border: `1px solid ${dispatch.color}`, color: dispatch.color, padding: '3px 10px', borderRadius: '20px', fontSize: '11px' }}>
                            {isBookingCancelled(b)
                              ? t('bookingClosed')
                              : b.booking_type === 'contract'
                                ? 'Contract active'
                              : (b.owner_approved
                                ? `${String.fromCodePoint(0x2705)} ${t('dispatched')}`
                                : `${String.fromCodePoint(0x23F3)} ${t('awaitingDispatch')}`)}
                          </span>
                          {isBookingPendingDispatch(b) ? (
                            <button style={{ background: 'rgba(233,69,96,0.1)', border: '1px solid rgba(233,69,96,0.4)', color: '#e94560', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }} onClick={() => handleCancelRequest(b)}>{t('cancelText')}</button>
                          ) : null}
                        </div>
                      </div>
                        <div className="mb-2 grid w-full min-w-0 grid-cols-2 gap-2 md:grid-cols-4">
                          {[
                            { label: t('machine'), val: getBookingMachineCode(b, machineLookup), icon: String.fromCodePoint(0x1F69C) },
                            { label: t('type'), val: bookingTypeDisplay(b), icon: String.fromCodePoint(0x1F4CB) },
                            { label: t('location'), val: b.location || t('naLabel'), icon: String.fromCodePoint(0x1F4CD) },
                            {
                              label: b.booking_type === 'contract' ? 'Paid at booking' : t('advancePaid'),
                              val: '₹' + (b.advance_paid || b.advancePaid || 0).toLocaleString('en-IN'),
                              icon: String.fromCodePoint(0x1F4B0),
                            },
...(b.booking_type === 'contract'
                              ? (() => {
                                const money = deriveContractMoneyBreakdown(b);
                                return [
                                  {
                                    label: 'Total Hire Value (A)',
                                    val: `₹${formatContractInr(money.totalHireValueA)}`,
                                    icon: String.fromCodePoint(0x1F4C8),
                                  },
                                  {
                                    label: 'Total Mobilization (B)',
                                    val: money.mobilizationCollected
                                      ? `₹${formatContractInr(money.totalMobilizationB)}`
                                      : 'Not collected at booking',
                                    icon: String.fromCodePoint(0x1F69A),
                                  },
                                  {
                                    label: 'Contract total',
                                    val: `₹${formatContractInr(money.contractTotal)}`,
                                    icon: String.fromCodePoint(0x1F4B3),
                                  },
                                ];
                              })()
                              : [
                                { label: t('baseAmount'), val: '₹' + (b.base_amount || b.baseAmt || 0).toLocaleString('en-IN'), icon: String.fromCodePoint(0x1F4C8) },
                                { label: t('gst18'), val: '₹' + (b.gst_amount || b.gst || 0).toLocaleString('en-IN'), icon: String.fromCodePoint(0x1F4DC) },
                                { label: t('total'), val: '₹' + (b.total_amount || b.total || 0).toLocaleString('en-IN'), icon: String.fromCodePoint(0x1F4B3) },
                              ]),

                            ...(b.selected_attachment
                              ? [{
                                label: 'Attachment',
                                val: formatBookingAttachmentSummary(b),
                                icon: String.fromCodePoint(0x1F527),
                              }]
                              : []),
                          ].map((d, j) => (
                            <div key={j} className="box-border min-w-0" style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px' }}>
                              <p style={{ color: '#8896a8', fontSize: '9px', margin: '0 0 3px' }}>{d.icon} {d.label}</p>
                              <p className="m-0 break-words text-xs font-semibold text-slate-200">{d.val}</p>
                            </div>
                          ))}
                        </div>
                        {b.owner_approved && b.owner_approved_at && (
                          <div style={{ padding: '8px 10px', background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: '8px', textAlign: 'center' }}>
                            <span style={{ color: '#4CAF50', fontSize: '11px' }}>{String.fromCodePoint(0x2705)} {t('machineDispatchedAt').replace('{when}', new Date(b.owner_approved_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }))}</span>
                          </div>
                        )}
                        <ClientOperatorStatusBadge booking={b} />
                        {b.booking_type === 'contract' ? (
                          <>
                            {pendingTerminations[b.id]?.status === 'pending' && (
                              <p style={{
                                margin: '0 0 8px',
                                padding: '8px 10px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,152,0,0.4)',
                                background: 'rgba(255,152,0,0.1)',
                                color: '#FFB74D',
                                fontSize: '11px',
                                fontWeight: 700,
                              }}
                              >
                                ⏳ Termination pending admin approval
                              </p>
                            )}
                            <ContractBookingStatus booking={b} role="client" allowConfirm walletUser={walletUser} t={t} />
                            {b.mobilization_loading_approved_at && !b.mobilization_arrival_confirmed_at && (
                              <button
                                type="button"
                                disabled={arrivalMilestoneBusyId === b.id}
                                onClick={() => onOpenArrivalMilestone?.(b)}
                                style={{
                                  width: '100%',
                                  marginTop: '8px',
                                  padding: '11px',
                                  background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                                  color: '#030810',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontWeight: 800,
                                  cursor: arrivalMilestoneBusyId === b.id ? 'wait' : 'pointer',
                                  fontSize: '13px',
                                  opacity: arrivalMilestoneBusyId === b.id ? 0.7 : 1,
                                }}
                              >
                                {arrivalMilestoneBusyId === b.id ? t('loading') : 'Confirm Machine Arrival'}
                              </button>
                            )}
                            {b.mobilization_arrival_confirmed_at && (
                              <div style={{
                                marginTop: '8px',
                                padding: '10px 12px',
                                background: 'rgba(76,175,80,0.1)',
                                border: '1px solid rgba(76,175,80,0.35)',
                                borderRadius: '8px',
                                textAlign: 'center',
                              }}
                              >
                                <p style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 800, margin: 0 }}>
                                  Arrival confirmed — remaining transport amount released to owner
                                </p>
                              </div>
                            )}
                            {!pendingTerminations[b.id]
                              && !['Cancelled', 'Completed'].includes(String(b.status || '')) && (
                              <button
                                type="button"
                                onClick={() => onRequestTermination?.(b)}
                                style={{
                                  width: '100%',
                                  marginTop: '8px',
                                  padding: '10px',
                                  background: 'rgba(80,20,30,0.45)',
                                  border: '1px solid rgba(233,69,96,0.4)',
                                  color: '#ff9aa8',
                                  borderRadius: '8px',
                                  fontWeight: 700,
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Request Termination
                              </button>
                            )}
                            {!['Cancelled', 'Completed'].includes(String(b.status || '')) && (
                              <button
                                type="button"
                                onClick={() => onRequestOwnerDefault?.(b)}
                                style={{
                                  width: '100%',
                                  marginTop: '8px',
                                  padding: '10px',
                                  background: 'rgba(90,24,32,0.35)',
                                  border: '1px solid rgba(233,69,96,0.28)',
                                  color: '#c97884',
                                  borderRadius: '8px',
                                  fontWeight: 700,
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                }}
                              >
                                Report Owner Default
                              </button>
                            )}
                          </>
                        ) : b.site_work_type ? (
                          <ClientSiteWorkPanel booking={b} />
                        ) : b.booking_type === 'milestone' ? (
                          <ClientMilestoneUnitsPanel booking={b} />
                        ) : (
                          <BookingFlowV2Card
                            booking={b}
                            onRefresh={onBookingRefresh}
                            walletBalance={walletBalance}
                            onRecharge={onRecharge}
                            onDownloadInvoice={false}
                          />
                        )}
                        {b.selected_attachment && (
                          <ClientAttachmentUsagePanel booking={b} />
                        )}
                        {typeof onBookAgain === 'function' && isBookingCompletedStatus(b) && (
                          <button
                            type="button"
                            onClick={() => onBookAgain(b)}
                            style={{
                              width: '100%',
                              marginTop: '10px',
                              padding: '10px',
                              background: 'rgba(76,175,80,0.1)',
                              border: '1px solid rgba(76,175,80,0.4)',
                              color: '#4CAF50',
                              borderRadius: '8px',
                              fontWeight: 700,
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                          >
                            {String.fromCodePoint(0x1F504)} {t('bookAgain')}
                          </button>
                        )}
                        <BookingFeedbackPanel
                          booking={b}
                          t={t}
                          submitting={feedbackSubmittingId === b.id}
                          onSubmit={onSubmitFeedback}
                        />
                      </div>
                    );
                  })}
                    </div>
                  );
                })}
                </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage(); // eslint-disable-line
  const { isMobile, isTablet, width, gridCols } = useWindowSize();
  const layoutMode = useDeviceLayout();
  const useMobileNav = layoutMode === 'phone';
  const sidebarOverlay = layoutMode === 'tablet';
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);
  const walletCompact = isMobile || isTablet;

  useEffect(() => {
    setSidebarOpen(!sidebarOverlay);
  }, [sidebarOverlay]);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [myBookingsTab, setMyBookingsTab] = useState('schedule');
  const [pushBannerDismissed, setPushBannerDismissed] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [hireMode, setHireMode] = useState('hourly');
  const [hireWizardStep, setHireWizardStep] = useState(1);
  const [hireModePicked, setHireModePicked] = useState(false);
  const [siteWorkType, setSiteWorkType] = useState('');
  const [siteBillingBasis, setSiteBillingBasis] = useState('per_foot');
  const [strataRateSoft, setStrataRateSoft] = useState('');
  const [strataRateMurram, setStrataRateMurram] = useState('');
  const [strataRateRock, setStrataRateRock] = useState('');
  const [estimatedDepth, setEstimatedDepth] = useState('');
  const [siteLumpsumAmount, setSiteLumpsumAmount] = useState('');
  const [noWaterClauseAccepted, setNoWaterClauseAccepted] = useState(false);
  const [siteWorkSubmitting, setSiteWorkSubmitting] = useState(false);
  const [siteWorkSuccess, setSiteWorkSuccess] = useState('');
  const [bookingType, setBookingType] = useState('hourly');
  const [quantity, setQuantity] = useState(3);
  const [bucketOnlyHours, setBucketOnlyHours] = useState(0);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [billingBasis, setBillingBasis] = useState('calendar_month');
  const [fixedRatePerCycle, setFixedRatePerCycle] = useState('');
  const [guaranteedHoursPerCycle, setGuaranteedHoursPerCycle] = useState('');
  const [standbyPercent, setStandbyPercent] = useState('50');
  const [monsoonStartMonth, setMonsoonStartMonth] = useState('6');
  const [monsoonEndMonth, setMonsoonEndMonth] = useState('9');
  const [terminationNoticeDays, setTerminationNoticeDays] = useState('15');
  const [terminationFeeFormula, setTerminationFeeFormula] = useState('25_percent_remaining');
  const [terminationFeeFlat, setTerminationFeeFlat] = useState('');
  const [showStandbyTerms, setShowStandbyTerms] = useState(false);
  const [showPenaltyTerms, setShowPenaltyTerms] = useState(false);
  const [ownerNoshowPenaltyPct, setOwnerNoshowPenaltyPct] = useState('25');
  const [ownerAbandonmentPenaltyPct, setOwnerAbandonmentPenaltyPct] = useState('20');
  const [ownerBreakdownCreditPct, setOwnerBreakdownCreditPct] = useState('5');
  const [clientTerminationPenaltyPct, setClientTerminationPenaltyPct] = useState('20');
  const [ownerPaymentDueDay, setOwnerPaymentDueDay] = useState('');
  const [mobilizationTransportBy, setMobilizationTransportBy] = useState('');
  const [mobilizationTransportAmount, setMobilizationTransportAmount] = useState('');
  const [demobilizationTransportBy, setDemobilizationTransportBy] = useState('');
  const [demobilizationTransportAmount, setDemobilizationTransportAmount] = useState('');
  const [attachmentRatePerCycle, setAttachmentRatePerCycle] = useState('');
  const [attachmentRateAutoSuggestion, setAttachmentRateAutoSuggestion] = useState(null);
  const [contractQuote, setContractQuote] = useState(null);
  const [contractQuoteLoading, setContractQuoteLoading] = useState(false);
  const [contractQuoteError, setContractQuoteError] = useState('');
  const [activeQuotation, setActiveQuotation] = useState(null);
  const [terminationModal, setTerminationModal] = useState(null);
  const [terminationType, setTerminationType] = useState('client');
  const [terminationSubmitting, setTerminationSubmitting] = useState(false);
  const [terminationError, setTerminationError] = useState('');
  const [pendingTerminations, setPendingTerminations] = useState({});
  const [ownerDefaultModal, setOwnerDefaultModal] = useState(null);
  const [ownerDefaultType, setOwnerDefaultType] = useState('no_show');
  const [ownerDefaultBreakdownDays, setOwnerDefaultBreakdownDays] = useState('');
  const [ownerDefaultSubmitting, setOwnerDefaultSubmitting] = useState(false);
  const [ownerDefaultError, setOwnerDefaultError] = useState('');
  const [ownerDefaultSuccess, setOwnerDefaultSuccess] = useState('');
  const [arrivalMilestoneModal, setArrivalMilestoneModal] = useState(null);
  const [arrivalPhotoFile, setArrivalPhotoFile] = useState(null);
  const [arrivalMilestoneBusyId, setArrivalMilestoneBusyId] = useState(null);
  const [arrivalMilestoneError, setArrivalMilestoneError] = useState('');
  const [quotationTermsAccepted, setQuotationTermsAccepted] = useState(false);
  const [paymentDueDateAccepted, setPaymentDueDateAccepted] = useState(false);
  const [clientPaymentDueDayProposal, setClientPaymentDueDayProposal] = useState('');
  const [esignTermsAccepted, setEsignTermsAccepted] = useState(false);
  const [esignPaySuccess, setEsignPaySuccess] = useState(null);
  const [quotationArrivalPhoto, setQuotationArrivalPhoto] = useState(null);
  const [quotationArrivalLoading, setQuotationArrivalLoading] = useState(false);
  const [quotationArrivalSuccess, setQuotationArrivalSuccess] = useState(false);
  const [mandateInfo, setMandateInfo] = useState(null);
  const [mandateLoading, setMandateLoading] = useState(false);
  const [mandateError, setMandateError] = useState('');
  const [quotationActionLoading, setQuotationActionLoading] = useState(false);
  const [quotationRejectedMessage, setQuotationRejectedMessage] = useState('');
  const [showQuotationTermsSnapshot, setShowQuotationTermsSnapshot] = useState(false);
  const [showQuotationResult, setShowQuotationResult] = useState(false);
  const [termsPrompt, setTermsPrompt] = useState({
    visible: false,
    currentVersion: '',
    userVersion: '',
    acknowledging: false,
    error: '',
  });

  const [walletBalance, setWalletBalance] = useState(null);
  const [walletVisible, setWalletVisible] = useState(false);
  const [rechargeAmt, setRechargeAmt] = useState('');
  const [showRecharge, setShowRecharge] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [pendingIdempotencyKey, setPendingIdempotencyKey] = useState(null);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  useSessionTimeout({
    pause: showRecharge || showPayment || showQuotationResult || bookingSubmitting
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showBlacklistWarning, setShowBlacklistWarning] = useState(false);
  const [showPhoneChangeModal, setShowPhoneChangeModal] = useState(false);
  const [clientUser, setClientUser] = useState(() => JSON.parse(localStorage.getItem('developmentexpress_user') || '{}'));
  const [clientPhoto, setClientPhoto] = useState('');
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelledCredit, setCancelledCredit] = useState(null);
  const [cancelledSectionOpen, setCancelledSectionOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [machineList, setMachineList] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [machineSearch, setMachineSearch] = useState('');
  const [machineAvailFilter, setMachineAvailFilter] = useState('all'); // all | available | on_job
  const [jobType, setJobType] = useState('');
  const [jobHoursHH, setJobHoursHH] = useState('');
  const [jobMM, setJobMM] = useState('00');
  // Search API + validation still speak decimal hours.
  const jobHours = jobHoursHH === ''
    ? ''
    : Number(jobHoursHH) + Number(jobMM) / 60;
  const [jobDate, setJobDate] = useState('');
  const [jobPin, setJobPin] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [jobFindLoading, setJobFindLoading] = useState(false);
  const [jobFindError, setJobFindError] = useState('');
  const [activeBookings, setActiveBookings] = useState([]);
  const [bookingsOffset, setBookingsOffset] = useState(0);
  const [bookingsHasMore, setBookingsHasMore] = useState(false);
  const [bookingsLoadingMore, setBookingsLoadingMore] = useState(false);
  const [transactionRows, setTransactionRows] = useState(TRANSACTIONS);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [txOffset, setTxOffset] = useState(0);
  const [txHasMore, setTxHasMore] = useState(false);
  const [txLoadingMore, setTxLoadingMore] = useState(false);
  const [reportStatusFilter, setReportStatusFilter] = useState('all');
  const [closedReportMonths, setClosedReportMonths] = useState(new Set());
  const [clientAnalytics, setClientAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [analyticsMonths, setAnalyticsMonths] = useState(6);
  const [feedbackSubmittingId, setFeedbackSubmittingId] = useState(null);
  const [siteAddress, setSiteAddress] = useState('');
  const [sitePincode, setSitePincode] = useState('');
  const [siteLat, setSiteLat] = useState(null);
  const [siteLng, setSiteLng] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState('');
  const [locationSource, setLocationSource] = useState(null);
  const [siteGpsLoading, setSiteGpsLoading] = useState(false);
  const [siteGpsError, setSiteGpsError] = useState('');
  const [siteFieldTouched, setSiteFieldTouched] = useState({
    work: false,
    address: false,
    pincode: false,
    gps: false,
  });
  const [siteBookAttempted, setSiteBookAttempted] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentUsageHours, setAttachmentUsageHours] = useState('');
  const [workDescription, setWorkDescription] = useState('');
  const [machineBookedDates, setMachineBookedDates] = useState([]);
  const [clientKycStatus, setClientKycStatus] = useState(null);
  const walletRechargeRef = useRef(null);
  const gpsFillingRef = useRef(false);
  const clientHardwareBackFromPopRef = useRef(false);
  const clientActiveTabRef = useRef(activeTab);
  const clientHardwareBackModalsRef = useRef({
    showPayment: false,
    showRecharge: false,
    showCancelModal: false,
    showBlacklistWarning: false,
    showQuotationResult: false,
  });

  clientActiveTabRef.current = activeTab;
  clientHardwareBackModalsRef.current = {
    showPayment,
    showRecharge,
    showCancelModal,
    showBlacklistWarning,
    showQuotationResult,
    sidebarOpen,
    sidebarOverlay,
    useMobileNav,
    mobileNavMenuOpen,
  };

  const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
  const userClientTier = getClientTier(clientUser?.client_type || user?.client_type);
  const pushEnabled = user?.pushNotificationsEnabled;

  useEffect(() => {
    if (activeQuotation?.billingBasis) {
      setBillingBasis(
        activeQuotation.billingBasis === 'working_days_26' ? 'working_days_26' : 'calendar_month',
      );
      return;
    }
    const clientType = clientUser?.client_type || user?.client_type;
    setBillingBasis(defaultBillingBasisFromClientType(clientType));
  }, [selectedMachine?.id, activeQuotation?.id, activeQuotation?.billingBasis, clientUser?.client_type, user?.client_type]);
  const handleLogout = () => {
    logoutAndRedirect(navigate);
  };

  useEffect(() => {
    if (activeTab === 'alerts') setPushBannerDismissed(true);
  }, [activeTab]);

  useEffect(() => {
    setClientPhoto(loadProfilePhoto(clientUser?.id, 'client') || '');
  }, [clientUser?.id]);

  useEffect(() => {
    let cancelled = false;
    const loadTermsPrompt = async () => {
      try {
        const token = localStorage.getItem('developmentexpress_token');
        if (!token) return;
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
        const response = await fetch(`${apiBase}/api/auth/terms/status`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.status === 401 || !response.ok) return;
        const payload = await response.json().catch(() => null);
        if (cancelled || !payload?.success) return;
        const currentVersion = payload.currentVersion || '';
        const userVersion = payload?.user?.terms_version || '';
        const needs = Boolean(payload.needsAcknowledgement);
        if (!needs || !currentVersion) return;
        const seenKey = `client_terms_prompt_seen_${clientUser?.id || 'anon'}_${currentVersion}`;
        const alreadySeen = localStorage.getItem(seenKey) === '1';
        if (alreadySeen) return;
        localStorage.setItem(seenKey, '1');
        setTermsPrompt({
          visible: true,
          currentVersion,
          userVersion,
          acknowledging: false,
          error: '',
        });
      } catch (_err) {
        // non-blocking
      }
    };
    loadTermsPrompt();
    return () => { cancelled = true; };
  }, [clientUser?.id]);

  useEffect(() => {
    const savedTab = sessionStorage.getItem('returnTab');
    if (savedTab) {
      sessionStorage.removeItem('returnTab');
      setActiveTab(savedTab);
    }

    const loadData = async () => {
      const machines = await getMachines();
      if (machines.length > 0) {
        const formatted = machines.map(m => ({
          id: m.id,
          code: m.machine_id,
          name: m.name, type: m.type, regNo: m.reg_no,
          year: m.year, location: m.location, fuel: m.fuel_level,
          condition: m.condition_pct, available: m.status !== 'Deployed',
          owner_id: m.owner_id || null,
          operator_id: m.operator_id || null,
          operator: m.operator?.name || null,
          operatorExp: m.operator?.experience_years || 0,
          operatorMachines: m.operator?.experience_machine_types || [],
          minimumHours: m.minimum_hours || 3,
          rank: m.rank || null,
          isBestMatch: m.is_best_match || false,
          settledJobsCount: m.settledJobsCount ?? m.settled_jobs_count ?? 0,
          score: m.score ?? null,
          distanceKm: m.distance_km ?? null,
          fuelCapacity: Number(m.fuel_capacity_litres || 0),
          rates: { perHour: m.rate_per_hour, perDay: m.rate_per_day, perWeek: m.rate_per_week, perMonth: m.rate_per_month },
          fuelBasis: m.fuel_basis || 'negotiable',
          ratePerHourFuelIncluded: m.rate_per_hour_fuel_included || m.rate_per_hour || null,
          ratePerHourFuelExcluded: m.rate_per_hour_fuel_excluded || null,
          operatorIncluded: m.operator_included !== false,
          operatorDailyRate: m.operator_daily_rate || null,
          advance: { hourly: 3, weekly: 3, monthly: 15 },
          specs: 'Engine: 92 HP | Bucket: 0.21 m3',
          attachments: m.attachments || [],
          attachmentRates: m.attachment_rates && typeof m.attachment_rates === 'object'
            ? m.attachment_rates
            : {},
          attachmentMinHours: m.attachment_min_hours && typeof m.attachment_min_hours === 'object'
            ? m.attachment_min_hours
            : {},
          working_hours_start: m.working_hours_start || null,
          working_hours_end: m.working_hours_end || null,
          working_days: m.working_days || null,
        }));
        setMachineList(formatted);
      }
      if (user?.id) {
        const bal = await getWalletBalance(user.id);
        setWalletBalance(bal);
        const txResult = await getMyTransactionsPage({ limit: 50, offset: 0 });
        if ((txResult.items || []).length > 0) {
          setTransactionRows(txResult.items.map((tx) => formatTransactionRow(tx, t)));
          setTxOffset(txResult.nextOffset || 0);
          setTxHasMore(Boolean(txResult.hasMore));
        } else {
          setTxHasMore(false);
        }
      }
    };
    loadData();
    const savedUser = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
    if (savedUser.id) {
      getMyBookingsPage({ limit: 100, offset: 0 }).then((result) => {
        if (result.items && result.items.length > 0) {
          setActiveBookings(result.items.map(mergeBookingFeedbackFields));
        }
        setBookingsHasMore(Boolean(result.hasMore));
        setBookingsOffset(result.nextOffset || 0);
      });
    }

    const walletInterval = setInterval(async () => {
      const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
      if (user?.id) {
        const bal = await getWalletBalance(user.id);
        if (bal !== null) setWalletBalance(bal);
      }
    }, 30000);
    return () => clearInterval(walletInterval);
  }, []); // eslint-disable-line

  useEffect(() => {
    const loadClientKycStatus = async () => {
      try {
        const data = await secureFetch('/api/client/kyc');
        setClientKycStatus(data.kyc?.status || 'not_submitted');
      } catch (err) {
        if ((err?.message || '') === 'auth_required') return;
        // soft gate — ignore load errors
      }
    };
    loadClientKycStatus();
  }, []);

  useEffect(() => {
    const poll = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      (async () => {
        try {
          const data = await secureFetch('/api/client/kyc');
          setClientKycStatus(data.kyc?.status || 'not_submitted');
        } catch (err) {
          if ((err?.message || '') === 'auth_required') return;
        }
      })();
    }, 30000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (clientHardwareBackFromPopRef.current) {
      clientHardwareBackFromPopRef.current = false;
      return;
    }
    window.history.pushState(
      { tab: activeTab },
      '',
      `?tab=${activeTab}`,
    );
  }, [activeTab]);

  useEffect(() => {
    if (!window.history.state?.tab) {
      window.history.replaceState({ tab: 'dashboard' }, '', '/client');
    }

    const restoreCurrentTabInHistory = () => {
      const tab = clientActiveTabRef.current;
      window.history.pushState({ tab }, '', `?tab=${tab}`);
    };

    const onPopState = (event) => {
      const modals = clientHardwareBackModalsRef.current;
      if (modals.sidebarOpen && modals.sidebarOverlay) {
        setSidebarOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.useMobileNav && modals.mobileNavMenuOpen) {
        setMobileNavMenuOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.showQuotationResult) {
        setShowQuotationResult(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.showPayment) {
        setShowPayment(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.showRecharge) {
        setShowRecharge(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.showCancelModal) {
        setShowCancelModal(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.showBlacklistWarning) {
        setShowBlacklistWarning(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (event.state && event.state.tab) {
        clientHardwareBackFromPopRef.current = true;
        setActiveTab(event.state.tab === 'reports' ? 'mybookings' : event.state.tab);
      } else {
        // Stack exhausted — absorb back press; stay on /client current tab
        const tab = clientActiveTabRef.current || 'dashboard';
        window.history.pushState({ tab }, '', '/client');
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const displayMachines = machineList.length > 0 ? machineList : MACHINES;
  const listingMachines = searchResults !== null ? searchResults : displayMachines;

  const filteredMachines = useMemo(() => {
    const q = machineSearch.trim().toLowerCase();
    return listingMachines.filter((m) => {
      if (machineAvailFilter === 'available' && !m.available) return false;
      if (machineAvailFilter === 'on_job' && m.available) return false;
      if (!q) return true;
      const name = String(m.name || '').toLowerCase();
      const code = String(m.code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [listingMachines, machineSearch, machineAvailFilter]);

  const machineTypeGroups = useMemo(() => {
    const byType = new Map();
    filteredMachines.forEach((m) => {
      const typeKey = String(m.type || '').trim() || '—';
      if (!byType.has(typeKey)) byType.set(typeKey, []);
      byType.get(typeKey).push(m);
    });
    return [...byType.entries()].map(([type, machines]) => {
      const sorted = [...machines].sort((a, b) => Number(Boolean(b.available)) - Number(Boolean(a.available)));
      return {
        type,
        machines: sorted,
        availableCount: sorted.filter((row) => row.available).length,
        busyCount: sorted.filter((row) => !row.available).length,
      };
    });
  }, [filteredMachines]);
  const availableMachineTypes = useMemo(() => {
    const types = new Set();
    machineList.forEach((m) => {
      const typeName = String(m.type || '').trim();
      if (typeName && typeName !== '—') types.add(typeName);
    });
    return Array.from(types).sort();
  }, [machineList]);
  const showMachineTypeHeaders = machineTypeGroups.length > 1;

  useEffect(() => {
    setSearchResults(null);
  }, [jobType, jobHours, jobDate, jobPin]);

  const clientKycBookingBlocked = clientKycStatus !== 'approved';
  const clientKycBookingBlockMessage = useMemo(() => {
    if (!clientKycBookingBlocked) return '';
    if (clientKycStatus === 'rejected') {
      return 'Your KYC was rejected. Re-submit your documents to book machines.';
    }
    if (clientKycStatus === 'pending') {
      return 'KYC verification is in progress. Complete verification to book machines.';
    }
    return 'Complete KYC verification to book machines.';
  }, [clientKycBookingBlocked, clientKycStatus]);

  const goToKycTab = () => {
    setActiveTab('kyc');
    setShowPayment(false);
    setBookingStep(1);
  };

  const onboardingChecklist = useMemo(() => {
    const kycDone = clientKycStatus === 'approved';
    const kycRejected = clientKycStatus === 'rejected';
    const walletDone = walletBalance !== null && walletBalance > 0;
    const bookingDone = Array.isArray(activeBookings)
      && activeBookings.filter((b) => !isBookingCancelled(b)).length > 0;

    const items = [
      {
        id: 'account',
        done: true,
        tone: 'green',
        labelKey: 'onboardingAccountApproved',
        tab: null,
      },
      {
        id: 'kyc',
        done: kycDone,
        tone: kycDone ? 'green' : (kycRejected ? 'red' : 'amber'),
        labelKey: kycDone
          ? 'onboardingKycComplete'
          : (kycRejected ? 'onboardingKycRejected' : 'onboardingKycPending'),
        tab: 'kyc',
      },
      {
        id: 'wallet',
        done: walletDone,
        tone: walletDone ? 'green' : 'amber',
        labelKey: walletDone ? 'onboardingWalletFunded' : 'onboardingWalletEmpty',
        tab: 'wallet',
      },
      {
        id: 'booking',
        done: bookingDone,
        tone: bookingDone ? 'green' : 'amber',
        labelKey: 'onboardingFirstBooking',
        tab: 'newbooking',
      },
    ];

    return { items, complete: items.every((item) => item.done) };
  }, [clientKycStatus, walletBalance, activeBookings]);

  const todayBookingsCount = useMemo(() => {
    if (!Array.isArray(activeBookings)) return 0;
    const todayKey = toDateInputValue(new Date());
    return activeBookings.filter((b) => {
      if (isBookingCancelled(b)) return false;
      if (!b.start_date && !b.created_at) return false;
      const d = new Date(b.start_date || b.created_at);
      if (Number.isNaN(d.getTime())) return false;
      return toDateInputValue(d) === todayKey;
    }).length;
  }, [activeBookings]);

  const renderClientOnboardingChecklist = () => {
    if (onboardingChecklist.complete) return null;
    const toneStyles = {
      green: { icon: String.fromCodePoint(0x2705), color: '#4CAF50', border: 'rgba(76,175,80,0.35)', bg: 'rgba(76,175,80,0.08)' },
      amber: { icon: String.fromCodePoint(0x2B1C), color: '#FF9800', border: 'rgba(255,152,0,0.35)', bg: 'rgba(255,152,0,0.08)' },
      red: { icon: String.fromCodePoint(0x274C), color: '#e94560', border: 'rgba(233,69,96,0.35)', bg: 'rgba(233,69,96,0.08)' },
    };
    return (
      <div
        style={{
          borderRadius: '12px',
          border: '1px solid rgba(201,168,76,0.3)',
          background: 'linear-gradient(135deg, rgba(18,42,74,0.9), rgba(6,14,28,0.95))',
          padding: isMobile ? '12px' : '14px 16px',
          marginBottom: '18px',
        }}
      >
        <p style={{ margin: '0 0 10px', color: '#c9a84c', fontSize: isMobile ? '13px' : '14px', fontWeight: 700 }}>
          {t('onboardingTitle')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {onboardingChecklist.items.filter((item) => !item.done).map((item) => {
            const tone = toneStyles[item.tone] || toneStyles.amber;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (item.tab === 'newbooking') {
                    setActiveTab('newbooking');
                    setBookingStep(1);
                    setShowPayment(false);
                    return;
                  }
                  if (item.tab) setActiveTab(item.tab);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  textAlign: 'left',
                  cursor: item.tab ? 'pointer' : 'default',
                  borderRadius: '8px',
                  border: `1px solid ${tone.border}`,
                  background: tone.bg,
                  color: tone.color,
                  padding: '8px 10px',
                  fontSize: isMobile ? '11px' : '12px',
                  fontWeight: 600,
                }}
              >
                <span aria-hidden="true">{tone.icon}</span>
                <span style={{ flex: 1 }}>{t(item.labelKey)}</span>
                {item.tab && !item.done ? <span style={{ opacity: 0.8 }}>→</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderClientKycBanner = () => {
    // Checklist already covers KYC — avoid duplicate prompts on New Booking.
    if (!onboardingChecklist.complete) return null;
    if (!clientKycStatus || clientKycStatus === 'approved') return null;
    let bannerStyle;
    let message;
    let showKycLink = false;
    if (clientKycStatus === 'rejected') {
      bannerStyle = {
        background: 'rgba(233,69,96,0.12)',
        border: '1px solid rgba(233,69,96,0.35)',
        color: '#e94560',
      };
      message = t('kycRejectedWarning');
      showKycLink = true;
    } else if (clientKycStatus === 'not_submitted') {
      bannerStyle = {
        background: 'rgba(255,152,0,0.12)',
        border: '1px solid rgba(255,152,0,0.35)',
        color: '#FF9800',
      };
      message = t('kycNotSubmittedHint');
      showKycLink = true;
    } else if (clientKycStatus === 'pending') {
      bannerStyle = {
        background: 'rgba(33,150,243,0.12)',
        border: '1px solid rgba(33,150,243,0.35)',
        color: '#2196F3',
      };
      message = t('kycPendingHint');
    } else {
      return null;
    }
    return (
      <div
        style={{
          ...bannerStyle,
          borderRadius: '10px',
          padding: '10px 12px',
          marginBottom: '18px',
          fontSize: isMobile ? '11px' : '12px',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
        }}
      >
        <p style={{ margin: 0, flex: '1 1 200px' }}>{message}</p>
        {showKycLink ? (
          <button
            type="button"
            onClick={() => setActiveTab('kyc')}
            style={{
              background: 'transparent',
              border: `1px solid ${bannerStyle.color}`,
              color: bannerStyle.color,
              borderRadius: '6px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            {t('kycGoToTab')} →
          </button>
        ) : null}
      </div>
    );
  };

  function fuelDisplay(fuelPct, capacityLitres) {
    if (capacityLitres > 0) {
      const litres = Math.round(
        (fuelPct / 100) * capacityLitres,
      );
      return `${litres}L / ${capacityLitres}L`;
    }
    return `${fuelPct}%`;
  }

  const machineLookup = useMemo(() => buildMachineLookup(displayMachines), [displayMachines]);
  const filteredTransactions = transactionRows.filter((row) => {
    if (transactionFilter === 'credit') return row.type === 'credit';
    if (transactionFilter === 'debit') return row.type === 'debit';
    return true;
  });

  const reportRows = useMemo(
    () => activeBookings.map((b) => normalizeReportBooking(b, machineLookup, t)),
    [activeBookings, machineLookup, t],
  );
  const filteredReportRows = useMemo(
    () => reportRows.filter((row) => matchesReportStatusFilter(row, reportStatusFilter)),
    [reportRows, reportStatusFilter],
  );
  const groupedReportMonths = useMemo(
    () => groupReportRowsByMonth(filteredReportRows),
    [filteredReportRows],
  );

  const clientAlerts = useMemo(() => {
    const alerts = [];
    const now = Date.now();
    const ms48h = 48 * 60 * 60 * 1000;
    const ms24h = 24 * 60 * 60 * 1000;

    const alertSortTime = (raw) => {
      if (!raw) return 0;
      const ms = new Date(raw).getTime();
      return Number.isFinite(ms) ? ms : 0;
    };

    const formatAlertTime = (raw) => {
      const ms = alertSortTime(raw);
      if (!ms) return t('recentLabel');
      return new Date(ms).toLocaleDateString(lang, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    activeBookings.filter((b) => isBookingPendingDispatch(b)).forEach((b) => {
      alerts.push({
        icon: String.fromCodePoint(0x23F3),
        msg: `${getBookingMachineCode(b, machineLookup)} — ${t('awaitingDispatch')}`,
        time: parseBookingDate(b).toLocaleDateString(lang, { day: '2-digit', month: 'short' }),
        color: '#FF9800',
        sortTime: parseBookingDate(b).getTime(),
      });
    });
    activeBookings.filter((b) => b.owner_approved).slice(0, 5).forEach((b) => {
      alerts.push({
        icon: String.fromCodePoint(0x2705),
        msg: `${getBookingMachineCode(b, machineLookup)} — ${t('dispatched')}`,
        time: b.owner_approved_at
          ? new Date(b.owner_approved_at).toLocaleDateString(lang, { day: '2-digit', month: 'short' })
          : t('recentLabel'),
        color: '#4CAF50',
        sortTime: alertSortTime(b.owner_approved_at) || parseBookingDate(b).getTime(),
      });
    });
    if (walletBalance !== null && walletBalance < 50000) {
      alerts.push({
        icon: String.fromCodePoint(0x1F4B3),
        msg: t('walletLowRecharge'),
        time: t('live'),
        color: '#e94560',
        sortTime: now,
      });
    }
    activeBookings.filter((b) => b.status === 'Extension_Pending').forEach((b) => {
      const timeRaw = b.updated_at || b.created_at;
      alerts.push({
        icon: '⏳',
        msg: `${t('alertExtensionPending')} — ${b.machines?.machine_id || b.booking_ref}`,
        time: formatAlertTime(timeRaw),
        color: '#f59e0b',
        sortTime: alertSortTime(timeRaw),
      });
    });
    activeBookings.filter((b) => b.status === 'Completing' || b.status === 'Completing_PendingPayment').forEach((b) => {
      const timeRaw = b.operator_completed_at || b.updated_at;
      alerts.push({
        icon: '✅',
        msg: `${t('alertConfirmCompletion')} ${b.machines?.machine_id || b.booking_ref}`,
        time: formatAlertTime(timeRaw),
        color: '#10b981',
        sortTime: alertSortTime(timeRaw),
      });
    });
    transactionRows
      .filter((tx) => {
        const type = String(tx.type || '').toLowerCase();
        const text = String(tx.desc || '').toLowerCase();
        const isRecharge = type === 'credit' || text.includes('recharge') || text.includes('topup');
        if (!isRecharge) return false;
        const sortTime = tx.sortTime || alertSortTime(tx.createdAt);
        return sortTime > 0 && now - sortTime <= ms24h;
      })
      .slice(0, 2)
      .forEach((tx) => {
        const timeRaw = tx.createdAt || tx.date;
        const amount = Number(tx.amount || 0);
        alerts.push({
          icon: '💰',
          msg: `₹${amount.toLocaleString('en-IN')} ${t('alertWalletCredited')}`,
          time: formatAlertTime(timeRaw),
          color: '#6366f1',
          sortTime: tx.sortTime || alertSortTime(timeRaw),
        });
      });
    activeBookings
      .filter((b) => {
        if (b.status !== 'Completed' || b.client_feedback_at) return false;
        const timeRaw = b.operator_completed_at || b.updated_at;
        const sortTime = alertSortTime(timeRaw);
        return sortTime > 0 && now - sortTime <= ms48h;
      })
      .slice(0, 2)
      .forEach((b) => {
        const timeRaw = b.operator_completed_at || b.updated_at;
        alerts.push({
          icon: '⭐',
          msg: `${t('alertFeedbackRequest')} ${b.machines?.machine_id || b.booking_ref}`,
          time: formatAlertTime(timeRaw),
          color: '#8b5cf6',
          sortTime: alertSortTime(timeRaw),
        });
      });

    if (clientKycStatus !== 'approved' && clientKycStatus !== 'rejected') {
      alerts.push({
        icon: '🪪',
        msg: clientKycStatus === 'pending' ? t('kycPendingHint') : t('kycNotSubmittedHint'),
        time: t('live'),
        color: '#FF9800',
        sortTime: now,
        tab: 'kyc',
      });
    }

    const walletLowShown = walletBalance !== null && walletBalance < 50000;
    if ((walletBalance === 0 || walletBalance === null) && !walletLowShown) {
      alerts.push({
        icon: '💳',
        msg: t('onboardingWalletEmpty'),
        time: t('live'),
        color: '#FF9800',
        sortTime: now,
        tab: 'wallet',
      });
    }

    if (!pushEnabled) {
      alerts.push({
        icon: '🔔',
        msg: t('pushEnablePrompt'),
        time: t('live'),
        color: '#FF9800',
        sortTime: now,
        tab: 'alerts',
      });
    }

    return alerts
      .sort((a, b) => (b.sortTime || 0) - (a.sortTime || 0))
      .map(({ sortTime, ...rest }) => rest);
  }, [activeBookings, machineLookup, walletBalance, transactionRows, t, lang, clientKycStatus, pushEnabled]);

  const clientDisplayName = user.company || user.name || t('clientPortal');
  const invoiceBookings = useMemo(
    () => activeBookings.filter(isGstInvoiceEligible),
    [activeBookings],
  );

  const { ref: clientChartWrapRef, width: clientChartWidth } = useClientChartWidth();

  const analyticsTrendSeries = useMemo(
    () => (Array.isArray(clientAnalytics?.monthly_trend) ? clientAnalytics.monthly_trend : []),
    [clientAnalytics],
  );

  const analyticsMaxSpend = useMemo(
    () => Math.max(...analyticsTrendSeries.map((s) => Number(s.spend || 0)), 1),
    [analyticsTrendSeries],
  );

  const topAnalyticsMachines = useMemo(
    () => (Array.isArray(clientAnalytics?.top_machines) ? clientAnalytics.top_machines : []),
    [clientAnalytics],
  );

  const analyticsInvoiceCount = useMemo(() => {
    const start = new Date();
    start.setUTCMonth(start.getUTCMonth() - analyticsMonths);
    start.setUTCHours(0, 0, 0, 0);
    const startMs = start.getTime();
    return activeBookings.filter((b) => {
      if (!isGstInvoiceEligible(b)) return false;
      const created = b.created_at ? new Date(b.created_at).getTime() : NaN;
      return Number.isFinite(created) && created >= startMs;
    }).length;
  }, [activeBookings, analyticsMonths]);

  async function loadClientAnalytics() {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const data = await secureFetch(
        `/api/bookings/me/analytics?months=${analyticsMonths}`,
      );
      setClientAnalytics(data);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setClientAnalytics(null);
      setAnalyticsError(t('failedToLoad'));
    } finally {
      setAnalyticsLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab !== 'mybookings') return;
    loadClientAnalytics();
  }, [activeTab, analyticsMonths]); // eslint-disable-line react-hooks/exhaustive-deps

  const groupedBookingsSchedule = useMemo(
    () => groupBookingsByMonth(activeBookings.filter((b) => !isBookingCancelled(b))),
    [activeBookings],
  );

  const cancelledBookingsList = useMemo(
    () => activeBookings.filter((b) => isBookingCancelled(b)),
    [activeBookings],
  );

  const cancelledBookingsSchedule = useMemo(
    () => groupBookingsByMonth(cancelledBookingsList),
    [cancelledBookingsList],
  );

  const handleSubmitBookingFeedback = async (bookingId, payload) => {
    setFeedbackSubmittingId(bookingId);
    const saved = await submitBookingFeedback(bookingId, payload);
    const patch = {
      client_operator_rating: payload.operatorRating,
      client_satisfied: payload.satisfied,
      client_suggestion: payload.suggestion || '',
      client_feedback_at: new Date().toISOString(),
    };
    if (!saved) {
      saveLocalBookingFeedback(bookingId, {
        operatorRating: payload.operatorRating,
        satisfied: payload.satisfied,
        suggestion: payload.suggestion || '',
      });
    }
    setActiveBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, ...patch } : b)));
    setFeedbackSubmittingId(null);
    if (saved) {
      alert(t('operatorFeedbackSaved'));
    }
  };

  const readArrivalFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

  const handleConfirmArrivalMilestone = async () => {
    const booking = arrivalMilestoneModal;
    if (!booking?.id || arrivalMilestoneBusyId) return;
    if (!arrivalPhotoFile) {
      setArrivalMilestoneError('Arrival photo is required.');
      return;
    }
    setArrivalMilestoneBusyId(booking.id);
    setArrivalMilestoneError('');
    try {
      const photoUrl = await readArrivalFileAsDataUrl(arrivalPhotoFile);
      const result = await secureFetch(
        `/api/client/bookings/${encodeURIComponent(booking.id)}/mobilization/confirm-arrival`,
        {
          method: 'POST',
          body: JSON.stringify({ photoUrl }),
        },
      );
      if (!result?.success) {
        setArrivalMilestoneError(result?.message || 'Failed to confirm arrival');
        return;
      }
      setActiveBookings((prev) => prev.map((b) => (
        b.id === booking.id
          ? {
            ...b,
            mobilization_arrival_photo_url: photoUrl,
            mobilization_arrival_confirmed_at: new Date().toISOString(),
            mobilization_arrival_released: result.releaseAmount ?? b.mobilization_arrival_released,
          }
          : b
      )));
      setArrivalMilestoneModal(null);
      setArrivalPhotoFile(null);
      alert('Arrival confirmed — remaining transport amount released to owner');
    } catch (err) {
      setArrivalMilestoneError(err?.message || 'Failed to confirm arrival');
    } finally {
      setArrivalMilestoneBusyId(null);
    }
  };

  const handleDownloadGstInvoice = useCallback(async (booking) => {
    const bookingId = booking?.id;
    if (!bookingId) {
      alert(t('pdfInvoiceFailed'));
      return;
    }
    try {
      const data = await secureFetch(`/api/bookings/${encodeURIComponent(bookingId)}/invoice-data`);
      if (!data?.booking) {
        alert(t('pdfInvoiceFailed'));
        return;
      }
      await generatePremiumClientInvoice(buildGstInvoicePayload(data.booking, user, t));
    } catch (err) {
      console.error('GST invoice PDF failed:', err);
      alert(err?.message || t('pdfInvoiceFailed'));
    }
  }, [user, t]);
  const handleDownloadBookingReport = useCallback(async () => {
    if (reportRows.length === 0) return;
    try {
      await generatePremiumClientBookingReport(reportRows, clientDisplayName);
    } catch (err) {
      console.error('Booking report PDF failed:', err);
      alert(t('pdfReportFailed'));
    }
  }, [reportRows, clientDisplayName]);

  const handleDownloadBookingHistoryPdf = useCallback(async () => {
    const historySource = activeBookings.filter((b) => !isBookingCancelled(b));
    if (historySource.length === 0) return;
    try {
      const historyRows = historySource.map((b) => {
        const row = normalizeReportBooking(b, machineLookup, t);
        return {
          ...row,
          sortDate: b.start_date || b.created_at || null,
        };
      });
      const cancelledCount = activeBookings.filter((b) => isBookingCancelled(b)).length;
      await generateClientBookingHistoryPDF({
        bookings: historyRows,
        clientName: clientDisplayName,
        clientPhone: user.phone
          ? (String(user.phone).startsWith('+') ? user.phone : `+91-${user.phone}`)
          : '',
        cancelledCount,
      });
    } catch (err) {
      console.error('Booking history PDF failed:', err);
      alert(t('pdfReportFailed'));
    }
  }, [activeBookings, machineLookup, clientDisplayName, user.phone, t]);

  useEffect(() => {
    const machineId = selectedMachine?.id;
    if (!machineId || hireMode === 'contract' || bookingType === 'hourly') {
      setMachineBookedDates([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await secureFetch(`/api/bookings/machine/${encodeURIComponent(machineId)}/booked-dates`);
        if (!cancelled) {
          setMachineBookedDates(Array.isArray(data?.booked_dates) ? data.booked_dates : []);
        }
      } catch (err) {
        if (!cancelled) setMachineBookedDates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedMachine?.id, bookingType, hireMode]);

  useEffect(() => {
    if (!activeQuotation?.id
      || !['quoted', 'client_esign', 'owner_review', 'admin_review', 'awaiting_mobilization', 'mobilization_confirmed', 'converted']
        .includes(activeQuotation.status)) {
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const data = await getContractQuotation(activeQuotation.id);
        if (cancelled || !data?.success || !data?.quotation) return;
        const next = data.quotation;
        if (next.status === 'cancelled') {
          setActiveQuotation(null);
          setHireMode('hourly');
          return;
        }
        setActiveQuotation(next);
        if (next.status === 'owner_rejected') {
          returnToMachineSelectionAfterReject(
            'This machine is not available, please select another',
          );
          return;
        }
        if (['cancelled', 'expired', 'converted'].includes(next.status)) {
          setContractQuote(mapQuotationToContractQuote(next));
          return;
        }
        if (next.status === 'admin_review' || next.status === 'client_esign') {
          setContractQuote(mapQuotationToContractQuote(next));
        }
        if (next.status === 'awaiting_mobilization'
          || next.status === 'mobilization_confirmed') {
          setContractQuote(mapQuotationToContractQuote(next));
        }
      } catch (_err) {
        // Ignore transient poll errors.
      }
    };
    const timer = setInterval(poll, 15000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeQuotation?.id, activeQuotation?.status]);

  useEffect(() => {
    if (activeQuotation?.id) return undefined;
    let cancelled = false;
    const restoreActiveQuotation = async () => {
      try {
        const [quotedRes, esignRes, ownerRes, adminRes, mobRes, mobConfirmedRes, convertedRes] = await Promise.all([
          secureFetch('/api/quotations?status=quoted'),
          secureFetch('/api/quotations?status=client_esign'),
          secureFetch('/api/quotations?status=owner_review'),
          secureFetch('/api/quotations?status=admin_review'),
          secureFetch('/api/quotations?status=awaiting_mobilization'),
          secureFetch('/api/quotations?status=mobilization_confirmed'),
          secureFetch('/api/quotations?status=converted'),
        ]);
        if (cancelled) return;
        const RESTORE_STATUSES = ['quoted', 'client_esign', 'owner_review', 'admin_review', 'awaiting_mobilization', 'mobilization_confirmed', 'converted'];
        const items = [
          ...(Array.isArray(quotedRes?.items) ? quotedRes.items : []),
          ...(Array.isArray(esignRes?.items) ? esignRes.items : []),
          ...(Array.isArray(ownerRes?.items) ? ownerRes.items : []),
          ...(Array.isArray(adminRes?.items) ? adminRes.items : []),
          ...(Array.isArray(mobRes?.items) ? mobRes.items : []),
          ...(Array.isArray(mobConfirmedRes?.items) ? mobConfirmedRes.items : []),
          ...(Array.isArray(convertedRes?.items) ? convertedRes.items : []),
        ].filter((q) => q && RESTORE_STATUSES.includes(q.status));
        if (items.length === 0) return;
        items.sort((a, b) => String(b.createdAt || b.quotedAt || '').localeCompare(
          String(a.createdAt || a.quotedAt || ''),
        ));
        const next = items[0];
        const machine = (machineList || []).find((m) => String(m.id) === String(next.machineId))
          || (next.machine?.id
            ? {
              id: next.machine.id,
              name: next.machine.name || '',
              code: next.machine.machineId || '',
              type: '',
              regNo: '',
              rates: { perHour: 0, perDay: 0, perWeek: 0, perMonth: 0 },
              available: true,
              attachments: [],
              attachmentRates: {},
              attachmentMinHours: {},
            }
            : null);
        if (!machine) return;
        setActiveQuotation(next);
        setContractQuote(mapQuotationToContractQuote(next));
        setSelectedMachine(machine);
        setHireMode('contract');
        setHireModePicked(true);
        if (next.startDate) setStartDate(next.startDate);
        if (next.endDate) setEndDate(next.endDate);
        if (next.billingCycle) setBillingCycle(next.billingCycle);
        if (next.billingBasis) {
          setBillingBasis(next.billingBasis === 'working_days_26' ? 'working_days_26' : 'calendar_month');
        }
        if (next.fixedRatePerCycle != null) setFixedRatePerCycle(String(next.fixedRatePerCycle));
        if (next.guaranteedHoursPerCycle != null) {
          setGuaranteedHoursPerCycle(String(next.guaranteedHoursPerCycle));
        }
        if (next.standbyPercent != null) setStandbyPercent(String(next.standbyPercent));
        if (next.monsoonStartMonth != null) setMonsoonStartMonth(String(next.monsoonStartMonth));
        if (next.monsoonEndMonth != null) setMonsoonEndMonth(String(next.monsoonEndMonth));
        if (next.terminationNoticeDays != null) {
          setTerminationNoticeDays(String(next.terminationNoticeDays));
        }
        if (next.terminationFeeFormula) setTerminationFeeFormula(next.terminationFeeFormula);
        if (next.terminationFeeFlat != null) setTerminationFeeFlat(String(next.terminationFeeFlat));
        if (next.ownerNoshowPenaltyPct != null) {
          setOwnerNoshowPenaltyPct(String(next.ownerNoshowPenaltyPct));
        }
        if (next.ownerAbandonmentPenaltyPct != null) {
          setOwnerAbandonmentPenaltyPct(String(next.ownerAbandonmentPenaltyPct));
        }
        if (next.ownerBreakdownCreditPct != null) {
          setOwnerBreakdownCreditPct(String(next.ownerBreakdownCreditPct));
        }
        if (next.clientTerminationPenaltyPct != null) {
          setClientTerminationPenaltyPct(String(next.clientTerminationPenaltyPct));
        }
        if (next.ownerPaymentDueDay != null) {
          setOwnerPaymentDueDay(String(next.ownerPaymentDueDay));
        }
        if (next.mobilizationTransportBy) setMobilizationTransportBy(next.mobilizationTransportBy);
        if (next.mobilizationTransportAmount != null) {
          setMobilizationTransportAmount(String(next.mobilizationTransportAmount));
        }
        if (next.demobilizationTransportBy) {
          setDemobilizationTransportBy(next.demobilizationTransportBy);
        }
        if (next.demobilizationTransportAmount != null) {
          setDemobilizationTransportAmount(String(next.demobilizationTransportAmount));
        }
        if (next.attachmentRatePerCycle != null) {
          setAttachmentRatePerCycle(String(next.attachmentRatePerCycle));
        }
        if (next.selectedAttachment) setSelectedAttachment(next.selectedAttachment);
        if (next.siteAddress) setSiteAddress(next.siteAddress);
        if (next.workDescription) setWorkDescription(next.workDescription);
      } catch (_err) {
        // Ignore restore errors — client can still request a new quotation.
      }
    };
    restoreActiveQuotation();
    return () => { cancelled = true; };
  }, [activeQuotation?.id, machineList]); // eslint-disable-line

  useEffect(() => {
    if (!activeQuotation?.id) {
      setShowQuotationTermsSnapshot(false);
      setEsignTermsAccepted(false);
      setEsignPaySuccess(null);
      setQuotationArrivalPhoto(null);
      setQuotationArrivalSuccess(false);
      setMandateInfo(null);
      setMandateError('');
      setMandateLoading(false);
    }
  }, [activeQuotation?.id]);

  useEffect(() => {
    if (!activeQuotation?.id || activeQuotation.status !== 'client_esign') {
      return undefined;
    }
    let cancelled = false;
    const loadMandate = async () => {
      try {
        const data = await secureFetch(
          `/api/client/mandates/${encodeURIComponent(activeQuotation.id)}`,
        );
        if (cancelled || !data?.success) return;
        setMandateInfo({
          mandate_id: data.mandate_id || null,
          mandate_status: data.mandate_status || null,
          mandate_max_amount: data.mandate_max_amount != null
            ? Number(data.mandate_max_amount)
            : null,
          mandate_bank_name: data.mandate_bank_name || null,
          authorization_url: data.authorization_url || null,
        });
      } catch (_err) {
        // Ignore transient mandate poll errors.
      }
    };
    loadMandate();
    const timer = setInterval(loadMandate, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeQuotation?.id, activeQuotation?.status]);

  const isHourlyAttachmentSplit = hireMode !== 'contract'
    && bookingType === 'hourly'
    && Boolean(selectedAttachment);

  const spotTotalHours = useMemo(() => {
    if (!isHourlyAttachmentSplit) {
      return bookedJobHoursEquiv(selectedMachine, bookingType, quantity);
    }
    const bucket = Number(bucketOnlyHours);
    const att = Number(attachmentUsageHours);
    const b = Number.isFinite(bucket) && bucket > 0 ? bucket : 0;
    const a = Number.isFinite(att) && att > 0 ? att : 0;
    return b + a;
  }, [
    isHourlyAttachmentSplit,
    selectedMachine,
    bookingType,
    quantity,
    bucketOnlyHours,
    attachmentUsageHours,
  ]);

  const effectiveQuantity = isHourlyAttachmentSplit ? spotTotalHours : quantity;

  const bookingQuote = useMemo(
    () => computeBookingQuote(selectedMachine, bookingType, effectiveQuantity, {
      selectedAttachment,
      attachmentUsageHours,
    }),
    [selectedMachine, bookingType, effectiveQuantity, selectedAttachment, attachmentUsageHours],
  );
  const displayedQuote = hireMode === 'contract'
    ? {
      base: Number(contractQuote?.baseAmount || 0),
      gst: Number(contractQuote?.gstAmount || 0),
      grandTotal: Number(contractQuote?.totalAmount || 0),
      advanceRequired: Number(contractQuote?.advanceRequired || 0),
      hireBase: Number(contractQuote?.hireBaseAmount || 0),
      hireGst: Number(contractQuote?.hireGstAmount || 0),
      hireTotal: Number(contractQuote?.hireTotalAmount || 0),
      hireAdvance: Number(contractQuote?.hireAdvanceRequired || 0),
      attachmentBase: Number(contractQuote?.attachmentBaseAmount || 0),
      attachmentGst: Number(contractQuote?.attachmentGstAmount || 0),
      attachmentTotal: Number(contractQuote?.attachmentTotalAmount || 0),
      attachmentRatePerCycle: Number(contractQuote?.attachmentRatePerCycle || 0),
      hireSubtotal: Number(
        contractQuote?.combinedHireBaseAmount
        ?? ((Number(contractQuote?.hireBaseAmount || 0) + Number(contractQuote?.attachmentBaseAmount || 0))),
      ),
      hireGstCombined: Number(
        contractQuote?.combinedHireGstAmount
        ?? ((Number(contractQuote?.hireGstAmount || 0) + Number(contractQuote?.attachmentGstAmount || 0))),
      ),
      totalHireValueA: Number(
        contractQuote?.combinedHireTotalAmount
        ?? ((Number(contractQuote?.hireTotalAmount || 0) + Number(contractQuote?.attachmentTotalAmount || 0))),
      ),
      mobilizationBase: Number(contractQuote?.mobilizationBaseAmount || 0),
      mobilizationGst: Number(contractQuote?.mobilizationGstAmount || 0),
      mobilizationTotal: Number(contractQuote?.mobilizationTotalAmount || 0),
      mobilizationCollected: Boolean(contractQuote?.mobilizationCollectedAtCreate),
      gstRate: Number(contractQuote?.gstRate || 0.18),
    }
    : bookingQuote;
  const { base: totalCost, gst: gstAmount, grandTotal, advanceRequired: advanceAmt } = displayedQuote;
  const renderContractQuoteBreakdown = () => {
    const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
    const row = (label, val, opts = {}) => (
      <div
        key={opts.key || label}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '5px 0',
          borderBottom: opts.bold ? 'none' : '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <span style={{
          color: opts.bold ? '#c9a84c' : '#8896a8',
          fontSize: opts.bold ? '12px' : '12px',
          fontWeight: opts.bold ? 700 : 400,
        }}
        >
          {label}
        </span>
        <span style={{
          color: opts.bold ? '#c9a84c' : '#e8e0d0',
          fontSize: opts.bold ? '13px' : '12px',
          fontWeight: 600,
        }}
        >
          {val}
        </span>
      </div>
    );
    const sectionTitle = (title) => (
      <p
        key={`sec-${title}`}
        style={{
          color: '#c9a84c',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          margin: '10px 0 4px',
        }}
      >
        {title}
      </p>
    );
    const gstLabel = formatGstPctLabel(displayedQuote.gstRate);
    const A = Number(displayedQuote.totalHireValueA || 0);
    const bookingAdvance = Number(displayedQuote.hireAdvance || 0);
    const remainingHire = Math.max(0, A - bookingAdvance);
    const nodes = [
      sectionTitle('Hire Charges'),
      row('Machine Hire', inr(displayedQuote.hireBase)),
    ];
    if (Number(displayedQuote.attachmentTotal || 0) > 0) {
      nodes.push(row('Attachment Hire', inr(displayedQuote.attachmentBase)));
    }
    nodes.push(
      row('Subtotal', inr(displayedQuote.hireSubtotal), { key: 'hire-subtotal' }),
      row(gstLabel, inr(displayedQuote.hireGstCombined), { key: 'hire-gst' }),
      row('Total Hire Value (A)', inr(A), { bold: true, key: 'hire-total-a' }),
    );
    if (displayedQuote.mobilizationCollected) {
      nodes.push(
        sectionTitle('Mobilization'),
        row('Transport Charge', inr(displayedQuote.mobilizationBase), { key: 'mob-transport' }),
        row(gstLabel, inr(displayedQuote.mobilizationGst), { key: 'mob-gst' }),
        row('Total Mobilization (B)', inr(displayedQuote.mobilizationTotal), { bold: true, key: 'mob-total-b' }),
      );
    }
    nodes.push(sectionTitle('Estimated Advance (after approval)'));
    nodes.push(row('Booking Advance (15% of A)', inr(bookingAdvance)));
    if (displayedQuote.mobilizationCollected) {
      nodes.push(row('Mobilization Charge (100% of B)', inr(displayedQuote.mobilizationTotal)));
    }
    nodes.push(row('Estimated Advance Total', inr(advanceAmt), { bold: true }));
    nodes.push(sectionTitle('Balance'));
    nodes.push(row('Remaining Hire Value (A − Booking Advance)', inr(remainingHire)));
    nodes.push(
      <p
        key="ra-note"
        style={{
          color: '#8896a8',
          fontSize: '10px',
          margin: '6px 0 0',
          lineHeight: 1.35,
        }}
      >
        No payment now. Remaining Hire Value is billed via monthly RA-Bills after the contract starts.
      </p>,
    );
    return nodes;
  };
  const previewCycles = useMemo(
    () => countContractCycles(startDate, endDate, billingCycle, billingBasis),
    [startDate, endDate, billingCycle, billingBasis],
  );

  const applyAttachmentRateSuggestion = useCallback((machine, attachmentId, gmhValue, currentFieldValue) => {
    const suggested = suggestAttachmentRatePerCycle(machine, attachmentId, gmhValue);
    const current = String(currentFieldValue ?? '').trim();
    const prev = attachmentRateAutoSuggestion;
    const matchesPrev = prev != null
      && (current === '' || current === String(prev) || Number(current) === Number(prev));
    const canOverwrite = current === '' || matchesPrev;

    if (suggested == null) {
      if (canOverwrite) setAttachmentRatePerCycle('');
      setAttachmentRateAutoSuggestion(null);
      return null;
    }
    if (canOverwrite) {
      setAttachmentRatePerCycle(String(suggested));
      setAttachmentRateAutoSuggestion(suggested);
    }
    return suggested;
  }, [attachmentRateAutoSuggestion]);

  useEffect(() => {
    if (hireMode !== 'contract' || !selectedMachine || !selectedAttachment) return;
    applyAttachmentRateSuggestion(
      selectedMachine,
      selectedAttachment,
      guaranteedHoursPerCycle,
      attachmentRatePerCycle,
    );
    // Intentionally depend on attachment + GMH + machine, not on every keystroke of the rate field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hireMode, selectedMachine, selectedAttachment, guaranteedHoursPerCycle]);

  const attachmentHourlyExtra = selectedAttachment && selectedMachine?.attachmentRates
    ? Number(selectedMachine.attachmentRates[selectedAttachment] || 0)
    : 0;
  const attachmentGmhNum = Number(guaranteedHoursPerCycle);
  const showAttachmentRateHint = hireMode === 'contract'
    && selectedAttachment
    && attachmentHourlyExtra > 0
    && attachmentGmhNum > 0;

  const attachmentMinHoursBooked = useMemo(() => {
    // Spot only — compare min-hours against attachment usage hours.
    if (hireMode === 'contract') return null;
    if (!selectedMachine || !selectedAttachment) return null;
    const usage = Number(attachmentUsageHours);
    return Number.isFinite(usage) ? usage : 0;
  }, [
    selectedMachine,
    selectedAttachment,
    hireMode,
    attachmentUsageHours,
  ]);

  const attachmentUsageHoursError = useMemo(() => {
    if (hireMode === 'contract') return null;
    if (!selectedMachine || !selectedAttachment) return null;
    return attachmentUsageHoursGateError({
      machine: selectedMachine,
      bookingType,
      quantity: effectiveQuantity,
      selectedAttachment,
      attachmentUsageHours,
    });
  }, [
    hireMode,
    selectedMachine,
    selectedAttachment,
    bookingType,
    effectiveQuantity,
    attachmentUsageHours,
  ]);

  // Non-hourly only: usage capped by job hours-equiv (legacy ceiling path).
  const attachmentUsageMaxHours = useMemo(() => {
    if (!selectedMachine || bookingType === 'hourly') return ATTACHMENT_HOURS_SANITY_MAX;
    const total = bookedJobHoursEquiv(selectedMachine, bookingType, quantity);
    return Number.isFinite(total) && total > 0 ? total : ATTACHMENT_HOURS_SANITY_MAX;
  }, [selectedMachine, bookingType, quantity]);

  const attachmentMinRequired = useMemo(() => {
    if (hireMode === 'contract' || !selectedMachine || !selectedAttachment) return 0;
    return readAttachmentMinHours(selectedMachine, selectedAttachment);
  }, [hireMode, selectedMachine, selectedAttachment]);

  // Feasibility-vs-duration only for daily/weekly/monthly (not hourly additive).
  const attachmentDurationBlocked = useMemo(() => {
    if (hireMode === 'contract') return null;
    if (bookingType === 'hourly') return null;
    if (!selectedMachine || !selectedAttachment) return null;
    return attachmentDurationFeasibilityError({
      machine: selectedMachine,
      bookingType,
      quantity,
      selectedAttachment,
    });
  }, [hireMode, selectedMachine, selectedAttachment, bookingType, quantity]);

  const hourlyTotalFloorError = useMemo(() => {
    if (!isHourlyAttachmentSplit || !selectedMachine) return null;
    return spotHourlyTotalFloorError({
      machine: selectedMachine,
      totalHours: spotTotalHours,
    });
  }, [isHourlyAttachmentSplit, selectedMachine, spotTotalHours]);

  const attachmentMinHoursError = useMemo(() => {
    if (hireMode === 'contract') return null;
    return attachmentUsageHoursError || hourlyTotalFloorError;
  }, [hireMode, attachmentUsageHoursError, hourlyTotalFloorError]);

  const workDescriptionError = useMemo(() => {
    const v = String(workDescription || '').trim();
    if (!v || v.length < 20) return 'Work description is required (min 20 characters).';
    if (CONTACT_NUMBER_RE.test(v.replace(/\s/g, ''))) return SITE_CONTACT_MSG;
    return null;
  }, [workDescription]);

  const siteAddressError = useMemo(() => {
    const v = String(siteAddress || '').trim();
    if (!v || v.length < 10) return 'Site address is required (min 10 characters).';
    if (CONTACT_NUMBER_RE.test(v.replace(/\s/g, ''))) return SITE_CONTACT_MSG;
    return null;
  }, [siteAddress]);

  const sitePincodeError = useMemo(() => {
    const v = String(sitePincode || '').trim();
    if (!PINCODE_RE.test(v)) return 'Pincode must be exactly 6 digits.';
    return null;
  }, [sitePincode]);

  const siteGpsRequiredError = useMemo(() => {
    if (!(Number.isFinite(siteLat) && Number.isFinite(siteLng) && locationSource === 'gps')) {
      return 'Site GPS location is required.';
    }
    return null;
  }, [siteLat, siteLng, locationSource]);

  const siteDetailsValid = !workDescriptionError
    && !siteAddressError
    && !sitePincodeError
    && !siteGpsRequiredError;

  const attachmentMinHoursUiNotice = useMemo(() => {
    if (hireMode === 'contract') return null;
    if (attachmentDurationBlocked) return null;
    if (!selectedMachine || !selectedAttachment || attachmentMinHoursBooked == null) return null;
    return attachmentMinHoursNotice({
      machine: selectedMachine,
      selectedAttachment,
      bookedHours: attachmentMinHoursBooked,
      mode: 'spot',
    });
  }, [
    selectedMachine,
    selectedAttachment,
    attachmentMinHoursBooked,
    hireMode,
    attachmentDurationBlocked,
  ]);

  // Non-hourly: seed usage when duration becomes feasible (legacy).
  // Hourly additive: seed attachment to owner min when empty; never clear for duration block.
  useEffect(() => {
    if (hireMode === 'contract') return;
    if (!selectedMachine || !selectedAttachment) return;

    if (bookingType === 'hourly') {
      const minH = attachmentMinRequired;
      if (!(minH > 0)) return;
      const usage = Number(attachmentUsageHours);
      if (attachmentUsageHours === '' || !Number.isFinite(usage) || usage <= 0) {
        setAttachmentUsageHours(minH);
      }
      return;
    }

    if (attachmentDurationBlocked) {
      if (attachmentUsageHours !== '') setAttachmentUsageHours('');
      return;
    }
    const minH = attachmentMinRequired;
    if (!(minH > 0)) return;
    const maxH = attachmentUsageMaxHours;
    const usage = Number(attachmentUsageHours);
    const invalid = attachmentUsageHours === ''
      || !Number.isFinite(usage)
      || usage < minH
      || usage > maxH;
    if (invalid) {
      setAttachmentUsageHours(Math.min(maxH, minH));
    }
  }, [
    hireMode,
    selectedMachine,
    selectedAttachment,
    bookingType,
    attachmentDurationBlocked,
    attachmentMinRequired,
    attachmentUsageMaxHours,
    attachmentUsageHours,
  ]);

  useEffect(() => {
    if (bookingStep !== 2) return undefined;
    let cancelled = false;
    getSavedAddresses()
      .then((result) => {
        if (cancelled) return;
        setSavedAddresses(Array.isArray(result.addresses) ? result.addresses.slice(0, 5) : []);
      })
      .catch(() => {
        if (!cancelled) setSavedAddresses([]);
      });
    return () => { cancelled = true; };
  }, [bookingStep]);

  const closePaymentModal = () => {
    setShowPayment(false);
    setPendingIdempotencyKey(null);
    setBookingStep(2);
  };

  const resetSiteLocation = () => {
    setSiteAddress('');
    setSitePincode('');
    setSiteLat(null);
    setSiteLng(null);
    setSelectedSavedAddressId('');
    setLocationSource(null);
    setSiteGpsLoading(false);
    setSiteGpsError('');
    setSiteFieldTouched({ work: false, address: false, pincode: false, gps: false });
    setSiteBookAttempted(false);
  };

  const resetBookingFlow = () => {
    setBookingStep(1);
    setSelectedMachine(null);
    setShowPayment(false);
    setPendingIdempotencyKey(null);
    resetSiteLocation();
    setSelectedAttachment(null);
    setAttachmentUsageHours('');
    setBucketOnlyHours(0);
    setWorkDescription('');
    setHireMode('hourly');
    setHireWizardStep(1);
    setHireModePicked(false);
    setBookingType('hourly');
    setBillingCycle('monthly');
    setFixedRatePerCycle('');
    setGuaranteedHoursPerCycle('');
    setMobilizationTransportBy('');
    setMobilizationTransportAmount('');
    setDemobilizationTransportBy('');
    setDemobilizationTransportAmount('');
    setAttachmentRatePerCycle('');
    setAttachmentRateAutoSuggestion(null);
    setContractQuote(null);
    setContractQuoteError('');
    setActiveQuotation(null);
    setQuotationTermsAccepted(false);
    setQuotationActionLoading(false);
    setQuotationRejectedMessage('');
  };

  const handleSiteAddressChange = (e) => {
    setSiteAddress(e.target.value);
    if (!gpsFillingRef.current) {
      setLocationSource('manual');
      setSelectedSavedAddressId('');
    }
  };

  const handleSitePincodeChange = (e) => {
    const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 6);
    setSitePincode(digits);
    if (!gpsFillingRef.current) {
      setLocationSource('manual');
      setSelectedSavedAddressId('');
    }
    setSiteFieldTouched((prev) => ({ ...prev, pincode: true }));
  };

  const persistSiteAddressAfterBooking = () => {
    const address = String(siteAddress || '').trim();
    if (!address) return;
    saveAddress({
      address,
      pincode: String(sitePincode || '').trim() || null,
      lat: Number.isFinite(siteLat) ? siteLat : null,
      lng: Number.isFinite(siteLng) ? siteLng : null,
    }).catch(() => {});
  };

  const handleSelectSavedAddress = (id) => {
    const nextId = String(id || '').trim();
    setSelectedSavedAddressId(nextId);
    if (!nextId) return;
    const row = savedAddresses.find((a) => String(a.id) === nextId);
    if (!row) return;
    gpsFillingRef.current = true;
    setSiteAddress(String(row.address || ''));
    setSitePincode(String(row.pincode || '').replace(/\D/g, '').slice(0, 6));
    const latRaw = row.lat;
    const lngRaw = row.lng;
    const lat = latRaw == null || latRaw === '' ? NaN : Number(latRaw);
    const lng = lngRaw == null || lngRaw === '' ? NaN : Number(lngRaw);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setSiteLat(lat);
      setSiteLng(lng);
      setLocationSource('gps');
    } else {
      setSiteLat(null);
      setSiteLng(null);
      setLocationSource('manual');
    }
    setSiteFieldTouched((prev) => ({ ...prev, address: true, pincode: true, gps: true }));
    setTimeout(() => { gpsFillingRef.current = false; }, 0);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSiteGpsError(t('siteGpsUnavailable'));
      return;
    }
    setSiteGpsLoading(true);
    setSiteGpsError('');
    try {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const lat = pos.coords.latitude;
            const lng = pos.coords.longitude;
            setSiteLat(lat);
            setSiteLng(lng);
            setLocationSource('gps');
            try {
              const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&format=json`,
                { headers: { Accept: 'application/json' } }
              );
              if (res.ok) {
                const data = await res.json();
                gpsFillingRef.current = true;
                setSiteAddress(data.display_name || '');
                setSitePincode(data.address?.postcode || '');
                setTimeout(() => { gpsFillingRef.current = false; }, 0);
              }
            } catch {
              // lat/lng captured; user can enter address manually
            }
          } finally {
            setSiteGpsLoading(false);
          }
        },
        (err) => {
          setSiteGpsLoading(false);
          setSiteGpsError(err.code === 1 ? t('siteGpsDenied') : t('siteGpsUnavailable'));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    } catch {
      setSiteGpsLoading(false);
      setSiteGpsError(t('siteGpsUnavailable'));
    }
  };

  const formatJobRequestMachines = (machines) => (machines || []).map((m) => ({
    id: m.id,
    code: m.machine_id,
    name: m.name, type: m.type, regNo: m.reg_no,
    year: m.year, location: m.location, fuel: m.fuel_level,
    condition: m.condition_pct, available: m.status !== 'Deployed',
    owner_id: m.owner_id || null,
    operator_id: m.operator_id || null,
    operator: m.operator?.name || null,
    operatorExp: m.operator?.experience_years || 0,
    operatorMachines: m.operator?.experience_machine_types || [],
    minimumHours: m.minimum_hours || 3,
    rank: m.rank || null,
    isBestMatch: m.is_best_match || false,
    settledJobsCount: m.settledJobsCount ?? m.settled_jobs_count ?? 0,
    score: m.score ?? null,
    distanceKm: m.distance_km ?? null,
    fuelCapacity: Number(m.fuel_capacity_litres || 0),
    rates: { perHour: m.rate_per_hour, perDay: m.rate_per_day, perWeek: m.rate_per_week, perMonth: m.rate_per_month },
    fuelBasis: m.fuel_basis || 'negotiable',
    ratePerHourFuelIncluded: m.rate_per_hour_fuel_included || m.rate_per_hour || null,
    ratePerHourFuelExcluded: m.rate_per_hour_fuel_excluded || null,
    operatorIncluded: m.operator_included !== false,
    operatorDailyRate: m.operator_daily_rate || null,
    advance: { hourly: 3, weekly: 3, monthly: 15 },
    specs: 'Engine: 92 HP | Bucket: 0.21 m3',
    attachments: m.attachments || [],
    attachmentRates: m.attachment_rates && typeof m.attachment_rates === 'object'
      ? m.attachment_rates
      : {},
    attachmentMinHours: m.attachment_min_hours && typeof m.attachment_min_hours === 'object'
      ? m.attachment_min_hours
      : {},
    working_hours_start: m.working_hours_start || null,
    working_hours_end: m.working_hours_end || null,
    working_days: m.working_days || null,
    below_minimum_hours: m.below_minimum_hours || false,
  }));

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      alert('Location detection not supported on this device.');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { 'Accept-Language': 'en' } },
          );
          const data = await res.json();
          const pin = data?.address?.postcode?.replace(/\D/g, '').slice(0, 6);
          if (pin && /^\d{6}$/.test(pin)) {
            setJobPin(pin);
          } else {
            alert('Could not detect pincode from your location. Please enter manually.');
          }
        } catch {
          alert('Location detection failed. Please enter pincode manually.');
        } finally {
          setGpsLoading(false);
        }
      },
      () => {
        alert('Location permission denied. Please enter pincode manually.');
        setGpsLoading(false);
      },
      { timeout: 10000, maximumAge: 60000 },
    );
  };

  const handleFindMachines = async () => {
    const type = String(jobType || '').trim();
    const hours = Number(jobHours);
    const date = String(jobDate || '').trim();
    const pin = String(jobPin || '').trim();
    const today = toDateInputValue(new Date());

    if (!type) {
      setJobFindError('Select a machine type.');
      return;
    }
    // machine minimum enforced post-selection
    if (!Number.isFinite(hours) || hours < 0.25) {
      setJobFindError('Minimum booking duration is 15 minutes');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date < today) {
      setJobFindError('Select a valid date (today or later).');
      return;
    }
    if (!PINCODE_RE.test(pin)) {
      setJobFindError('Enter a 6-digit site pincode.');
      return;
    }

    setJobFindLoading(true);
    setJobFindError('');
    try {
      let lat = null;
      let lng = null;
      const geoRes = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(pin)}&countrycodes=in&format=json&limit=1`,
        { headers: { Accept: 'application/json' } },
      );
      if (geoRes.ok) {
        const geoRows = await geoRes.json();
        const row = Array.isArray(geoRows) ? geoRows[0] : null;
        const parsedLat = Number(row?.lat);
        const parsedLng = Number(row?.lon);
        if (Number.isFinite(parsedLat) && Number.isFinite(parsedLng)) {
          lat = parsedLat;
          lng = parsedLng;
        }
      }
      if (lat == null || lng == null) {
        setJobFindError('Could not find location for this pincode. Check the pin and try again.');
        return;
      }

      const useJobRanking = hireMode !== 'contract' && hireMode !== 'site_work';
      const machines = await getMachines({
        type,
        hours,
        date,
        lat,
        lng,
        radius: 100,
        limit: '3',
        ...(useJobRanking ? { ranking: 'job_request' } : {}),
      });
      setSearchResults(formatJobRequestMachines(machines));
    } catch {
      setJobFindError('Could not find machines. Please try again.');
    } finally {
      setJobFindLoading(false);
    }
  };

  const buildSiteLocationPayload = () => {
    const address = siteAddress.trim();
    if (!address) return {};
    const payload = {
      site_address: address,
      location_source: locationSource || 'manual',
    };
    const pincode = sitePincode.trim();
    if (pincode) payload.site_pincode = pincode;
    if (Number.isFinite(siteLat)) payload.site_lat = siteLat;
    if (Number.isFinite(siteLng)) payload.site_lng = siteLng;
    return payload;
  };

  const handleClientNavTab = (tabId) => {
    if (tabId === 'reports') tabId = 'mybookings';
    if (tabId === 'newbooking' && activeTab !== 'newbooking') {
      setBookingStep(selectedMachine ? 2 : 1);
    }
    setActiveTab(tabId);
    window.history.pushState({ tab: tabId }, '', '/client');
  };

  const selectMachineForBooking = (machine) => {
    const openQuotationMachineId = activeQuotation?.machineId ?? activeQuotation?.machine?.id;
    if (activeQuotation?.id && String(openQuotationMachineId) === String(machine?.id)) {
      if (!window.confirm('You have an open quotation for this machine. Select it anyway and clear the existing quotation?')) return;
    }
    if (activeQuotation?.id && String(openQuotationMachineId) !== String(machine?.id)) {
      const confirmed = window.confirm(
        'You have an open quotation for another machine. Selecting this machine will cancel that quotation. Continue?',
      );
      if (!confirmed) return;
    }
    setSelectedMachine(machine);
    setQuantity(machine?.minimumHours || 3);
    if (!jobHoursHH || Number(jobHoursHH) < (machine?.minimumHours || 3)) {
      setJobHoursHH(String(machine?.minimumHours || 3));
      setJobMM('00');
    }
    setHireMode('hourly');
    setHireWizardStep(1);
    setHireModePicked(false);
    setBookingType('hourly');
    setBillingCycle('monthly');
    setBillingBasis(defaultBillingBasisFromClientType(clientUser?.client_type || user?.client_type));
    setFixedRatePerCycle('');
    setGuaranteedHoursPerCycle('');
    setMobilizationTransportBy('');
    setMobilizationTransportAmount('');
    setDemobilizationTransportBy('');
    setDemobilizationTransportAmount('');
    setAttachmentRatePerCycle('');
    setAttachmentRateAutoSuggestion(null);
    setContractQuote(null);
    setContractQuoteError('');
    setActiveQuotation(null);
    setQuotationTermsAccepted(false);
    setQuotationRejectedMessage('');
    setSelectedAttachment(null);
    setAttachmentUsageHours('');
    setBucketOnlyHours(0);
    setWorkDescription('');
    setBookingStep(2);
    setActiveTab('newbooking');
  };

  const handleBookAgain = (booking) => {
    const machineId = booking?.machine_id;
    const machineCode = booking?.machines?.machine_id;
    const machine = displayMachines.find((m) => (
      (machineId != null && String(m.id) === String(machineId))
      || (machineCode && String(m.code) === String(machineCode))
    ));
    if (!machine || !machine.available) {
      alert(t('machineNoLongerAvailable'));
      return;
    }
    selectMachineForBooking(machine);
  };

  const openWalletRecharge = (prefillAmount) => {
    if (prefillAmount != null) {
      setRechargeAmt(String(prefillAmount));
    }
    if (useMobileNav) {
      setActiveTab('wallet');
      return;
    }
    setShowRecharge(true);
  };

  const openRechargeForBooking = () => {
    const shortfall = Math.max(1, advanceAmt - (walletBalance ?? 0));
    openWalletRecharge(shortfall);
  };

  const invalidateQuotedDraft = () => {
    setContractQuote(null);
    setContractQuoteError('');
    setQuotationTermsAccepted(false);
    setActiveQuotation((prev) => {
      if (prev && (prev.status === 'owner_review' || prev.status === 'admin_review')) {
        return prev;
      }
      return null;
    });
  };

  const mapQuotationToContractQuote = (quotation) => {
    if (!quotation) return null;
    const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    const hireTaxable = Number(quotation.hireTaxable || 0);
    const hireGst = Number(quotation.hireGst || 0);
    const hireTotal = Number(quotation.totalHireValueA || 0);
    const mobTaxable = Number(quotation.mobTaxable || 0);
    const mobGst = Number(quotation.mobGst || 0);
    const mobTotal = Number(quotation.totalMobilizationB || 0);
    const cycles = Number(quotation.quantity || 0);
    const attachmentRatePerCycle = Number(quotation.attachmentRatePerCycle || 0);
    const gstRate = 0.18;
    // hire_* columns store combined machine+attachment; split for the breakdown lines.
    const attachmentBaseAmount = attachmentRatePerCycle > 0 && cycles > 0
      ? round2(attachmentRatePerCycle * cycles)
      : 0;
    const attachmentGstAmount = attachmentBaseAmount > 0
      ? round2(attachmentBaseAmount * gstRate)
      : 0;
    const attachmentTotalAmount = round2(attachmentBaseAmount + attachmentGstAmount);
    return {
      cycles,
      baseAmount: hireTaxable + mobTaxable,
      gstAmount: hireGst + mobGst,
      totalAmount: Number(quotation.contractTotal || 0),
      advanceRequired: Number(quotation.advanceRequired || 0),
      hireBaseAmount: round2(Math.max(0, hireTaxable - attachmentBaseAmount)),
      hireGstAmount: round2(Math.max(0, hireGst - attachmentGstAmount)),
      hireTotalAmount: round2(Math.max(0, hireTotal - attachmentTotalAmount)),
      hireAdvanceRequired: Number(quotation.advanceHire || 0),
      attachmentBaseAmount,
      attachmentGstAmount,
      attachmentTotalAmount,
      attachmentRatePerCycle,
      combinedHireBaseAmount: hireTaxable,
      combinedHireGstAmount: hireGst,
      combinedHireTotalAmount: hireTotal,
      mobilizationBaseAmount: mobTaxable,
      mobilizationGstAmount: mobGst,
      mobilizationTotalAmount: mobTotal,
      mobilizationCollectedAtCreate: Number(quotation.advanceMobilization || 0) > 0,
      gstRate,
      billingCycle: quotation.billingCycle,
      billingBasis: quotation.billingBasis || 'calendar_month',
      fixedRatePerCycle: Number(quotation.fixedRatePerCycle || 0),
      startDate: quotation.startDate,
      endDate: quotation.endDate,
    };
  };

  const returnToMachineSelectionAfterReject = (message) => {
    setQuotationRejectedMessage(
      message || 'This machine is not available, please select another',
    );
    setActiveQuotation(null);
    setContractQuote(null);
    setQuotationTermsAccepted(false);
    setEsignTermsAccepted(false);
    setEsignPaySuccess(null);
    setSelectedMachine(null);
    setBookingStep(1);
    setActiveTab('newbooking');
  };

  const handleRequestQuotation = async () => {
    if (clientKycBookingBlocked) {
      setContractQuoteError(clientKycBookingBlockMessage);
      return;
    }
    if (!guaranteedHoursPerCycle || Number(guaranteedHoursPerCycle) <= 0) {
      setContractQuoteError('Enter guaranteed hours per cycle.');
      return;
    }
    if (!mobilizationTransportBy || !demobilizationTransportBy) {
      setContractQuoteError('Choose who pays mobilization and demobilization transport.');
      return;
    }
    if (mobilizationTransportBy === 'client' && !(Number(mobilizationTransportAmount) > 0)) {
      setContractQuoteError('Enter mobilization transport amount when client pays.');
      return;
    }
    if (demobilizationTransportBy === 'client' && !(Number(demobilizationTransportAmount) > 0)) {
      setContractQuoteError('Enter demobilization transport amount when client pays (record only — not collected now).');
      return;
    }
    if (!String(siteAddress || '').trim() || !String(workDescription || '').trim()) {
      setContractQuoteError('Enter site address and work description before requesting a quotation.');
      return;
    }
    const standbyNum = Number(standbyPercent);
    if (!(standbyNum >= 30 && standbyNum <= 75)) {
      setContractQuoteError('Standby rate must be between 30% and 75%.');
      return;
    }
    if (Number(monsoonStartMonth) > Number(monsoonEndMonth)) {
      setContractQuoteError('End month must be after start month.');
      return;
    }
    if (!(Number(terminationNoticeDays) > 0)) {
      setContractQuoteError('Enter a valid termination notice period (days).');
      return;
    }
    if (terminationFeeFormula === 'flat' && !(Number(terminationFeeFlat) >= 0)) {
      setContractQuoteError('Enter a valid flat termination fee.');
      return;
    }
    const noshowPct = Number(ownerNoshowPenaltyPct);
    const abandonPct = Number(ownerAbandonmentPenaltyPct);
    const breakdownPct = Number(ownerBreakdownCreditPct);
    const clientExitPct = Number(clientTerminationPenaltyPct);
    if (!(noshowPct >= 25 && noshowPct <= 50)) {
      setContractQuoteError('No-show penalty must be between 25% and 50%.');
      return;
    }
    if (!(abandonPct >= 20 && abandonPct <= 40)) {
      setContractQuoteError('Abandonment penalty must be between 20% and 40%.');
      return;
    }
    if (!(breakdownPct >= 5 && breakdownPct <= 15)) {
      setContractQuoteError('Breakdown compensation must be between 5% and 15%.');
      return;
    }
    if (!(clientExitPct >= 20 && clientExitPct <= 40)) {
      setContractQuoteError('Early exit penalty must be between 20% and 40%.');
      return;
    }
    const paymentDueDay = Number(ownerPaymentDueDay);
    if (!Number.isInteger(paymentDueDay) || paymentDueDay < 1 || paymentDueDay > 28) {
      setContractQuoteError('Payment due day must be between 1 and 28.');
      return;
    }

    setContractQuoteLoading(true);
    setQuotationActionLoading(true);
    setContractQuoteError('');
    try {
      const response = await createContractQuotation({
        machineId: selectedMachine?.id,
        startDate,
        endDate,
        billingCycle,
        billingBasis,
        fixedRatePerCycle,
        guaranteedHoursPerCycle,
        standbyPercent: Number(standbyPercent),
        monsoonStartMonth: Number(monsoonStartMonth),
        monsoonEndMonth: Number(monsoonEndMonth),
        terminationNoticeDays: Number(terminationNoticeDays),
        terminationFeeFormula,
        terminationFeeFlat: terminationFeeFormula === 'flat'
          ? (terminationFeeFlat === '' ? null : Number(terminationFeeFlat))
          : null,
        ownerNoshowPenaltyPct: noshowPct,
        ownerAbandonmentPenaltyPct: abandonPct,
        ownerBreakdownCreditPct: breakdownPct,
        clientTerminationPenaltyPct: clientExitPct,
        ownerPaymentDueDay: paymentDueDay,
        attachmentRatePerCycle: selectedAttachment && attachmentRatePerCycle !== ''
          ? attachmentRatePerCycle
          : null,
        selectedAttachment: selectedAttachment || null,
        mobilizationTransportBy,
        mobilizationTransportAmount: mobilizationTransportBy === 'client'
          ? mobilizationTransportAmount
          : (mobilizationTransportAmount || null),
        demobilizationTransportBy,
        demobilizationTransportAmount: demobilizationTransportBy === 'client'
          ? demobilizationTransportAmount
          : (demobilizationTransportAmount || null),
        siteAddress: siteAddress.trim(),
        siteLocation: siteAddress.trim() || null,
        workDescription: workDescription.trim(),
      });
      if (!response?.success || !response?.quotation) {
        setContractQuoteError(response?.message || 'Could not create quotation');
        return;
      }
      setActiveQuotation(response.quotation);
      setContractQuote(mapQuotationToContractQuote(response.quotation));
      setShowQuotationResult(true);
      setQuotationTermsAccepted(false);
      setPaymentDueDateAccepted(false);
      setClientPaymentDueDayProposal('');
    } catch (error) {
      setContractQuoteError(error?.message || 'Could not create quotation');
    } finally {
      setContractQuoteLoading(false);
      setQuotationActionLoading(false);
    }
  };

  const handleAcceptTermsAndSendQuotation = async () => {
    if (!activeQuotation?.id) {
      setContractQuoteError('Request a quotation first.');
      return;
    }
    if (!quotationTermsAccepted) {
      setContractQuoteError('Accept the contract terms before sending to the owner.');
      return;
    }
    if (!paymentDueDateAccepted) {
      setContractQuoteError('Agree to the payment due date before sending to the owner.');
      return;
    }
    let proposedDueDay;
    if (clientPaymentDueDayProposal !== '') {
      proposedDueDay = Number(clientPaymentDueDayProposal);
      if (!Number.isInteger(proposedDueDay) || proposedDueDay < 1 || proposedDueDay > 28) {
        setContractQuoteError('Alternate payment due day must be between 1 and 28.');
        return;
      }
    }
    setQuotationActionLoading(true);
    setContractQuoteError('');
    try {
      const response = await acceptContractQuotationTerms(activeQuotation.id, {
        acceptTerms: true,
        clientPaymentDueDay: proposedDueDay,
        acceptPaymentDueDate: true,
      });
      if (!response?.success || !response?.quotation) {
        setContractQuoteError(response?.message || 'Could not send quotation to owner');
        return;
      }
      setActiveQuotation(response.quotation);
      setContractQuote(mapQuotationToContractQuote(response.quotation));
      setShowQuotationResult(false);
    } catch (error) {
      setContractQuoteError(error?.message || 'Could not send quotation to owner');
    } finally {
      setQuotationActionLoading(false);
    }
  };

  const handleCancelQuotation = async () => {
    if (!activeQuotation?.id || activeQuotation.status !== 'quoted') return;
    if (!window.confirm('Cancel this quotation? This cannot be undone.')) return;
    setQuotationActionLoading(true);
    setContractQuoteError('');
    try {
      const response = await cancelContractQuotation(activeQuotation.id);
      if (!response?.success) {
        setContractQuoteError(response?.message || 'Could not cancel quotation');
        return;
      }
      setActiveQuotation(null);
      setHireMode('hourly');
      alert('Quotation cancelled');
    } catch (error) {
      setContractQuoteError(error?.message || 'Could not cancel quotation');
    } finally {
      setQuotationActionLoading(false);
    }
  };

  const handleAuthorizeEnachMandate = async () => {
    if (!activeQuotation?.id || activeQuotation.status !== 'client_esign' || mandateLoading) return;
    const maxAmount = Number(
      activeQuotation.contractTotal
      || contractQuote?.totalAmount
      || mandateInfo?.mandate_max_amount
      || 0,
    );
    if (!(maxAmount > 0)) {
      setMandateError('Contract total (mandate max amount) is missing on this quotation.');
      return;
    }
    setMandateLoading(true);
    setMandateError('');
    try {
      const result = await secureFetch('/api/client/mandates/create', {
        method: 'POST',
        body: JSON.stringify({
          quotationId: activeQuotation.id,
          maxAmount,
        }),
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Could not create e-NACH mandate');
      }
      const next = {
        mandate_id: result.mandate_id || null,
        mandate_status: result.mandate_status || 'pending',
        mandate_max_amount: result.mandate_max_amount != null
          ? Number(result.mandate_max_amount)
          : maxAmount,
        mandate_bank_name: result.mandate_bank_name || mandateInfo?.mandate_bank_name || null,
        authorization_url: result.authorization_url || null,
      };
      setMandateInfo(next);
      if (next.authorization_url && typeof window !== 'undefined') {
        window.open(next.authorization_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setMandateError(err?.message || 'Could not create e-NACH mandate');
    } finally {
      setMandateLoading(false);
    }
  };

  const handleConfirmQuotationArrival = async () => {
    if (!activeQuotation?.id || activeQuotation.status !== 'converted' || quotationArrivalLoading) {
      return;
    }
    setQuotationArrivalLoading(true);
    setContractQuoteError('');
    try {
      let photoUrl = null;
      if (quotationArrivalPhoto) {
        photoUrl = await readArrivalFileAsDataUrl(quotationArrivalPhoto);
      }
      const result = await confirmClientArrival(activeQuotation.id, { photoUrl });
      if (!result?.ok && !result?.success) {
        setContractQuoteError(result?.message || 'Could not confirm arrival');
        return;
      }
      setQuotationArrivalSuccess(true);
      if (result.quotation) {
        setActiveQuotation(result.quotation);
        setContractQuote(mapQuotationToContractQuote(result.quotation));
      }
    } catch (err) {
      setContractQuoteError(err?.message || 'Could not confirm arrival');
    } finally {
      setQuotationArrivalLoading(false);
    }
  };

  const handleSignAndPayAdvance = async () => {
    if (!activeQuotation?.id || activeQuotation.status !== 'client_esign') return;
    if (!esignTermsAccepted) {
      setContractQuoteError('Confirm you have read and agree to the contract hire terms.');
      return;
    }
    if (clientKycBookingBlocked) {
      setContractQuoteError(clientKycBookingBlockMessage);
      return;
    }
    const requiredAdvance = Number(activeQuotation.advanceRequired || 0);
    if (!(requiredAdvance > 0)) {
      setContractQuoteError('Advance amount is missing on this quotation.');
      return;
    }
    if (walletBalance == null || walletBalance < requiredAdvance) {
      openWalletRecharge(Math.max(1, requiredAdvance - (walletBalance ?? 0)));
      return;
    }
    setQuotationActionLoading(true);
    setContractQuoteError('');
    try {
      const idempotencyKey = `BK${Date.now()}`;
      const response = await clientEsignAndPayQuotation(activeQuotation.id, {
        acceptEsign: true,
        idempotencyKey,
      });
      if (!response?.success) {
        setContractQuoteError(response?.message || 'E-sign / payment failed');
        return;
      }
      persistSiteAddressAfterBooking();
      if (response.quotation) {
        setActiveQuotation(response.quotation);
        setContractQuote(mapQuotationToContractQuote(response.quotation));
      }
      const bookingRef = response.booking?.booking_ref
        || response.quotation?.bookingRef
        || idempotencyKey;
      setEsignPaySuccess({
        bookingRef,
        advance: requiredAdvance,
      });
      const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
      if (user?.id) {
        const bal = await getWalletBalance(user.id);
        setWalletBalance(bal);
      }
      const bookingsResult = await getMyBookingsPage({ limit: 100, offset: 0 });
      if (bookingsResult?.items) {
        setActiveBookings(bookingsResult.items.map(mergeBookingFeedbackFields));
        setBookingsHasMore(Boolean(bookingsResult.hasMore));
        setBookingsOffset(bookingsResult.nextOffset || 0);
      }
    } catch (error) {
      setContractQuoteError(error?.message || 'E-sign / payment failed');
    } finally {
      setQuotationActionLoading(false);
    }
  };

  const handleBook = async () => {
    if (!siteDetailsValid) {
      setSiteBookAttempted(true);
      setSiteFieldTouched({ work: true, address: true, pincode: true, gps: true });
      return;
    }

    if (hireMode === 'contract') {
      // Contract hire uses the quotation flow (no payment in Checkpoint 2).
      if (!activeQuotation || activeQuotation.status === 'quoted') {
        if (activeQuotation?.status === 'quoted') {
          await handleAcceptTermsAndSendQuotation();
        } else {
          await handleRequestQuotation();
        }
      }
      return;
    }

    if (hireMode === 'site_work') {
      if (!selectedMachine?.id || !selectedMachine?.owner_id) {
        setContractQuoteError('Selected machine has no owner linked');
        return;
      }
      if (!siteWorkType) {
        setContractQuoteError('Select a site work type');
        return;
      }
      if (!noWaterClauseAccepted) {
        setContractQuoteError('Accept the no-water / dry-bore clause to continue');
        return;
      }
      if (clientKycBookingBlocked) {
        setContractQuoteError(clientKycBookingBlockMessage);
        return;
      }

      const soft = Number(strataRateSoft);
      const murram = Number(strataRateMurram);
      const rock = Number(strataRateRock);
      const depth = Number(estimatedDepth);
      const lumpsum = Number(siteLumpsumAmount);
      let estimate = 0;
      if (siteBillingBasis === 'lumpsum') {
        if (!(lumpsum > 0)) {
          setContractQuoteError('Enter lump sum amount');
          return;
        }
        estimate = lumpsum;
      } else {
        if (!(depth > 0)) {
          setContractQuoteError('Enter estimated depth');
          return;
        }
        const avgRate = [soft, murram, rock].filter((n) => Number.isFinite(n) && n > 0);
        if (avgRate.length === 0) {
          setContractQuoteError('Enter at least one strata rate (soft / murram / rock)');
          return;
        }
        const mean = avgRate.reduce((a, b) => a + b, 0) / avgRate.length;
        estimate = depth * mean;
      }
      const advanceAmount = Math.max(1, Math.ceil(estimate * 0.25));

      setSiteWorkSubmitting(true);
      setContractQuoteError('');
      setSiteWorkSuccess('');
      try {
        const result = await secureFetch('/api/client/site-work/create', {
          method: 'POST',
          body: JSON.stringify({
            ownerId: selectedMachine.owner_id,
            machineId: selectedMachine.id,
            siteWorkType,
            billingBasis: siteBillingBasis,
            estimatedDepth: siteBillingBasis === 'lumpsum' ? null : depth,
            strataRates: {
              soft: Number.isFinite(soft) ? soft : null,
              murram: Number.isFinite(murram) ? murram : null,
              rock: Number.isFinite(rock) ? rock : null,
            },
            lumpsumAmount: siteBillingBasis === 'lumpsum' ? lumpsum : null,
            advanceAmount,
            noWaterClauseAccepted: true,
          }),
        });
        if (result?.success === false) {
          throw new Error(result.message || 'Failed to create site work request');
        }
        persistSiteAddressAfterBooking();
        setSiteWorkSuccess(
          `Site work request sent (draft). Advance due ≈ ₹${advanceAmount.toLocaleString('en-IN')} (25%). Owner will review.`,
        );
      } catch (err) {
        setContractQuoteError(err?.message || 'Failed to create site work request');
      } finally {
        setSiteWorkSubmitting(false);
      }
      return;
    }

    if (clientKycBookingBlocked) {
      setContractQuoteError(clientKycBookingBlockMessage);
      return;
    }

    if (attachmentMinHoursError) {
      setContractQuoteError(attachmentMinHoursError);
      return;
    }

    const existingBooking = activeBookings.find((b) =>
      b.machine_id === selectedMachine?.id
      && !['Completed', 'Cancelled', 'Disputed'].includes(b.status),
    );

    if (existingBooking) {
      const confirm = window.confirm(
        t('confirmDuplicateActiveBooking')
          .replace('{{ref}}', existingBooking.booking_ref),
      );
      if (!confirm) return;
    }

    const requiredAdvance = advanceAmt;
    if (walletBalance == null || walletBalance < requiredAdvance) {
      openWalletRecharge(Math.max(1, requiredAdvance - (walletBalance ?? 0)));
      return;
    }
    setPendingIdempotencyKey((prev) => prev || `BK${Date.now()}`);
    setBookingStep(3);
    setShowPayment(true);
  };

  const handleConfirmBooking = async () => {
    if (clientKycBookingBlocked) {
      closePaymentModal();
      setContractQuoteError(clientKycBookingBlockMessage);
      return;
    }

    if (attachmentMinHoursError) {
      closePaymentModal();
      setContractQuoteError(attachmentMinHoursError);
      return;
    }

    const idempotencyKey = pendingIdempotencyKey || `BK${Date.now()}`;
    if (!pendingIdempotencyKey) setPendingIdempotencyKey(idempotencyKey);
    if (bookingSubmitting) return;

    setBookingSubmitting(true);
    try {
      const minH = selectedMachine?.minimumHours || 3;
      if (hireMode === 'contract' && !contractQuote) {
        alert('Request a contract quote before confirming the booking.');
        return;
      }
      const spotQty = Number(effectiveQuantity);
      const bookedHours = hireMode === 'hourly' && bookingType === 'hourly'
        ? (Number.isFinite(spotQty) && spotQty > 0 ? spotQty : minH)
        : null;
      const ratePerHour = hireMode === 'hourly' && bookingType === 'hourly'
        ? Number(selectedMachine?.rates?.perHour || 0)
        : null;
      const result = await createBooking({
        idempotency_key: idempotencyKey,
        client_id: user.id,
        machine_id: selectedMachine.id,
        booking_type: hireMode,
        booking_unit: hireMode === 'hourly' ? bookingType : undefined,
        quantity: hireMode === 'contract' ? contractQuote.cycles : effectiveQuantity,
        booked_hours: bookedHours,
        rate_per_hour: ratePerHour,
        minimum_hours: selectedMachine?.minimumHours || 3,
        base_amount: totalCost,
        gst_amount: gstAmount,
        total_amount: grandTotal,
        advance_paid: advanceAmt,
        status: 'Active',
        start_date: hireMode === 'contract'
          ? startDate
          : new Date().toISOString().split('T')[0],
        end_date: hireMode === 'contract' ? endDate : null,
        billing_cycle: hireMode === 'contract' ? billingCycle : null,
        fixed_rate_per_cycle: hireMode === 'contract' ? Number(fixedRatePerCycle) : null,
        guaranteed_hours_per_cycle: hireMode === 'contract' ? Number(guaranteedHoursPerCycle) : null,
        attachment_rate_per_cycle: hireMode === 'contract' && selectedAttachment && attachmentRatePerCycle !== ''
          ? Number(attachmentRatePerCycle)
          : null,
        mobilization_transport_by: hireMode === 'contract' ? mobilizationTransportBy : null,
        mobilization_transport_amount: hireMode === 'contract'
          ? (mobilizationTransportAmount === '' ? null : Number(mobilizationTransportAmount))
          : null,
        demobilization_transport_by: hireMode === 'contract' ? demobilizationTransportBy : null,
        demobilization_transport_amount: hireMode === 'contract'
          ? (demobilizationTransportAmount === '' ? null : Number(demobilizationTransportAmount))
          : null,
        location: selectedMachine.location,
        ...buildSiteLocationPayload(),
        selected_attachment: selectedAttachment || null,
        attachment_usage_hours: hireMode !== 'contract' && selectedAttachment
          ? Number(attachmentUsageHours)
          : null,
        site_location: siteAddress.trim() || null,
        work_description: workDescription.trim() || null,
      });

      if (!result?.success) {
        if (result?.code === 'INSUFFICIENT_BALANCE') {
          const balance = Number(result?.data?.balance ?? walletBalance ?? 0);
          const required = Number(result?.data?.required ?? advanceAmt);
          const shortfall = Math.max(1, required - balance);
          closePaymentModal();
          openWalletRecharge(shortfall);
          setContractQuoteError(
            `Wallet balance is ₹${balance.toLocaleString('en-IN')}. Add ₹${shortfall.toLocaleString('en-IN')} to continue.`,
          );
          return;
        }
        if (result?.code === 'KYC_REJECTED' || result?.code === 'KYC_PENDING' || result?.code === 'KYC_REQUIRED') {
          closePaymentModal();
          setContractQuoteError(result?.message || clientKycBookingBlockMessage);
          return;
        }
        if (result?.code === 'DUPLICATE_ACTIVE_BOOKING') {
          closePaymentModal();
          const ref = result?.data?.existingBookingRef
            || result?.data?.existing_booking_ref
            || '—';
          alert(t('activeBookingWarning').replace('{{ref}}', ref));
          return;
        }
        alert(result?.message || t('bookingFailedGeneric'));
        return;
      }

      const bookingRef = result.data?.booking_ref
        || result.data?.booking?.booking_ref
        || idempotencyKey;
      const newBalance = Number(
        result.data?.new_balance
        ?? (await getWalletBalance(user.id))
      );
      const savedBooking = result.data?.booking || null;

      setWalletBalance(newBalance);
      setPendingIdempotencyKey(null);
      const nowIso = new Date().toISOString();
      const today = nowIso.split('T')[0];
      setActiveBookings(prev => [...prev, {
        ...(savedBooking || {}),
        id: savedBooking?.id || bookingRef,
        booking_ref: bookingRef,
        machine_id: selectedMachine.id,
        machine: selectedMachine.code || selectedMachine.id,
        machineFull: selectedMachine.name,
        operator: selectedMachine.operator,
        ratePerHour: selectedMachine.rates.perHour,
        booking_type: hireMode,
        booking_unit: hireMode === 'hourly' ? bookingType : 'hourly',
        type: hireMode === 'contract'
          ? 'Contract'
          : bookingType.charAt(0).toUpperCase() + bookingType.slice(1),
        startTime: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
        quantity: hireMode === 'contract' ? contractQuote.cycles : effectiveQuantity,
        hours: hireMode === 'contract' ? null : effectiveQuantity,
        base_amount: totalCost,
        gst_amount: gstAmount,
        total_amount: grandTotal,
        advance_paid: advanceAmt,
        advancePaid: advanceAmt,
        status: savedBooking?.status || 'Active',
        location: selectedMachine.location,
        fuel: selectedMachine.fuel,
        start_date: savedBooking?.start_date || (hireMode === 'contract' ? startDate : today),
        end_date: savedBooking?.end_date || (hireMode === 'contract' ? endDate : null),
        billing_cycle: savedBooking?.billing_cycle || (hireMode === 'contract' ? billingCycle : null),
        fixed_rate_per_cycle: savedBooking?.fixed_rate_per_cycle
          || (hireMode === 'contract' ? Number(fixedRatePerCycle) : null),
        guaranteed_hours_per_cycle: savedBooking?.guaranteed_hours_per_cycle
          || (hireMode === 'contract' ? Number(guaranteedHoursPerCycle) : null),
        attachment_rate_per_cycle: savedBooking?.attachment_rate_per_cycle
          ?? (hireMode === 'contract' && selectedAttachment && attachmentRatePerCycle !== ''
            ? Number(attachmentRatePerCycle)
            : null),
        mobilization_transport_by: savedBooking?.mobilization_transport_by
          || (hireMode === 'contract' ? mobilizationTransportBy : null),
        mobilization_transport_amount: savedBooking?.mobilization_transport_amount
          ?? (hireMode === 'contract'
            ? (mobilizationTransportAmount === '' ? null : Number(mobilizationTransportAmount))
            : null),
        demobilization_transport_by: savedBooking?.demobilization_transport_by
          || (hireMode === 'contract' ? demobilizationTransportBy : null),
        demobilization_transport_amount: savedBooking?.demobilization_transport_amount
          ?? (hireMode === 'contract'
            ? (demobilizationTransportAmount === '' ? null : Number(demobilizationTransportAmount))
            : null),
        created_at: savedBooking?.created_at || nowIso,
        date: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        machines: savedBooking?.machines || null,
      }]);
      persistSiteAddressAfterBooking();
      closePaymentModal();
      resetBookingFlow();
      setActiveTab('mybookings');
      Promise.allSettled([
        Promise.resolve(sendBookingConfirmation('contact@developmentexpress.in', {
          id: bookingRef,
          machine: selectedMachine.name,
          type: hireMode === 'contract' ? 'contract' : bookingType,
          advance: advanceAmt.toLocaleString('en-IN'),
        })),
        sendBookingNotifications({
          booking: {
            id: bookingRef,
            type: hireMode === 'contract' ? 'contract' : bookingType,
            location: selectedMachine.location,
            advance: advanceAmt,
            total: Math.round(totalCost * 1.18),
          },
          client: { name: user.name, userId: user.id },
          machine: { name: selectedMachine.name },
          operator: selectedMachine.operator_id ? { userId: selectedMachine.operator_id } : null,
          owner: selectedMachine.owner_id ? { userId: selectedMachine.owner_id } : null,
        }),
        sendWalletNotification({
          userId: user.id,
          name: user.name || 'Client',
          type: 'debit',
          amount: advanceAmt,
          balance: newBalance,
          ref: bookingRef,
        }),
      ]).catch(() => {});
    } finally {
      setBookingSubmitting(false);
    }
  };

  const CLIENT = clientUser;
const clientName = CLIENT.name || 'Client';

  const handleClientPhoneChanged = (newPhone) => {
    setClientUser((prev) => {
      const next = { ...prev, phone: newPhone };
      localStorage.setItem('developmentexpress_user', JSON.stringify(next));
      return next;
    });
  };

  const handleAcknowledgeTerms = async () => {
    setTermsPrompt((prev) => ({ ...prev, acknowledging: true, error: '' }));
    try {
      const payload = await acknowledgeTerms();
      if (!payload?.success) {
        throw new Error('Could not acknowledge terms right now.');
      }
      setClientUser((prev) => {
        const next = {
          ...prev,
          terms_accepted: true,
          terms_accepted_at: payload.user?.terms_accepted_at || new Date().toISOString(),
          terms_version: payload.user?.terms_version || termsPrompt.currentVersion,
        };
        localStorage.setItem('developmentexpress_user', JSON.stringify(next));
        return next;
      });
      setTermsPrompt((prev) => ({ ...prev, visible: false, acknowledging: false, error: '' }));
    } catch (err) {
      setTermsPrompt((prev) => ({
        ...prev,
        acknowledging: false,
        error: err?.message || 'Could not acknowledge terms right now.',
      }));
    }
  };

  const handleWalletCredited = async (newBalance, amt, paymentId) => {
    const balance = newBalance ?? (await getWalletBalance(CLIENT.id));
    setWalletBalance(balance);
    setShowRecharge(false);
    const payLine = paymentId ? `\nPayment ID: ${paymentId}` : '';
    alert(`${String.fromCodePoint(0x2705)} ${t('paymentSuccess')}\n${formatTranslation(t('walletCreditedAlert'), {
      amount: Number(amt).toLocaleString('en-IN'),
      payLine,
    })}`);
  };

  const refreshClientBookings = useCallback(async () => {
    const savedUser = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
    if (!savedUser.id) return;
    const result = await getMyBookingsPage({ limit: 100, offset: 0 });
    if (result.items) {
      setActiveBookings(result.items.map(mergeBookingFeedbackFields));
      setBookingsHasMore(Boolean(result.hasMore));
      setBookingsOffset(result.nextOffset || 0);
    }
    const bal = await getWalletBalance(savedUser.id);
    if (bal !== null) setWalletBalance(bal);
  }, []);

  const refreshPendingTerminations = useCallback(async (bookings) => {
    const contracts = (bookings || []).filter((b) => b.booking_type === 'contract' && b.id);
    if (contracts.length === 0) {
      setPendingTerminations({});
      return;
    }
    const entries = await Promise.all(contracts.map(async (b) => {
      try {
        const data = await secureFetch(`/api/client/terminations?bookingId=${encodeURIComponent(b.id)}`);
        return [b.id, data?.termination || null];
      } catch (_err) {
        return [b.id, null];
      }
    }));
    const map = {};
    entries.forEach(([id, term]) => {
      if (term) map[id] = term;
    });
    setPendingTerminations(map);
  }, []);

  useEffect(() => {
    refreshPendingTerminations(activeBookings);
  }, [activeBookings, refreshPendingTerminations]);

  const estimateTerminationFee = (booking, type) => {
    if (type === 'mutual' || type === 'force_majeure') {
      return { remaining: 0, feeCycles: 0, gross: 0, gst: 0, net: 0 };
    }
    const start = new Date(`${String(booking.start_date || '').slice(0, 10)}T00:00:00`);
    const end = new Date(`${String(booking.end_date || '').slice(0, 10)}T00:00:00`);
    let remaining = 0;
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end >= start) {
      const months = String(booking.billing_cycle || '').toLowerCase() === 'quarterly' ? 3 : 1;
      remaining = 1;
      const cursor = new Date(start);
      while (remaining < 120) {
        cursor.setMonth(cursor.getMonth() + months);
        if (cursor >= end) break;
        remaining += 1;
      }
    }
    const feeCycles = Math.min(remaining, 3);
    const rate = Number(booking.fixed_rate_per_cycle || 0);
    const gross = Math.round(feeCycles * rate * 0.25 * 100) / 100;
    const taxQuote = computeBookingTaxQuote(gross, {
      recipientGstin: booking.client_gstin,
      siteStateCode: booking.site_state_code,
    });
    const gst = taxQuote.gst;
    const net = Math.round((gross + gst) * 100) / 100;
    return { remaining, feeCycles, gross, gst, net, cgst: taxQuote.cgst, sgst: taxQuote.sgst, igst: taxQuote.igst, interState: taxQuote.interState };
  };

  const handleInitiateTermination = async () => {
    if (!terminationModal || terminationSubmitting) return;
    setTerminationSubmitting(true);
    setTerminationError('');
    try {
      const result = await secureFetch('/api/client/terminations/initiate', {
        method: 'POST',
        body: JSON.stringify({
          bookingId: terminationModal.id,
          terminationType,
        }),
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Termination request failed');
      }
      setPendingTerminations((prev) => ({
        ...prev,
        [terminationModal.id]: result.termination || { status: 'pending' },
      }));
      setTerminationModal(null);
    } catch (err) {
      setTerminationError(err?.message || 'Termination request failed');
    } finally {
      setTerminationSubmitting(false);
    }
  };

  const handleReportOwnerDefault = async () => {
    if (!ownerDefaultModal || ownerDefaultSubmitting) return;
    if (ownerDefaultType === 'breakdown') {
      const days = Number(ownerDefaultBreakdownDays);
      if (!Number.isFinite(days) || days < 0) {
        setOwnerDefaultError('Enter valid breakdown days');
        return;
      }
    }
    setOwnerDefaultSubmitting(true);
    setOwnerDefaultError('');
    setOwnerDefaultSuccess('');
    try {
      const result = await secureFetch(
        `/api/client/bookings/${ownerDefaultModal.id}/owner-default/report`,
        {
          method: 'POST',
          body: JSON.stringify({
            defaultType: ownerDefaultType,
            breakdownDays: ownerDefaultType === 'breakdown'
              ? Number(ownerDefaultBreakdownDays)
              : null,
          }),
        },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to report owner default');
      }
      setOwnerDefaultSuccess(result?.message || 'Default reported — admin will review');
      setTimeout(() => {
        setOwnerDefaultModal(null);
        setOwnerDefaultSuccess('');
        setOwnerDefaultType('no_show');
        setOwnerDefaultBreakdownDays('');
      }, 1200);
    } catch (err) {
      setOwnerDefaultError(err?.message || 'Failed to report owner default');
    } finally {
      setOwnerDefaultSubmitting(false);
    }
  };

  const hasLiveBooking = useMemo(
    () => activeBookings.some(bookingNeedsLiveSync),
    [activeBookings],
  );

  const trackingBooking = (activeBookings || []).find((b) =>
    ['Dispatched', 'Extension_Pending', 'Completing', 'Completing_PendingPayment'].includes(b.status),
  ) || null;

  useEffect(() => {
    if (!hasLiveBooking) return undefined;
    const id = setInterval(() => {
      refreshClientBookings();
    }, 5000);
    return () => clearInterval(id);
  }, [hasLiveBooking, refreshClientBookings]);

  const handleCancelRequest = (booking) => { setCancelBooking(booking); setShowCancelModal(true); };

  const loadMoreClientBookings = async () => {
    if (!bookingsHasMore || bookingsLoadingMore) return;
    setBookingsLoadingMore(true);
    const result = await getMyBookingsPage({ limit: 100, offset: bookingsOffset });
    if ((result.items || []).length > 0) {
      setActiveBookings((prev) => appendUniqueById(prev, result.items.map(mergeBookingFeedbackFields)));
      setBookingsOffset(result.nextOffset || bookingsOffset);
      setBookingsHasMore(Boolean(result.hasMore));
    } else {
      setBookingsHasMore(false);
    }
    setBookingsLoadingMore(false);
  };

  const loadMoreClientTransactions = async () => {
    if (!txHasMore || txLoadingMore) return;
    setTxLoadingMore(true);
    const result = await getMyTransactionsPage({ limit: 50, offset: txOffset });
    if ((result.items || []).length > 0) {
      const mapped = result.items.map((tx) => formatTransactionRow(tx, t));
      setTransactionRows((prev) => appendUniqueById(prev, mapped));
      setTxOffset(result.nextOffset || txOffset);
      setTxHasMore(Boolean(result.hasMore));
    } else {
      setTxHasMore(false);
    }
    setTxLoadingMore(false);
  };

  const handleConfirmCancel = async () => {
    if (!cancelBooking?.id || cancelSubmitting) return;
    setCancelSubmitting(true);
    try {
      const result = await cancelClientBooking(cancelBooking.id);
      const errText = String(result?.error || result?.message || '').toLowerCase();
      const alreadyCancelled = result?.duplicate
        || (result?.code === 'CANCEL_NOT_ALLOWED' && errText.includes('cancel'));
      if (!result?.success && !alreadyCancelled) {
        alert(result?.error || result?.message || t('cancellationFailed'));
        return;
      }
      const credited = Number(result.credited || 0);
      const penalty = Number(result.penalty || 0);
      if (Number.isFinite(result.clientNewBalance)) {
        setWalletBalance(result.clientNewBalance);
      } else if (credited > 0) {
        setWalletBalance((prev) => prev + credited);
      }
      setActiveBookings((prev) => prev.filter((b) => b.id !== cancelBooking.id));
      if (credited > 0) {
        setCancelledCredit({
          machine: getBookingMachineCode(cancelBooking, machineLookup),
          deducted: penalty,
          credited,
        });
      }
      setShowCancelModal(false);
      setCancelBooking(null);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const getQtyConfig = () => {
    switch (bookingType) {
      case 'hourly': return {
        min: selectedMachine?.minimumHours || 3,
        max: 24,
        unit: 'Hours',
      };
      case 'daily': return { min: 1, max: 30, unit: 'Days' };
      case 'weekly': return { min: 1, max: 12, unit: 'Weeks' };
      case 'monthly': return { min: 1, max: 12, unit: 'Months' };
      default: return { min: 1, max: 30, unit: 'Days' };
    }
  };
  useEffect(() => { if (!startDate || !endDate) return; const days = Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000)); if (bookingType === 'daily') setQuantity(days); else if (bookingType === 'weekly') setQuantity(Math.max(1, Math.ceil(days / 7))); else if (bookingType === 'monthly') setQuantity(Math.max(1, Math.ceil(days / 30))); }, [startDate, endDate, bookingType]);
  const qtyConfig = getQtyConfig();

  const mobileNavItems = NAV.map((n) => {
    let badge;
    if (n.id === 'account' && clientAlerts.length > 0) {
      badge = clientAlerts.length > 99 ? '99+' : clientAlerts.length;
    } else if (n.id === 'newbooking' && todayBookingsCount > 0) {
      badge = todayBookingsCount > 99 ? '99+' : todayBookingsCount;
    }
    return {
      ...n,
      label: n.i18nKey && t(n.i18nKey) !== n.i18nKey ? t(n.i18nKey) : n.label,
      ...(badge ? { badge } : {}),
    };
  });
  const tabTitleItems = [
    ...mobileNavItems,
    ...OFF_NAV_TABS.map((n) => ({
      ...n,
      label: n.i18nKey && t(n.i18nKey) !== n.i18nKey ? t(n.i18nKey) : n.label,
    })),
  ];

  const renderNavBadge = (badge) => {
    if (!badge) return null;
    return (
      <span
        style={{
          marginLeft: 'auto',
          minWidth: '18px',
          height: '18px',
          padding: '0 5px',
          borderRadius: '999px',
          background: '#e94560',
          color: '#fff',
          fontSize: '10px',
          fontWeight: 800,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        {badge}
      </span>
    );
  };

  return (
    <div
      className={useMobileNav ? 'portal-dashboard-shell--phone' : ''}
      style={useMobileNav ? s.containerMobile : s.container}
    >
      {useMobileNav && (
        <MobileNav
          navItems={mobileNavItems}
          activeTab={activeTab}
          setActiveTab={handleClientNavTab}
          title={t('appName')}
          subtitle={t('clientPortal')}
          drawerMode="full"
          drawerSectionTitle={t('drawerMenu')}
          hideBottomNav
          menuOpen={mobileNavMenuOpen}
          onMenuOpenChange={setMobileNavMenuOpen}
          topContent={
            <div style={{ background: 'rgba(201,168,76,0.08)', borderRadius: '8px', padding: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <p style={{ color: '#8896a8', fontSize: '9px', margin: 0, letterSpacing: '1px' }}>{t('walletBalance').toUpperCase()}</p>
                <button
                  type="button"
                  onClick={() => setWalletVisible((v) => !v)}
                  aria-label={walletVisible ? 'Hide wallet balance' : 'Show wallet balance'}
                  aria-pressed={walletVisible}
                  style={{ background: 'transparent', border: 'none', color: '#8896a8', cursor: 'pointer', padding: '2px 4px', fontSize: '12px', lineHeight: 1 }}
                >
                  👁
                </button>
              </div>
              <p style={{ color: '#8896a8', fontSize: '12px', fontWeight: 600, margin: '6px 0', letterSpacing: '0.2px', fontVariantNumeric: 'tabular-nums' }}>
                {walletBalance === null ? '₹...' : walletVisible ? `₹${walletBalance.toLocaleString('en-IN')}` : '₹ ••••••'}
              </p>
              <button style={{ background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', width: '100%' }} onClick={() => openWalletRecharge()}>{t('rechargePlus')}</button>
            </div>
          }
          bottomContent={
            <button style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.3)', background: 'rgba(233,69,96,0.08)', color: '#e94560', cursor: 'pointer', fontSize: '13px', width: '100%' }} onClick={handleLogout}>{t('logout')}</button>
          }
        />
      )}

      {!useMobileNav && sidebarOverlay && (
        <>
          {sidebarOpen && (
            <div
              onClick={() => setSidebarOpen(false)}
              style={{
                position: 'fixed', inset: 0,
                zIndex: 30,
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(1px)',
              }}
            />
          )}
          <div style={{
            ...s.sidebar,
            position: 'fixed',
            left: 0, top: 0,
            zIndex: 40,
            height: '100svh',
            maxHeight: '100svh',
            transform: sidebarOpen
              ? 'translateX(0)'
              : 'translateX(-100%)',
            transition: 'transform 300ms ease',
            pointerEvents: sidebarOpen ? 'auto' : 'none',
          }}>
            <div style={s.sidebarHeader}>
              <div style={s.sidebarHeaderBrand}>
                <div style={s.logoCircle}>DE</div>
                <div>
                  <p style={s.logoTitle}>{t('appName')}</p>
                  <p style={s.logoSub}>{t('clientPortal')}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                style={s.sidebarToggleBtn}
                aria-label="Close sidebar"
              >
                <PanelLeftClose size={16} />
              </button>
            </div>
            <div style={s.divider} />
            <div style={s.sidebarNavScroll}>
              {mobileNavItems.map(item => (
                <button key={item.id} style={activeTab === item.id ? s.navActive : s.nav} onClick={() => { handleClientNavTab(item.id); if (sidebarOverlay) setSidebarOpen(false); }}>
                  <span>{item.icon}</span><span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>{renderNavBadge(item.badge)}
                </button>
              ))}
            </div>
            <div style={s.divider} />
            <div style={s.walletBox}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <p style={{ ...s.walletLabel, margin: 0 }}>{t('walletBalance').toUpperCase()}</p>
                <button
                  type="button"
                  onClick={() => setWalletVisible((v) => !v)}
                  aria-label={walletVisible ? 'Hide wallet balance' : 'Show wallet balance'}
                  aria-pressed={walletVisible}
                  style={{ background: 'transparent', border: 'none', color: '#8896a8', cursor: 'pointer', padding: '2px 4px', fontSize: '12px', lineHeight: 1, flexShrink: 0 }}
                >
                  👁
                </button>
              </div>
              <p style={{ color: '#8896a8', fontSize: '12px', fontWeight: 600, margin: '4px 0', letterSpacing: '0.2px', fontVariantNumeric: 'tabular-nums' }}>
                {walletBalance === null ? '₹...' : walletVisible ? `₹${walletBalance.toLocaleString('en-IN')}` : '₹ ••••••'}
              </p>
              <button style={s.rechargeBtn} onClick={() => openWalletRecharge()}>{t('rechargeWallet')}</button>
            </div>
            <div style={s.warningBox} onClick={() => setShowBlacklistWarning(true)}>
              <p style={s.warningText}>{t('directContactBlacklist')}</p>
            </div>
            <button style={s.logoutBtn} onClick={handleLogout}>{t('logout')}</button>
            <p style={s.sidebarFooter}>{t('sinceExcellence')}</p>
          </div>
        </>
      )}

      {!useMobileNav && !sidebarOpen && (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          style={s.sidebarOpenFab}
          aria-label="Open sidebar"
        >
          <PanelLeft size={16} />
        </button>
      )}

      {!useMobileNav && !sidebarOverlay && sidebarOpen && (
        <div style={s.sidebar}>
          <div style={s.sidebarHeader}>
            <div style={s.sidebarHeaderBrand}>
              <div style={s.logoCircle}>DE</div>
              <div>
                <p style={s.logoTitle}>{t('appName')}</p>
                <p style={s.logoSub}>{t('clientPortal')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              style={s.sidebarToggleBtn}
              aria-label="Close sidebar"
            >
              <PanelLeftClose size={16} />
            </button>
          </div>
          <div style={s.divider} />
          <div style={s.sidebarNavScroll}>
            {mobileNavItems.map(item => (
              <button key={item.id} style={activeTab === item.id ? s.navActive : s.nav} onClick={() => { handleClientNavTab(item.id); if (sidebarOverlay) setSidebarOpen(false); }}>
                <span>{item.icon}</span><span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>{renderNavBadge(item.badge)}
              </button>
            ))}
          </div>
          <div style={s.divider} />
          <div style={s.walletBox}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <p style={{ ...s.walletLabel, margin: 0 }}>{t('walletBalance').toUpperCase()}</p>
              <button
                type="button"
                onClick={() => setWalletVisible((v) => !v)}
                aria-label={walletVisible ? 'Hide wallet balance' : 'Show wallet balance'}
                aria-pressed={walletVisible}
                style={{ background: 'transparent', border: 'none', color: '#8896a8', cursor: 'pointer', padding: '2px 4px', fontSize: '12px', lineHeight: 1, flexShrink: 0 }}
              >
                👁
              </button>
            </div>
            <p style={{ color: '#8896a8', fontSize: '12px', fontWeight: 600, margin: '4px 0', letterSpacing: '0.2px', fontVariantNumeric: 'tabular-nums' }}>
              {walletBalance === null ? '₹...' : walletVisible ? `₹${walletBalance.toLocaleString('en-IN')}` : '₹ ••••••'}
            </p>
            <button style={s.rechargeBtn} onClick={() => openWalletRecharge()}>{t('rechargeWallet')}</button>
          </div>
          <div style={s.warningBox} onClick={() => setShowBlacklistWarning(true)}>
            <p style={s.warningText}>{t('directContactBlacklist')}</p>
          </div>
          <button style={s.logoutBtn} onClick={handleLogout}>{t('logout')}</button>
          <p style={s.sidebarFooter}>{t('sinceExcellence')}</p>
        </div>
      )}

      <div className={useMobileNav ? 'portal-dashboard-main-wrap--phone' : 'portal-dashboard-scroll-shell'} style={useMobileNav ? undefined : s.scrollShell}>
      <div
        className={`client-dashboard-main min-w-0 ${useMobileNav ? 'portal-dashboard-main--phone' : 'portal-dashboard-main--locked'}`}
        style={{
          ...(useMobileNav ? s.mainMobile : s.main),
          ...(useMobileNav
            ? { padding: `calc(70px + env(safe-area-inset-top, 0px)) ${isTablet ? '16px' : '10px'} calc(16px + env(safe-area-inset-bottom, 0px))` }
            : isMobile
              ? { padding: '20px 16px' }
              : {
                  padding: sidebarOpen ? '25px' : '25px 25px 25px 60px',
                  paddingTop: sidebarOverlay ? 'calc(20px + env(safe-area-inset-top, 0px))' : '25px',
                }),
        }}
      >
        <div style={{ ...s.header, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '8px' : '0', alignItems: isMobile ? 'flex-start' : 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ minWidth: 0, maxWidth: '100%' }}>
              <h2 style={{ ...s.pageTitle, fontSize: isMobile ? '16px' : '20px' }}>
                {tabTitleItems.find(n => n.id === activeTab)?.icon}{' '}
                {tabTitleItems.find(n => n.id === activeTab)?.label}
              </h2>
              {!useMobileNav && (
              <p style={s.pageDate}>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '100%' }}>
            {!useMobileNav && <LanguageSelector compact={true} />}
            {!useMobileNav && (
              <div style={s.clientBadge}>
                <PortalAvatar userId={clientUser?.id} name={clientDisplayName} photoUrl={clientPhoto} size={32} />
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  {clientDisplayName}
                  <span style={{
                    marginTop: '3px',
                    alignSelf: 'flex-start',
                    background: 'rgba(201,168,76,0.08)',
                    border: '1px solid rgba(201,168,76,0.22)',
                    borderRadius: '8px',
                    color: '#8896a8',
                    fontSize: '10px',
                    fontWeight: 600,
                    padding: '2px 8px',
                  }}>
                    {formatClientType(clientUser?.client_type || user?.client_type)}
                  </span>
                </div>
                <AccountStatusPill status={user.status} />
              </div>
            )}
          </div>
        </div>
        {termsPrompt.visible && (
          <div style={{ marginBottom: '12px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', borderRadius: '10px', padding: '10px 12px' }}>
            <p style={{ color: '#c9a84c', fontSize: '12px', margin: '0 0 6px', fontWeight: 700 }}>
              {t('tacBannerTitle')}
            </p>
            <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 8px', lineHeight: 1.45 }}>
              Please review the latest platform Terms & Conditions ({termsPrompt.currentVersion}) and acknowledge once.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => { sessionStorage.setItem('returnTab', activeTab); navigate('/terms'); }}
                style={{ textDecoration: 'none', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.45)', color: '#c9a84c', borderRadius: '8px', padding: '7px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
              >
                {t('tacBannerOpen')}
              </button>
              <button
                type="button"
                onClick={handleAcknowledgeTerms}
                disabled={termsPrompt.acknowledging}
                style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.5)', color: '#4CAF50', borderRadius: '8px', padding: '7px 12px', fontSize: '11px', fontWeight: 700, cursor: termsPrompt.acknowledging ? 'wait' : 'pointer' }}
              >
                {termsPrompt.acknowledging ? 'Saving...' : t('tacBannerAgree')}
              </button>
              <button
                type="button"
                onClick={() => setTermsPrompt((prev) => ({ ...prev, visible: false }))}
                style={{ background: 'transparent', border: '1px solid rgba(136,150,168,0.45)', color: '#8896a8', borderRadius: '8px', padding: '7px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
              >
                {t('tacBannerLater')}
              </button>
            </div>
            {termsPrompt.error ? (
              <p style={{ color: '#e94560', fontSize: '11px', margin: '8px 0 0' }}>{termsPrompt.error}</p>
            ) : null}
          </div>
        )}

        {activeTab === 'dashboard' && (() => {
          const ongoingBookings = activeBookings.filter((b) =>
            ['Active', 'Dispatched', 'Extension_Pending', 'Completing', 'Completing_PendingPayment'].includes(b.status)
            && !isBookingCancelled(b),
          );
          const heroBooking = ongoingBookings[0] || null;
          const recentBookings = (activeBookings || []).slice(0, 3);
          const hour = new Date().getHours();
          const greetKey = hour < 12 ? 'goodMorning' : hour < 17 ? 'goodAfternoon' : 'goodEvening';
          const dashTitle = {
            ...s.sectionTitle,
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            paddingLeft: 8,
            color: '#C9A84C',
          };
          const touchBtn = {
            minHeight: 44,
            minWidth: 44,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 13,
            borderRadius: 8,
            padding: '11px 16px',
          };
          const statusBadge = (status) => {
            if (status === 'Active') return { background: 'rgba(201,168,76,0.15)', border: '1px solid #C9A84C', color: '#C9A84C' };
            if (status === 'Dispatched') return { background: 'rgba(76,175,80,0.15)', border: '1px solid #4CAF50', color: '#4CAF50' };
            if (status === 'Extension_Pending') return { background: 'rgba(255,152,0,0.15)', border: '1px solid #FF9800', color: '#FF9800' };
            if (status === 'Completing' || status === 'Completing_PendingPayment') return { background: 'rgba(33,150,243,0.15)', border: '1px solid #2196F3', color: '#2196F3' };
            if (status === 'Disputed') return { background: 'rgba(233,69,96,0.15)', border: '1px solid #e94560', color: '#e94560' };
            return { background: 'rgba(136,150,168,0.15)', border: '1px solid #8896a8', color: '#8896a8' };
          };
          const formatAmount = (b) => {
            const amt = Number(b.total_amount ?? b.total ?? 0);
            return `₹${Number.isFinite(amt) ? amt.toLocaleString('en-IN') : '0'}`;
          };
          const navyCard = {
            background: 'linear-gradient(135deg, #0f2040, #030810)',
            border: '1px solid rgba(201,168,76,0.22)',
            borderRadius: 12,
            padding: '16px',
            minWidth: 0,
          };
          return (
          <div className="client-tab-panel w-full min-w-0 overflow-x-hidden">
            <div style={{ ...navyCard, marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <p style={{ color: '#8896a8', fontSize: 12, margin: 0, fontWeight: 500, minWidth: 0, flex: '1 1 180px' }}>
                  {t(greetKey)},{' '}
                  <span style={{ color: '#C9A84C', fontWeight: 700, fontSize: isMobile ? 16 : 18, wordBreak: 'break-word' }}>{clientDisplayName}</span>
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{ color: walletVisible ? '#C9A84C' : '#8896a8', fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}>
                    {walletBalance === null ? '₹...' : walletVisible ? `₹${walletBalance.toLocaleString('en-IN')}` : '₹ ••••••'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setWalletVisible((v) => !v)}
                    aria-label={walletVisible ? 'Hide wallet balance' : 'Show wallet balance'}
                    aria-pressed={walletVisible}
                    style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.28)', color: '#C9A84C', cursor: 'pointer', padding: '8px 10px', fontSize: 14, lineHeight: 1, borderRadius: 8, minHeight: 44, minWidth: 44 }}
                  >
                    👁
                  </button>
                </div>
              </div>
            </div>

            {heroBooking ? (
              <div style={{
                ...navyCard,
                marginBottom: 14,
                borderLeft: '3px solid #C9A84C',
                boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <div style={{ minWidth: 0, flex: '1 1 160px' }}>
                    <p style={{ color: '#e8e0d0', fontWeight: 700, fontSize: 14, margin: 0, wordBreak: 'break-word' }}>
                      {getBookingMachineLabel(heroBooking, machineLookup)}
                    </p>
                    <p style={{ color: '#8896a8', fontSize: 10, margin: '2px 0 0' }}>{heroBooking.booking_ref || heroBooking.id}</p>
                  </div>
                  <span style={{ padding: '6px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, flexShrink: 0, ...statusBadge(heroBooking.status) }}>
                    {heroBooking.status || '—'}
                  </span>
                </div>
                <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 12px' }}>
                  {t('duration')}:{' '}
                  <span style={{ color: '#C9A84C', fontWeight: 700 }}>{formatHoursHm(Number(heroBooking.booked_hours ?? heroBooking.quantity ?? 0))}</span>
                </p>
                <ClientOperatorStatusBadge booking={heroBooking} />
                <button
                  type="button"
                  onClick={() => handleClientNavTab('mybookings')}
                  style={{
                    ...touchBtn,
                    width: '100%',
                    background: 'transparent',
                    border: '1px solid rgba(201,168,76,0.45)',
                    color: '#C9A84C',
                  }}
                >
                  View Details →
                </button>
              </div>
            ) : null}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 18 }}>
              <button
                type="button"
                onClick={() => handleClientNavTab('mybookings')}
                style={{
                  ...touchBtn,
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid rgba(201,168,76,0.45)',
                  color: '#C9A84C',
                  gap: 8,
                }}
              >
                📋 {t('myBookings')}
              </button>
            </div>

            {recentBookings.length > 0 ? (
              <div style={{ ...navyCard, marginBottom: 18 }}>
                <h3 style={{ ...dashTitle, paddingLeft: 0, margin: '0 0 12px' }}>{t('recentActivity')}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {recentBookings.map((b) => (
                    <div
                      key={b.id || b.booking_ref}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 8,
                        flexWrap: 'nowrap',
                        background: 'rgba(0,0,0,0.28)',
                        border: '1px solid rgba(201,168,76,0.12)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        minWidth: 0,
                      }}
                    >
                      <p style={{ margin: 0, minWidth: 0, flex: '1 1 0', wordBreak: 'break-word' }}>
                        <span style={{ color: '#8896a8', fontSize: 10, fontWeight: 500, display: 'block' }}>
                          {b.booking_ref || b.id || '—'}
                        </span>
                        <span style={{ color: '#e8e0d0', fontSize: 12, fontWeight: 600 }}>
                          {getBookingMachineLabel(b, machineLookup)}
                        </span>
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0, flexWrap: 'nowrap' }}>
                        <span style={{ padding: '4px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, ...statusBadge(b.status) }}>
                          {b.status || '—'}
                        </span>
                        <span style={{ color: '#C9A84C', fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {formatAmount(b)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          );
        })()}

        {activeTab === 'newbooking' && (
          <div className="client-tab-panel w-full min-w-0 overflow-x-hidden">
            {renderClientOnboardingChecklist()}
            {!pushEnabled && !pushBannerDismissed && onboardingChecklist.complete ? (
              <div
                style={{
                  background: 'rgba(255,152,0,0.12)',
                  border: '1px solid rgba(255,152,0,0.35)',
                  color: '#FF9800',
                  borderRadius: '10px',
                  padding: '10px 12px',
                  marginBottom: '18px',
                  fontSize: isMobile ? '11px' : '12px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                <p style={{ margin: 0, flex: '1 1 200px' }}>
                  {String.fromCodePoint(0x1F514)} Enable push notifications for instant booking updates
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setActiveTab('alerts')}
                    style={{
                      background: 'transparent',
                      border: '1px solid #FF9800',
                      color: '#FF9800',
                      borderRadius: '6px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: '700',
                      cursor: 'pointer',
                    }}
                  >
                    Enable
                  </button>
                  <button
                    type="button"
                    onClick={() => setPushBannerDismissed(true)}
                    aria-label="Dismiss"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#FF9800',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      padding: '4px 6px',
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : null}
            <BookingStepper
              currentStep={showPayment ? 3 : bookingStep}
              isMobile={isMobile}
              t={t}
              canGoToStep2={Boolean(selectedMachine)}
              canGoToStep3={Boolean(selectedMachine && walletBalance != null && walletBalance >= advanceAmt)}
              onStepChange={(step) => {
                if (step === 1) {
                  setBookingStep(1);
                  setShowPayment(false);
                } else if (step === 2 && selectedMachine) {
                  setBookingStep(2);
                  setShowPayment(false);
                } else if (step === 3 && selectedMachine) {
                  handleBook();
                }
              }}
            />

            {bookingStep === 1 && (
              <>
            {renderClientKycBanner()}
            {quotationRejectedMessage ? (
              <div style={{ margin: '0 0 14px', padding: '12px 14px', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.4)', background: 'rgba(233,69,96,0.1)' }}>
                <p style={{ margin: '0 0 8px', color: '#e94560', fontSize: '13px', fontWeight: 700 }}>
                  {quotationRejectedMessage}
                </p>
                <button
                  type="button"
                  onClick={() => setQuotationRejectedMessage('')}
                  style={{ background: 'transparent', border: '1px solid rgba(233,69,96,0.45)', color: '#e94560', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Dismiss
                </button>
              </div>
            ) : null}
            <p style={{ margin: '0 0 14px', color: 'rgba(201,168,76,0.75)', fontSize: isMobile ? '10px' : '11px', lineHeight: 1.45 }}>
              <strong style={{ color: '#c9a84c' }}>{t('walletOnlyPolicyTitle')}:</strong> {t('walletOnlyPolicyDesc')}
            </p>
            <div style={{ ...s.cardRow, gridTemplateColumns: gridCols(2, 3, 4) }}>
              {[
                { icon: '🚜', val: displayMachines.filter(m => m.available).length, label: t('availableNow') },
                { icon: '💳', val: walletBalance === null ? '...' : '₹' + walletBalance.toLocaleString('en-IN'), label: t('walletBalance') },
                { icon: '📋', val: todayBookingsCount, label: t('todayLabel') },
                { icon: '⭐', val: '4.78', label: t('avgRating') },
              ].map((c, i) => (
                <div key={i} style={s.card}>
                  <p style={s.cardIcon}>{c.icon}</p>
                  <h3 style={s.cardVal}>{c.val}</h3>
                  <p style={s.cardLbl}>{c.label}</p>
                </div>
              ))}
            </div>
            {hireMode !== 'contract' ? (
              <div
                style={{
                  margin: '0 0 18px',
                  padding: isMobile ? '14px' : '18px',
                  background: '#030810',
                  border: '1px solid rgba(201,168,76,0.28)',
                  borderRadius: '12px',
                }}
              >
                <h3 style={{ ...s.sectionTitle, margin: '0 0 14px' }}>What machine do you need?</h3>
                <div className="grid w-full min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
                  <label style={{ display: 'block', color: '#8896a8', fontSize: '11px' }}>
                    Machine type
                    <select
                      value={jobType}
                      onChange={(e) => setJobType(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        marginTop: '6px',
                        background: '#030810',
                        color: '#e8e0d0',
                        border: '1px solid rgba(201,168,76,0.28)',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        fontSize: '13px',
                        outline: 'none',
                      }}
                    >
                      <option value="">
                        {t('selectMachineType') === 'selectMachineType' ? 'Select machine type' : t('selectMachineType')}
                      </option>
                      {availableMachineTypes.map((type) => (
                        <option
                          key={type}
                          value={type}
                          style={{ background: '#0a1628', color: '#e8e0d0' }}
                        >
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div style={{ display: 'block', color: '#8896a8', fontSize: '11px' }}>
                    Duration
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '6px',
                    }}
                    >
                      <input
                        type="number"
                        min={0}
                        max={23}
                        step={1}
                        value={jobHoursHH}
                        onChange={(e) => setJobHoursHH(e.target.value)}
                        placeholder="HH"
                        aria-label="Duration hours"
                        style={{
                          flex: 1,
                          minWidth: 0,
                          boxSizing: 'border-box',
                          background: '#030810',
                          color: '#e8e0d0',
                          border: '1px solid rgba(201,168,76,0.28)',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          fontSize: '13px',
                          outline: 'none',
                        }}
                      />
                      <span style={{ color: '#c9a84c', fontSize: '14px', fontWeight: 700 }}>:</span>
                      <select
                        value={jobMM}
                        onChange={(e) => setJobMM(e.target.value)}
                        aria-label="Duration minutes"
                        style={{
                          flex: 1,
                          minWidth: 0,
                          boxSizing: 'border-box',
                          background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                          color: '#e8e0d0',
                          border: '1px solid rgba(201,168,76,0.3)',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        {['00', '15', '30', '45'].map((mm) => (
                          <option key={mm} value={mm} style={{ background: '#0a1628', color: '#e8e0d0' }}>
                            {mm}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <label style={{ display: 'block', color: '#8896a8', fontSize: '11px' }}>
                    Date
                    <input
                      type="date"
                      min={toDateInputValue(new Date())}
                      value={jobDate}
                      onChange={(e) => setJobDate(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        marginTop: '6px',
                        background: '#030810',
                        color: '#e8e0d0',
                        border: '1px solid rgba(201,168,76,0.28)',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        fontSize: '13px',
                        outline: 'none',
                        colorScheme: 'dark',
                      }}
                    />
                  </label>
                  <label style={{ display: 'block', color: '#8896a8', fontSize: '11px' }}>
                    Site pincode
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: '6px' }}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={jobPin}
                        onChange={(e) => setJobPin(String(e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit pin"
                        maxLength={6}
                        style={{
                          flex: '1 1 140px',
                          minWidth: 140,
                          boxSizing: 'border-box',
                          background: '#030810',
                          color: '#e8e0d0',
                          border: '1px solid rgba(201,168,76,0.28)',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          fontSize: '13px',
                          outline: 'none',
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleDetectLocation}
                        disabled={gpsLoading}
                        style={{
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid rgba(201,168,76,0.4)',
                          background: gpsLoading
                            ? 'rgba(201,168,76,0.05)'
                            : 'rgba(201,168,76,0.12)',
                          color: '#C9A84C',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: gpsLoading ? 'not-allowed' : 'pointer',
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          minHeight: 44,
                        }}
                      >
                        {gpsLoading ? '⏳' : '📍'} {gpsLoading
                          ? (t('detecting') === 'detecting' ? 'Detecting...' : t('detecting'))
                          : (t('useMyLocation') === 'useMyLocation' ? 'Use my location' : t('useMyLocation'))}
                      </button>
                    </div>
                  </label>
                </div>
                {jobFindError ? (
                  <p style={{ color: '#e94560', fontSize: '12px', margin: '10px 0 0', lineHeight: 1.4 }}>
                    {jobFindError}
                  </p>
                ) : null}
                <button
                  type="button"
                  disabled={jobFindLoading}
                  onClick={handleFindMachines}
                  style={{
                    width: '100%',
                    marginTop: '14px',
                    minHeight: '48px',
                    borderRadius: '10px',
                    border: 'none',
                    background: jobFindLoading ? 'rgba(201,168,76,0.25)' : '#C9A84C',
                    color: jobFindLoading ? '#8896a8' : '#030810',
                    fontWeight: 700,
                    fontSize: isMobile ? '13px' : '14px',
                    cursor: jobFindLoading ? 'wait' : 'pointer',
                  }}
                >
                  {jobFindLoading ? 'Finding…' : 'Find Machines →'}
                </button>
              </div>
            ) : null}
            <h3 style={s.sectionTitle}>{t('availableMachinery')}</h3>
            <div style={{ marginBottom: '12px' }}>
              <input
                type="search"
                value={machineSearch}
                onChange={(e) => setMachineSearch(e.target.value)}
                placeholder="Search by name or ID…"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: '#030810',
                  color: '#e8e0d0',
                  border: '1px solid rgba(201,168,76,0.28)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  fontSize: '13px',
                  marginBottom: '10px',
                  outline: 'none',
                }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                {[
                  { id: 'all', label: 'All' },
                  { id: 'available', label: 'Available' },
                  { id: 'on_job', label: 'On Job' },
                ].map((chip) => {
                  const active = machineAvailFilter === chip.id;
                  return (
                    <button
                      key={chip.id}
                      type="button"
                      onClick={() => setMachineAvailFilter(chip.id)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: active ? '1px solid #C9A84C' : '1px solid rgba(201,168,76,0.22)',
                        background: active ? 'rgba(201,168,76,0.15)' : 'transparent',
                        color: active ? '#C9A84C' : '#8896a8',
                      }}
                    >
                      {chip.label}
                    </button>
                  );
                })}
                <span style={{ marginLeft: 'auto', color: '#8896a8', fontSize: '11px' }}>
                  {`Showing ${filteredMachines.length} machine${filteredMachines.length === 1 ? '' : 's'}`}
                </span>
              </div>
            </div>
            {searchResults !== null && searchResults.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: '#8896a8',
                fontSize: 13,
              }}>
                {t('noMachinesFound') === 'noMachinesFound'
                  ? 'No machines available for your search. Try adjusting the date, hours, or location.'
                  : t('noMachinesFound')}
              </div>
            ) : searchResults !== null ? (
              <>
                <p style={{
                  margin: '0 0 10px',
                  color: '#8896a8',
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
                >
                  Top matches for your job
                </p>
                <div className="grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
                  {filteredMachines.slice(0, 3).map((m, i) => (
                    <MachineListingCard
                      key={m.id || m.code || i}
                      m={m}
                      s={s}
                      t={t}
                      clientKycBookingBlocked={clientKycBookingBlocked}
                      clientKycBookingBlockMessage={clientKycBookingBlockMessage}
                      selectMachineForBooking={selectMachineForBooking}
                      showType
                    />
                  ))}
                </div>
                {filteredMachines.length > 3 ? (
                  <>
                    <p style={{
                      margin: '20px 0 10px',
                      color: '#8896a8',
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                    >
                      More machines nearby
                    </p>
                    <div className="grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
                      {filteredMachines.slice(3).map((m, i) => (
                        <MachineListingCard
                          key={m.id || m.code || `more-${i}`}
                          m={m}
                          s={s}
                          t={t}
                          clientKycBookingBlocked={clientKycBookingBlocked}
                          clientKycBookingBlockMessage={clientKycBookingBlockMessage}
                          selectMachineForBooking={selectMachineForBooking}
                          showType
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </>
            ) : showMachineTypeHeaders ? (
              machineTypeGroups.map((group) => (
                <div key={group.type} className="w-full min-w-0" style={{ marginBottom: '16px' }}>
                  <div
                    style={{
                      borderLeft: '3px solid #C9A84C',
                      paddingLeft: '10px',
                      marginBottom: '10px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '10px' }}>
                      <span style={{ color: '#C9A84C', fontWeight: 700, fontSize: '13px' }}>{group.type}</span>
                      <span style={{ color: '#8896a8', fontSize: '11px' }}>
                        {`${group.availableCount} ${t('machineGroupAvailable')} · ${group.busyCount} ${t('machineGroupBusy')}`}
                      </span>
                    </div>
                    <div style={{ height: '1px', background: 'rgba(201,168,76,0.22)', marginTop: '8px' }} />
                  </div>
                  <div className="grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
                    {group.machines.map((m, i) => (
                      <MachineListingCard
                        key={m.id || m.code || `${group.type}-${i}`}
                        m={m}
                        s={s}
                        t={t}
                        clientKycBookingBlocked={clientKycBookingBlocked}
                        clientKycBookingBlockMessage={clientKycBookingBlockMessage}
                        selectMachineForBooking={selectMachineForBooking}
                        showType={false}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
                {(machineTypeGroups[0]?.machines || []).map((m, i) => (
                  <MachineListingCard
                    key={m.id || m.code || i}
                    m={m}
                    s={s}
                    t={t}
                    clientKycBookingBlocked={clientKycBookingBlocked}
                    clientKycBookingBlockMessage={clientKycBookingBlockMessage}
                    selectMachineForBooking={selectMachineForBooking}
                    showType
                  />
                ))}
              </div>
            )}
              </>
            )}

            {bookingStep === 2 && selectedMachine && (
              <div className="grid w-full min-w-0 grid-cols-1 gap-5 md:grid-cols-[1.2fr_1fr]">
                <div className="box-border w-full min-w-0" style={{ ...s.calcCard, padding: isMobile ? '12px' : '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                    <h3 style={{ ...s.calcTitle, margin: 0 }}>{t('bookingCostCalculator')}</h3>
                  </div>
                  <div style={s.selectedBox}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
                      <div>
                        <p style={s.selLabel}>{t('selectedMachineText')}</p>
                        <p style={s.selName}>{selectedMachine.name}</p>
                        <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{selectedMachine.type} - {selectedMachine.regNo}</p>
                      </div>
                      <button style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }} onClick={() => setBookingStep(1)}>{t('changeMachine')}</button>
                    </div>
                  </div>
                  {hireWizardStep === 1 && (
                    <div className="mx-auto mb-4 w-full max-w-[900px]">
                      <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 10px' }}>
                        Choose hire mode
                      </p>
                      <div className="mb-4 grid w-full grid-cols-1 gap-3 min-[481px]:grid-cols-2">
                        {[
                          { id: 'hourly', label: 'Standard Booking', sub: 'Hourly / Daily', icon: '⏱️' },
                          ...(userClientTier >= 2 ? [
                            { id: 'contract', label: 'Contract Booking', sub: 'Monthly / Custom', icon: '📄' },
                            { id: 'site_work', label: 'Site Work', sub: 'Well / Excavation', icon: '⛏️' },
                          ] : []),
                        ].map((mode) => {
                          const selected = hireModePicked && hireMode === mode.id;
                          return (
                            <button
                              key={mode.id}
                              type="button"
                              className="w-full min-h-[88px] rounded-xl px-[clamp(0.75rem,2.5vw,1.25rem)] py-[clamp(0.75rem,2vw,1.1rem)] text-left"
                              style={{
                                cursor: 'pointer',
                                border: selected ? '2px solid #C9A84C' : '1px solid rgba(201,168,76,0.18)',
                                background: selected ? 'rgba(201,168,76,0.12)' : '#030810',
                                color: selected ? '#C9A84C' : '#8896a8',
                              }}
                              onClick={() => {
                                setHireMode(mode.id);
                                setHireModePicked(true);
                                invalidateQuotedDraft();
                                if (mode.id === 'hourly') {
                                  setBookingType('hourly');
                                  setQuantity(selectedMachine?.minimumHours || 3);
                                  setBucketOnlyHours(0);
                                }
                              }}
                            >
                              <p style={{ margin: 0, fontSize: 'clamp(1.1rem, 3.5vw, 1.4rem)' }}>{mode.icon}</p>
                              <p style={{
                                margin: '8px 0 0',
                                fontSize: 'clamp(0.9rem, 2.8vw, 1rem)',
                                fontWeight: 700,
                                color: selected ? '#C9A84C' : '#e8e0d0',
                              }}
                              >
                                {mode.label}
                              </p>
                              <p style={{
                                margin: '4px 0 0',
                                fontSize: 'clamp(0.7rem, 2.2vw, 0.8rem)',
                                color: selected ? 'rgba(201,168,76,0.85)' : '#8896a8',
                              }}
                              >
                                {mode.sub}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                      {userClientTier === 1 && (
                        <div
                          style={{
                            margin: '0 0 12px',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid rgba(201,168,76,0.22)',
                            background: 'rgba(15, 32, 64, 0.65)',
                            color: '#8896a8',
                            fontSize: '12px',
                            lineHeight: 1.45,
                          }}
                        >
                          Contract and Site Work booking is available for Commercial and Government clients
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={!hireModePicked}
                        onClick={() => setHireWizardStep(2)}
                        className="w-full max-w-[900px]"
                        style={{
                          width: '100%',
                          minHeight: 'clamp(44px, 8vw, 52px)',
                          borderRadius: '10px',
                          border: 'none',
                          background: hireModePicked ? '#C9A84C' : 'rgba(201,168,76,0.25)',
                          color: hireModePicked ? '#030810' : '#8896a8',
                          fontWeight: 700,
                          fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)',
                          cursor: hireModePicked ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                  {hireWizardStep >= 2 && (
                    <>
                    {hireMode === 'hourly' && (
                      <>
                        <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 8px' }}>{t('bookingTypeText')}:</p>
                        <div className="mb-4 grid w-full grid-cols-2 gap-2 md:grid-cols-4">
                          {[
                            { id: 'hourly', label: t('perHour'), icon: '⏱️', rate: '₹' + selectedMachine.rates.perHour + '/hr' },
                            { id: 'daily', label: t('perDay'), icon: '📅', rate: '₹' + selectedMachine.rates.perDay + '/d' },
                            { id: 'weekly', label: t('perWeek'), icon: '📆', rate: '₹' + selectedMachine.rates.perWeek + '/wk' },
                            { id: 'monthly', label: t('perMonth'), icon: '🗓️', rate: '₹' + selectedMachine.rates.perMonth + '/mo' },
                          ].map(tp => (
                            <button
                              key={tp.id}
                              type="button"
                              className="min-h-[72px] w-full min-w-0 rounded-lg px-1 py-2 text-center"
                              style={{
                                cursor: 'pointer',
                                border: bookingType === tp.id ? '2px solid #c9a84c' : '1px solid rgba(201,168,76,0.2)',
                                background: bookingType === tp.id ? 'rgba(201,168,76,0.12)' : 'rgba(0,0,0,0.3)',
                              }}
                              onClick={() => {
                                const nextType = tp.id;
                                setBookingType(nextType);
                                if (nextType === 'hourly') {
                                  const minH = selectedMachine?.minimumHours || 3;
                                  setQuantity(minH);
                                  if (selectedAttachment) {
                                    const minAtt = readAttachmentMinHours(selectedMachine, selectedAttachment);
                                    if (minAtt > 0 && minH >= minAtt) {
                                      setAttachmentUsageHours(minAtt);
                                      setBucketOnlyHours(Math.max(0, minH - minAtt));
                                    } else if (minAtt > 0) {
                                      setAttachmentUsageHours(minAtt);
                                      setBucketOnlyHours(0);
                                    } else {
                                      setBucketOnlyHours(minH);
                                    }
                                  } else {
                                    setBucketOnlyHours(0);
                                  }
                                } else {
                                  setQuantity(1);
                                  setBucketOnlyHours(0);
                                }
                              }}
                            >
                              <p className="m-0 text-base">{tp.icon}</p>
                              <p className="m-0 mt-1 break-words text-[11px] font-bold" style={{ color: bookingType === tp.id ? '#c9a84c' : '#e8e0d0' }}>{tp.label}</p>
                              <p className="m-0 mt-0.5 break-words text-[9px] text-[#4CAF50]">{tp.rate}</p>
                            </button>
                          ))}
                        </div>
                        {bookingType === 'hourly' && !selectedAttachment && (
                          <div style={{ marginBottom: '15px' }}>
                            <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 6px' }}>
                              Duration:{' '}
                              <span style={{ color: '#c9a84c', fontWeight: '700' }}>
                                {formatHoursHm(Number(quantity) || 0)}
                              </span>
                            </p>
                            <DurationHmPicker
                              idPrefix="spot-job-duration"
                              value={Number(quantity) || 0}
                              minHours={qtyConfig.min}
                              maxHours={qtyConfig.max}
                              onChange={(next) => {
                                const v = Number(next);
                                setQuantity(Number.isFinite(v) && v > 0 ? v : qtyConfig.min);
                              }}
                            />
                            <p style={{
                              color: 'rgba(201,168,76,0.75)',
                              fontSize: '11px',
                              margin: '6px 0 0',
                              lineHeight: 1.4,
                            }}
                            >
                              Minimum: {selectedMachine?.minimumHours || 3} hrs
                            </p>
                          </div>
                        )}
                        {bookingType === 'hourly' && selectedAttachment && (
                          <div style={{ marginBottom: '15px' }}>
                            <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 6px' }}>
                              Bucket-only hours:{' '}
                              <span style={{ color: '#c9a84c', fontWeight: '700' }}>
                                {formatHoursHm(Number(bucketOnlyHours) || 0)}
                              </span>
                            </p>
                            <DurationHmPicker
                              idPrefix="spot-bucket-only"
                              value={Number(bucketOnlyHours) || 0}
                              minHours={0}
                              maxHours={ATTACHMENT_HOURS_SANITY_MAX}
                              onChange={(next) => setBucketOnlyHours(Number(next) || 0)}
                            />
                            <p style={{
                              color: 'rgba(201,168,76,0.75)',
                              fontSize: '11px',
                              margin: '6px 0 0',
                              lineHeight: 1.4,
                            }}
                            >
                              Minimum: {selectedMachine?.minimumHours || 3} hrs total
                            </p>
                            <p style={{
                              color: '#8896a8',
                              fontSize: '12px',
                              margin: '12px 0 0',
                            }}
                            >
                              Total booking hours:{' '}
                              <span style={{ color: '#c9a84c', fontWeight: '700' }}>
                                {formatHoursHm(spotTotalHours)}
                              </span>
                              {' '}
                              <span style={{ color: '#8896a8', fontWeight: 400 }}>
                                (bucket-only + attachment)
                              </span>
                            </p>
                            {hourlyTotalFloorError ? (
                              <p style={{
                                color: '#e94560',
                                fontSize: '11px',
                                margin: '6px 0 0',
                                lineHeight: 1.4,
                              }}
                              >
                                {hourlyTotalFloorError}
                              </p>
                            ) : null}
                          </div>
                        )}
                        {bookingType !== 'hourly' && (<div className="min-w-0 w-full" style={{ marginBottom: '15px' }}><BookingCalendar onRangeSelect={(start, end) => { setStartDate(start); setEndDate(end); const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000)); if (bookingType === 'daily') setQuantity(days); else if (bookingType === 'weekly') setQuantity(Math.max(1, Math.ceil(days / 7))); else if (bookingType === 'monthly') setQuantity(Math.max(1, Math.ceil(days / 30))); }} bookingType={bookingType} quantity={quantity} bookedDates={machineBookedDates} /></div>)}
                      </>
                    )}
                    {hireMode === 'site_work' && (
                      <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-amber-400/20 bg-amber-400/5 p-3 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <p className="mb-2 text-xs text-slate-400">Site work type</p>
                          <div className="grid grid-cols-1 gap-2 min-[481px]:grid-cols-2">
                            {[
                              { id: 'well_digging', label: 'Well Digging' },
                              { id: 'excavation', label: 'Excavation' },
                            ].map((tp) => (
                              <button
                                key={tp.id}
                                type="button"
                                onClick={() => setSiteWorkType(tp.id)}
                                className="min-h-[48px] rounded-lg px-2 py-2 text-center text-xs font-bold"
                                style={{
                                  border: siteWorkType === tp.id ? '2px solid #c9a84c' : '1px solid rgba(201,168,76,0.2)',
                                  background: siteWorkType === tp.id ? 'rgba(201,168,76,0.12)' : 'rgba(0,0,0,0.3)',
                                  color: siteWorkType === tp.id ? '#c9a84c' : '#e8e0d0',
                                  cursor: 'pointer',
                                }}
                              >
                                {tp.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        {!siteWorkType ? (
                          <p className="md:col-span-2 m-0 text-[11px] leading-relaxed text-[#c9a84c]">
                            Select Well Digging or Excavation. No hire charges until you enter rates and depth — 25% advance is shown after submit.
                          </p>
                        ) : (
                        <>
                        <div className="md:col-span-2">
                          <p className="mb-2 text-xs text-slate-400">Billing basis</p>
                          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                            {[
                              { id: 'per_foot', label: 'Per foot' },
                              { id: 'per_meter', label: 'Per meter' },
                              { id: 'hourly', label: 'Hourly' },
                              { id: 'lumpsum', label: 'Lump sum' },
                            ].map((tp) => (
                              <button
                                key={tp.id}
                                type="button"
                                onClick={() => setSiteBillingBasis(tp.id)}
                                className="min-h-[44px] rounded-lg px-2 py-2 text-center text-[11px] font-bold"
                                style={{
                                  border: siteBillingBasis === tp.id ? '2px solid #c9a84c' : '1px solid rgba(201,168,76,0.2)',
                                  background: siteBillingBasis === tp.id ? 'rgba(201,168,76,0.12)' : 'rgba(0,0,0,0.3)',
                                  color: siteBillingBasis === tp.id ? '#c9a84c' : '#e8e0d0',
                                  cursor: 'pointer',
                                }}
                              >
                                {tp.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        {siteBillingBasis !== 'lumpsum' && (
                          <>
                            <label className="text-xs text-slate-400">
                              Soft strata ₹
                              <input type="number" min="0" step="0.01" value={strataRateSoft} onChange={(e) => setStrataRateSoft(e.target.value)} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" />
                            </label>
                            <label className="text-xs text-slate-400">
                              Murram strata ₹
                              <input type="number" min="0" step="0.01" value={strataRateMurram} onChange={(e) => setStrataRateMurram(e.target.value)} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" />
                            </label>
                            <label className="text-xs text-slate-400">
                              Rock strata ₹
                              <input type="number" min="0" step="0.01" value={strataRateRock} onChange={(e) => setStrataRateRock(e.target.value)} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" />
                            </label>
                            <label className="text-xs text-slate-400">
                              Estimated depth
                              <input type="number" min="0" step="0.01" value={estimatedDepth} onChange={(e) => setEstimatedDepth(e.target.value)} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" />
                            </label>
                          </>
                        )}
                        {siteBillingBasis === 'lumpsum' && (
                          <label className="text-xs text-slate-400 md:col-span-2">
                            Lump sum amount ₹
                            <input type="number" min="1" step="0.01" value={siteLumpsumAmount} onChange={(e) => setSiteLumpsumAmount(e.target.value)} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" />
                          </label>
                        )}
                        <div className="md:col-span-2 rounded-lg border border-[#C9A84C]/25 bg-[#030810]/60 px-3 py-3">
                          <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#C9A84C]">
                            No-water / dry-bore clause (mandatory)
                          </p>
                          <p className="mb-3 text-[11px] leading-relaxed text-[#e8e0d0]">
                            {NO_WATER_CLAUSE_TEXT[lang] || NO_WATER_CLAUSE_TEXT.en}
                          </p>
                          <label className="flex items-start gap-2 text-xs text-slate-300">
                            <input
                              type="checkbox"
                              checked={noWaterClauseAccepted}
                              onChange={(e) => setNoWaterClauseAccepted(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span>I accept the no-water clause (audio/text confirmation in my language).</span>
                          </label>
                          <p className="mt-3 text-[11px] text-[#c9a84c]">
                            Advance payable: 25% of estimated site-work value (shown after submit).
                          </p>
                        </div>
                        {siteWorkSuccess && (
                          <div className="md:col-span-2 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-400">
                            <p style={{ margin: 0 }}>{siteWorkSuccess}</p>
                            <p style={{ margin: '6px 0 0', fontWeight: 500 }}>{t('dewateringReferral')}</p>
                          </div>
                        )}
                        </>
                        )}
                      </div>
                    )}
                    {hireMode === 'contract' && (
                      <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-amber-400/20 bg-amber-400/5 p-3 md:grid-cols-2">
                        <label className="text-xs text-slate-400">
                          Contract start date
                          <input type="date" value={startDate} min={new Date().toISOString().split('T')[0]} onChange={(event) => { setStartDate(event.target.value); invalidateQuotedDraft(); }} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" style={{ colorScheme: 'dark' }} />
                        </label>
                        <label className="text-xs text-slate-400">
                          Contract end date
                          <input type="date" value={endDate} min={startDate} onChange={(event) => { setEndDate(event.target.value); invalidateQuotedDraft(); }} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" style={{ colorScheme: 'dark' }} />
                        </label>
                        <label className="text-xs text-slate-400">
                          Billing cycle
                          <select value={billingCycle} onChange={(event) => { setBillingCycle(event.target.value); invalidateQuotedDraft(); }} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100">
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                          </select>
                        </label>
                        <div className="md:col-span-2">
                          <p className="mb-1 text-xs text-slate-400">Billing basis</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => { setBillingBasis('calendar_month'); invalidateQuotedDraft(); }}
                              style={{
                                background: billingBasis === 'calendar_month'
                                  ? 'rgba(201,168,76,0.22)'
                                  : 'rgba(201,168,76,0.12)',
                                border: billingBasis === 'calendar_month'
                                  ? '1px solid #C9A84C'
                                  : '1px solid rgba(201,168,76,0.5)',
                                borderRadius: '8px',
                                color: '#C9A84C',
                                fontSize: '13px',
                                fontWeight: 600,
                                padding: '10px 16px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              📅 Calendar Month
                            </button>
                            <button
                              type="button"
                              onClick={() => { setBillingBasis('working_days_26'); invalidateQuotedDraft(); }}
                              style={{
                                background: billingBasis === 'working_days_26'
                                  ? 'rgba(201,168,76,0.22)'
                                  : 'rgba(201,168,76,0.12)',
                                border: billingBasis === 'working_days_26'
                                  ? '1px solid #C9A84C'
                                  : '1px solid rgba(201,168,76,0.5)',
                                borderRadius: '8px',
                                color: '#C9A84C',
                                fontSize: '13px',
                                fontWeight: 600,
                                padding: '10px 16px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              🏗 26 Working Days
                            </button>
                          </div>
                          <p className="mb-0 mt-1 text-[10px] text-slate-500">
                            {billingBasis === 'working_days_26'
                              ? 'Billing per 26 working days per cycle'
                              : 'Billing per calendar month (Jan 1 → Feb 1)'}
                          </p>
                        </div>
                        <label className="text-xs text-slate-400">
                          Fixed rate per cycle
                          <input type="number" min="1" step="0.01" value={fixedRatePerCycle} onChange={(event) => { setFixedRatePerCycle(event.target.value); invalidateQuotedDraft(); }} placeholder="Enter agreed rate" className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" />
                          {previewCycles !== null && Number(fixedRatePerCycle) > 0 && !contractQuote ? (
                            <p className="mb-0 mt-1 text-[10px] italic text-de-gold">
                              {previewCycles} cycle(s) · ₹{(previewCycles * Number(fixedRatePerCycle)).toLocaleString('en-IN')} estimated
                            </p>
                          ) : null}
                        </label>
                        <label className="text-xs text-slate-400 md:col-span-2">
                          Guaranteed hours per cycle
                          <input type="number" min="0.01" step="0.01" value={guaranteedHoursPerCycle} onChange={(event) => { setGuaranteedHoursPerCycle(event.target.value); invalidateQuotedDraft(); }} placeholder="Agreed minimum hours" className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100" />
                        </label>

                        <div className="md:col-span-2 rounded-lg border border-[#C9A84C]/25 bg-[#030810]/60 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setShowStandbyTerms((v) => !v)}
                            className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                          >
                            <span className="text-[11px] font-bold uppercase tracking-wide text-[#C9A84C]">
                              Standby &amp; Termination Terms
                            </span>
                            <span className="text-[#C9A84C] text-xs">{showStandbyTerms ? '▲' : '▼'}</span>
                          </button>
                          {showStandbyTerms && (
                            <div className="grid grid-cols-1 gap-3 border-t border-[#C9A84C]/20 p-3 md:grid-cols-2">
                              <label className="text-xs text-slate-400 md:col-span-2">
                                Standby rate during idle months (%)
                                <input
                                  type="number"
                                  min="30"
                                  max="75"
                                  step="1"
                                  value={standbyPercent}
                                  onChange={(e) => { setStandbyPercent(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                />
                                <p className="mb-0 mt-1 text-[10px] text-slate-500">
                                  Machine charged at this % of monthly rate during standby periods
                                </p>
                              </label>
                              <label className="text-xs text-slate-400">
                                Standby start month
                                <select
                                  value={monsoonStartMonth}
                                  onChange={(e) => { setMonsoonStartMonth(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                >
                                  <option value="1">January</option>
                                  <option value="2">February</option>
                                  <option value="3">March</option>
                                  <option value="4">April</option>
                                  <option value="5">May</option>
                                  <option value="6">June</option>
                                  <option value="7">July</option>
                                  <option value="8">August</option>
                                  <option value="9">September</option>
                                  <option value="10">October</option>
                                  <option value="11">November</option>
                                  <option value="12">December</option>
                                </select>
                              </label>
                              <label className="text-xs text-slate-400">
                                Standby end month
                                <select
                                  value={monsoonEndMonth}
                                  onChange={(e) => { setMonsoonEndMonth(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                >
                                  <option value="1">January</option>
                                  <option value="2">February</option>
                                  <option value="3">March</option>
                                  <option value="4">April</option>
                                  <option value="5">May</option>
                                  <option value="6">June</option>
                                  <option value="7">July</option>
                                  <option value="8">August</option>
                                  <option value="9">September</option>
                                  <option value="10">October</option>
                                  <option value="11">November</option>
                                  <option value="12">December</option>
                                </select>
                              </label>
                              <label className="text-xs text-slate-400">
                                Notice period required (days)
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={terminationNoticeDays}
                                  onChange={(e) => { setTerminationNoticeDays(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                />
                              </label>
                              <label className="text-xs text-slate-400">
                                Termination fee formula
                                <select
                                  value={terminationFeeFormula}
                                  onChange={(e) => { setTerminationFeeFormula(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                >
                                  <option value="25_percent_remaining">25% of remaining cycles</option>
                                  <option value="flat">Flat fee</option>
                                </select>
                              </label>
                              {terminationFeeFormula === 'flat' && (
                                <label className="text-xs text-slate-400 md:col-span-2">
                                  Flat termination fee (₹)
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={terminationFeeFlat}
                                    onChange={(e) => { setTerminationFeeFlat(e.target.value); invalidateQuotedDraft(); }}
                                    placeholder="Enter flat fee amount"
                                    className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                  />
                                </label>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="md:col-span-2 rounded-lg border border-[#C9A84C]/25 bg-[#030810]/60 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setShowPenaltyTerms((v) => !v)}
                            className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
                          >
                            <span className="text-[11px] font-bold uppercase tracking-wide text-[#C9A84C]">
                              Penalty Terms
                            </span>
                            <span className="text-[#C9A84C] text-xs">{showPenaltyTerms ? '▲' : '▼'}</span>
                          </button>
                          {showPenaltyTerms && (
                            <div className="grid grid-cols-1 gap-3 border-t border-[#C9A84C]/20 p-3 md:grid-cols-2">
                              <label className="text-xs text-slate-400">
                                No-show penalty (% of contract value)
                                <input
                                  type="number"
                                  min="25"
                                  max="50"
                                  step="1"
                                  value={ownerNoshowPenaltyPct}
                                  onChange={(e) => { setOwnerNoshowPenaltyPct(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                />
                                <p className="mb-0 mt-1 text-[10px] text-slate-500">Floor 25%. Range 25–50.</p>
                              </label>
                              <label className="text-xs text-slate-400">
                                Abandonment penalty (% of remaining value)
                                <input
                                  type="number"
                                  min="20"
                                  max="40"
                                  step="1"
                                  value={ownerAbandonmentPenaltyPct}
                                  onChange={(e) => { setOwnerAbandonmentPenaltyPct(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                />
                                <p className="mb-0 mt-1 text-[10px] text-slate-500">Floor 20%. Range 20–40.</p>
                              </label>
                              <label className="text-xs text-slate-400">
                                Breakdown compensation (% per week)
                                <input
                                  type="number"
                                  min="5"
                                  max="15"
                                  step="1"
                                  value={ownerBreakdownCreditPct}
                                  onChange={(e) => { setOwnerBreakdownCreditPct(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                />
                                <p className="mb-0 mt-1 text-[10px] text-slate-500">Floor 5%. Range 5–15.</p>
                              </label>
                              <label className="text-xs text-slate-400">
                                Your early exit penalty (%)
                                <input
                                  type="number"
                                  min="20"
                                  max="40"
                                  step="1"
                                  value={clientTerminationPenaltyPct}
                                  onChange={(e) => { setClientTerminationPenaltyPct(e.target.value); invalidateQuotedDraft(); }}
                                  className="mt-1 w-full rounded border border-[#C9A84C]/30 bg-slate-950 px-3 py-2 text-slate-100"
                                />
                                <p className="mb-0 mt-1 text-[10px] text-slate-500">Floor 20%. Range 20–40.</p>
                              </label>
                            </div>
                          )}
                        </div>

                        <label className="text-xs text-slate-400 md:col-span-2">
                          Payment expected by day ___ of each month
                          <input
                            type="number"
                            min="1"
                            max="28"
                            step="1"
                            required
                            value={ownerPaymentDueDay}
                            onChange={(e) => { setOwnerPaymentDueDay(e.target.value); invalidateQuotedDraft(); }}
                            placeholder="1–28"
                            className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100"
                          />
                          <p className="mb-0 mt-1 text-[10px] text-slate-500">Required. Calendar day 1–28.</p>
                        </label>

                        {Array.isArray(selectedMachine.attachments) && selectedMachine.attachments.length > 0 && (
                          <>
                            <label className="text-xs text-slate-400 md:col-span-2">
                              {t('bookingAttachment')}
                              <select
                                value={selectedAttachment ?? ''}
                                onChange={(e) => {
                                  const next = e.target.value || null;
                                  setSelectedAttachment(next);
                                  if (!next) {
                                    setAttachmentRatePerCycle('');
                                    setAttachmentRateAutoSuggestion(null);
                                  }
                                  invalidateQuotedDraft();
                                }}
                                className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100"
                              >
                                <option value="">{t('bookingAttachmentNone')}</option>
                                {selectedMachine.attachments.map((att) => {
                                  const id = String(att);
                                  return (
                                    <option key={id} value={id}>
                                      {ATTACHMENT_LABEL_BY_ID[id] || id}
                                    </option>
                                  );
                                })}
                              </select>
                            </label>
                            {selectedAttachment ? (
                              <label className="text-xs text-slate-400 md:col-span-2">
                                Attachment rate per cycle (optional)
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={attachmentRatePerCycle}
                                  onChange={(event) => {
                                    setAttachmentRatePerCycle(event.target.value);
                                    invalidateQuotedDraft();
                                  }}
                                  placeholder="Leave blank if bundled in fixed rate"
                                  className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100"
                                />
                                {showAttachmentRateHint ? (
                                  <p className="mb-0 mt-1 text-[10px] text-amber-200/90">
                                    {`Suggested from owner rate ₹${attachmentHourlyExtra.toLocaleString('en-IN')}/hr × ${attachmentGmhNum} GMH`}
                                  </p>
                                ) : (
                                  <p className="mb-0 mt-1 text-[10px] text-slate-500">
                                    Leave blank if bundled in fixed rate. Pre-fills when the owner registered an attachment hourly rate and GMH is set.
                                  </p>
                                )}
                              </label>
                            ) : null}
                          </>
                        )}
                        <label className="text-xs text-slate-400">
                          Mobilization transport paid by
                          <select value={mobilizationTransportBy} onChange={(event) => {
                            const next = event.target.value;
                            setMobilizationTransportBy(next);
                            if (next === 'owner') setMobilizationTransportAmount('');
                            invalidateQuotedDraft();
                          }} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100">
                            <option value="">Select who pays</option>
                            <option value="client">Client (collected at booking)</option>
                            <option value="owner">Owner (outside platform)</option>
                            <option value="shared">Shared (record only)</option>
                          </select>
                        </label>
                        <label className="text-xs text-slate-400">
                          Mobilization transport amount
                          <input type="number" min="0" step="0.01" value={mobilizationTransportAmount} onChange={(event) => { setMobilizationTransportAmount(event.target.value); invalidateQuotedDraft(); }} placeholder={mobilizationTransportBy === 'client' ? 'Required taxable amount' : 'Optional'} disabled={!mobilizationTransportBy || mobilizationTransportBy === 'owner'} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100 disabled:opacity-50" />
                        </label>
                        <label className="text-xs text-slate-400">
                          Demobilization transport paid by
                          <select value={demobilizationTransportBy} onChange={(event) => {
                            const next = event.target.value;
                            setDemobilizationTransportBy(next);
                            if (next === 'owner') setDemobilizationTransportAmount('');
                            invalidateQuotedDraft();
                          }} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100">
                            <option value="">Select who pays</option>
                            <option value="client">Client (record only for now)</option>
                            <option value="owner">Owner (outside platform)</option>
                            <option value="shared">Shared (record only)</option>
                          </select>
                        </label>
                        <label className="text-xs text-slate-400">
                          Demobilization transport amount
                          <input type="number" min="0" step="0.01" value={demobilizationTransportAmount} onChange={(event) => { setDemobilizationTransportAmount(event.target.value); invalidateQuotedDraft(); }} placeholder={demobilizationTransportBy === 'client' ? 'Required for record' : 'Optional'} disabled={!demobilizationTransportBy || demobilizationTransportBy === 'owner'} className="mt-1 w-full rounded border border-de-gold/30 bg-slate-950 px-3 py-2 text-slate-100 disabled:opacity-50" />
                        </label>
                        <p className="m-0 text-[11px] text-amber-200 md:col-span-2">
                          Transport is one-time (not part of RA-Bill cycles). Only client-paid Mobilization Charge is collected now (100% of B, with GST). Shared and demobilization are stored for the contract record.
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && contractQuote ? (
                      !showQuotationResult ? renderContractQuoteBreakdown() : null
                    ) : hireMode !== 'site_work' ? (
                      [
                        {
                          label: selectedAttachment ? 'Standard Bucket Work' : 'Base Machine Charge',
                          val: '₹' + Number(displayedQuote.unitBase ?? totalCost ?? 0).toLocaleString('en-IN'),
                        },
                        ...(selectedMachine?.operatorIncluded !== false ? [{
                          label: 'Operator',
                          val: 'Included ✓',
                        }] : selectedMachine?.operatorDailyRate ? [{
                          label: 'Operator',
                          val: `₹${Number(selectedMachine.operatorDailyRate).toLocaleString('en-IN')}/day (separate)`,
                        }] : []),
                        ...(Number(displayedQuote.attachmentSurcharge || 0) > 0 ? (() => {
                          const attName = ATTACHMENT_LABEL_BY_ID[selectedAttachment]
                            || selectedAttachment
                            || 'Attachment';
                          const usageN = Number(
                            displayedQuote.attachmentUsageHours ?? attachmentUsageHours,
                          );
                          const rateN = Number(
                            displayedQuote.attachmentHourly
                            ?? selectedMachine?.attachmentRates?.[selectedAttachment]
                            ?? 0,
                          );
                          const usageLabel = Number.isFinite(usageN) && usageN > 0
                            ? formatHoursHm(usageN).replace(/ hr\b/g, 'hr').replace(/ min\b/g, 'min')
                            : null;
                          const rateLabel = Number.isFinite(rateN) && rateN > 0
                            ? `₹${rateN.toLocaleString('en-IN')}/hr`
                            : null;
                          const label = usageLabel && rateLabel
                            ? `${attName} — ${usageLabel} × ${rateLabel}`
                            : `${attName} Work`;
                          return [{
                            label,
                            val: '₹' + Number(displayedQuote.attachmentSurcharge || 0).toLocaleString('en-IN'),
                          }];
                        })() : []),
                        ...(displayedQuote.attachmentSurcharge > 0
                          && Math.round(totalCost * 100)
                            !== Math.round(Number(displayedQuote.unitBase ?? totalCost) * 100)
                          ? [{ label: t('baseAmount'), val: '₹' + totalCost.toLocaleString('en-IN') }]
                          : []),
                        { label: t('gst18'), val: '₹' + gstAmount.toLocaleString('en-IN') },
                      ].map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <span style={{ color: '#8896a8', fontSize: '12px' }}>{r.label}</span>
                          <span style={{ color: '#e8e0d0', fontSize: '12px', fontWeight: '600' }}>{r.val}</span>
                        </div>
                      ))
                    ) : null}
                    {hireMode !== 'contract' && hireMode !== 'site_work' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0' }}>
                        <span style={{ color: '#c9a84c', fontWeight: '700' }}>{t('grandTotalText')}</span>
                        <span style={{ color: '#c9a84c', fontWeight: '700', fontSize: '16px' }}>₹{grandTotal.toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    {hireMode !== 'contract' && hireMode !== 'site_work' && (
                      <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '8px', marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>
                            {t('advanceRequired50Pct')}
                          </p>
                          <p style={{ color: '#c9a84c', fontWeight: '700', margin: 0 }}>₹{advanceAmt.toLocaleString('en-IN')}</p>
                        </div>
                        <p style={{ color: '#8896a8', fontSize: '10px', margin: '6px 0 0', lineHeight: 1.35 }}>
                          {t('advance50PctHint')}
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && contractQuote && (
                      <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '6px', padding: '8px', marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>Estimated advance (collected after approval)</p>
                          <p style={{ color: '#c9a84c', fontWeight: '700', margin: 0 }}>₹{advanceAmt.toLocaleString('en-IN')}</p>
                        </div>
                        <p style={{ color: '#8896a8', fontSize: '10px', margin: '6px 0 0', lineHeight: 1.35 }}>
                          No payment now. Accept terms to send this quotation to the owner.
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && activeQuotation?.id && ['quoted', 'owner_review', 'admin_review', 'client_esign'].includes(activeQuotation.status) && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: '8px',
                          background: 'rgba(201,168,76,0.12)',
                          border: '1px solid rgba(201,168,76,0.5)',
                          borderRadius: '8px',
                          color: '#C9A84C',
                          fontSize: '12px',
                          fontWeight: 600,
                          padding: '6px 12px',
                        }}
                      >
                        {billingBasisChipLabel(activeQuotation.billingBasis || billingBasis)}
                      </span>
                    )}
                    {hireMode === 'contract' && !contractQuote && (
                      <p className="mb-0 mt-2 text-[11px] text-amber-200">
                        Fill contract terms, site address, and work description, then request a quotation.
                      </p>
                    )}
                    {hireMode === 'contract' && activeQuotation?.status === 'owner_review' && (
                      <div style={{ background: 'rgba(255,152,0,0.1)', border: '1px solid rgba(255,152,0,0.35)', borderRadius: '6px', padding: '10px', marginTop: '8px' }}>
                        <p style={{ color: '#FF9800', fontWeight: 700, fontSize: '12px', margin: '0 0 4px' }}>
                          Awaiting owner response — 1 hour
                        </p>
                        <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>
                          Quotation #{activeQuotation.quotationRef || activeQuotation.id}
                          {activeQuotation.ownerResponseDeadline
                            ? ` · Deadline ${new Date(activeQuotation.ownerResponseDeadline).toLocaleString('en-IN')}`
                            : ''}
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && activeQuotation?.status === 'admin_review' && (
                      <div style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.35)', borderRadius: '6px', padding: '10px', marginTop: '8px' }}>
                        <p style={{ color: '#4CAF50', fontWeight: 700, fontSize: '12px', margin: 0 }}>
                          Owner accepted. Quotation is in admin review.
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && activeQuotation?.status === 'awaiting_mobilization' && (
                      <div style={{
                        background: 'rgba(201,168,76,0.1)',
                        border: '1px solid rgba(201,168,76,0.35)',
                        borderRadius: '6px',
                        padding: '10px',
                        marginTop: '8px',
                      }}
                      >
                        <p style={{ color: '#C9A84C', fontWeight: 700, fontSize: '12px', margin: 0 }}>
                          Machine dispatched — awaiting arrival at site
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && activeQuotation?.status === 'mobilization_confirmed' && (
                      <div style={{
                        background: 'rgba(201,168,76,0.1)',
                        border: '1px solid rgba(201,168,76,0.35)',
                        borderRadius: '6px',
                        padding: '10px',
                        marginTop: '8px',
                      }}
                      >
                        <p style={{ color: '#C9A84C', fontWeight: 700, fontSize: '12px', margin: 0 }}>
                          Owner confirmed mobilization — awaiting your arrival confirmation
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && activeQuotation?.status === 'converted' && (
                      <div style={{
                        marginTop: '10px',
                        padding: '12px',
                        borderRadius: '8px',
                        background: (quotationArrivalSuccess || activeQuotation.mobilizationArrivalConfirmedAt)
                          ? 'rgba(76,175,80,0.1)'
                          : 'rgba(3,8,16,0.85)',
                        border: (quotationArrivalSuccess || activeQuotation.mobilizationArrivalConfirmedAt)
                          ? '1px solid rgba(76,175,80,0.35)'
                          : '1px solid rgba(201,168,76,0.35)',
                      }}
                      >
                        {(quotationArrivalSuccess || activeQuotation.mobilizationArrivalConfirmedAt) ? (
                          <p style={{ color: '#4CAF50', fontWeight: 800, fontSize: '12px', margin: 0 }}>
                            ✅ Arrival confirmed — Contract active
                          </p>
                        ) : (
                          <>
                            <p style={{ color: '#C9A84C', fontWeight: 800, fontSize: '12px', margin: '0 0 8px' }}>
                              Machine dispatched — confirm arrival at site
                            </p>
                            <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '8px' }}>
                              Arrival photo (optional)
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setQuotationArrivalPhoto(e.target.files?.[0] || null)}
                                style={{ display: 'block', marginTop: '6px', color: '#e8e0d0', fontSize: '11px' }}
                              />
                            </label>
                          </>
                        )}
                      </div>
                    )}
                    {hireMode === 'contract' && activeQuotation?.status === 'client_esign' && !esignPaySuccess && (
                      <div style={{
                        marginTop: '10px',
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'rgba(3,8,16,0.85)',
                        border: '1px solid rgba(201,168,76,0.35)',
                      }}
                      >
                        <p style={{ color: '#C9A84C', fontWeight: 800, fontSize: '12px', margin: '0 0 8px' }}>
                          Ready for e-sign & advance
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Machine</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                              {activeQuotation.machine?.name || selectedMachine?.name || '—'}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Dates</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                              {activeQuotation.startDate || '—'}
                              {' → '}
                              {activeQuotation.endDate || '—'}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Rate / cycle</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                              ₹{Number(activeQuotation.fixedRatePerCycle || 0).toLocaleString('en-IN')}
                              {activeQuotation.billingCycle ? ` · ${activeQuotation.billingCycle}` : ''}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Advance due</p>
                            <p style={{ color: '#C9A84C', fontSize: '13px', fontWeight: 800, margin: '2px 0 0' }}>
                              ₹{Number(activeQuotation.advanceRequired || 0).toLocaleString('en-IN')}
                            </p>
                          </div>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '12px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={esignTermsAccepted}
                            onChange={(e) => setEsignTermsAccepted(e.target.checked)}
                            style={{ marginTop: '2px' }}
                          />
                          <span style={{ color: '#e8e0d0', fontSize: '11px', lineHeight: 1.4 }}>
                            I have read and agree to the contract hire terms
                          </span>
                        </label>
                        <div style={{
                          marginTop: '14px',
                          paddingTop: '12px',
                          borderTop: '1px solid rgba(201,168,76,0.25)',
                        }}
                        >
                          <p style={{ color: '#C9A84C', fontWeight: 800, fontSize: '12px', margin: '0 0 8px' }}>
                            Set up Payment Mandate (e-NACH)
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                            <div>
                              <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Max amount (total contract value)</p>
                              <p style={{ color: '#C9A84C', fontSize: '13px', fontWeight: 800, margin: '2px 0 0' }}>
                                ₹{Number(
                                  mandateInfo?.mandate_max_amount
                                  || activeQuotation.contractTotal
                                  || contractQuote?.totalAmount
                                  || 0,
                                ).toLocaleString('en-IN')}
                              </p>
                            </div>
                            <div>
                              <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Bank authorization</p>
                              <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                                Required — RBI e-NACH / MSMED 2026
                              </p>
                            </div>
                          </div>
                          {mandateInfo?.mandate_status === 'confirmed' ? (
                            <p style={{ color: '#4CAF50', fontWeight: 700, fontSize: '12px', margin: '10px 0 0' }}>
                              ✅ e-NACH Active — auto-debit enabled
                              {mandateInfo.mandate_bank_name
                                ? ` · ${mandateInfo.mandate_bank_name}`
                                : ''}
                            </p>
                          ) : mandateInfo?.mandate_status === 'rejected' ? (
                            <div style={{ marginTop: '10px' }}>
                              <p style={{ color: '#ef5350', fontWeight: 700, fontSize: '12px', margin: 0 }}>
                                ❌ Mandate rejected — please retry
                              </p>
                              <button
                                type="button"
                                onClick={handleAuthorizeEnachMandate}
                                disabled={mandateLoading}
                                style={{
                                  marginTop: '10px',
                                  width: '100%',
                                  background: '#C9A84C',
                                  border: 'none',
                                  color: '#030810',
                                  borderRadius: '8px',
                                  padding: '10px 14px',
                                  fontSize: '12px',
                                  fontWeight: 800,
                                  cursor: mandateLoading ? 'wait' : 'pointer',
                                  opacity: mandateLoading ? 0.7 : 1,
                                }}
                              >
                                {mandateLoading ? 'Creating mandate…' : 'Authorize e-NACH Mandate →'}
                              </button>
                            </div>
                          ) : mandateInfo?.mandate_id ? (
                            <div style={{ marginTop: '10px' }}>
                              <p style={{ color: '#e8e0d0', fontSize: '11px', margin: 0 }}>
                                Mandate ID:
                                {' '}
                                <span style={{ color: '#C9A84C', fontWeight: 700 }}>
                                  {mandateInfo.mandate_id}
                                </span>
                              </p>
                              <p style={{ color: '#8896a8', fontSize: '11px', margin: '6px 0 0' }}>
                                Mandate pending bank authorization
                              </p>
                              {mandateInfo.authorization_url && (
                                <a
                                  href={mandateInfo.authorization_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: 'inline-block',
                                    marginTop: '8px',
                                    color: '#C9A84C',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                  }}
                                >
                                  Continue bank authorization →
                                </a>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={handleAuthorizeEnachMandate}
                              disabled={mandateLoading}
                              style={{
                                marginTop: '12px',
                                width: '100%',
                                background: '#C9A84C',
                                border: 'none',
                                color: '#030810',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                fontSize: '12px',
                                fontWeight: 800,
                                cursor: mandateLoading ? 'wait' : 'pointer',
                                opacity: mandateLoading ? 0.7 : 1,
                              }}
                            >
                              {mandateLoading ? 'Creating mandate…' : 'Authorize e-NACH Mandate →'}
                            </button>
                          )}
                          {mandateError && (
                            <p style={{ color: '#ef5350', fontSize: '11px', margin: '8px 0 0' }}>
                              {mandateError}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {hireMode === 'contract' && esignPaySuccess && (
                      <div style={{
                        marginTop: '10px',
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'rgba(76,175,80,0.1)',
                        border: '1px solid rgba(76,175,80,0.35)',
                      }}
                      >
                        <p style={{ color: '#4CAF50', fontWeight: 800, fontSize: '12px', margin: 0 }}>
                          Signed & paid — booking #
                          {esignPaySuccess.bookingRef || 'created'}
                        </p>
                        <p style={{ color: '#8896a8', fontSize: '10px', margin: '6px 0 0' }}>
                          Advance ₹{Number(esignPaySuccess.advance || 0).toLocaleString('en-IN')} deducted from wallet.
                        </p>
                      </div>
                    )}
                    {hireMode === 'contract' && activeQuotation?.status === 'quoted' && (
                      <>
                      {!showQuotationResult && (
                      <>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={quotationTermsAccepted}
                          onChange={(e) => setQuotationTermsAccepted(e.target.checked)}
                          style={{ marginTop: '2px' }}
                        />
                        <span style={{ color: '#e8e0d0', fontSize: '11px', lineHeight: 1.4 }}>
                          I accept the contract hire terms and authorize sending this quotation to the machine owner for review.
                        </span>
                      </label>
                      {activeQuotation.ownerPaymentDueDay != null && (
                        <>
                          <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginTop: '10px' }}>
                            Propose alternate day (optional)
                            <input
                              type="number"
                              min="1"
                              max="28"
                              step="1"
                              value={clientPaymentDueDayProposal}
                              onChange={(e) => setClientPaymentDueDayProposal(e.target.value)}
                              placeholder={`${activeQuotation.ownerPaymentDueDay} (owner day)`}
                              style={{
                                marginTop: '6px',
                                width: '100%',
                                borderRadius: '8px',
                                border: '1px solid rgba(201,168,76,0.3)',
                                background: '#020617',
                                color: '#e8e0d0',
                                padding: '7px 10px',
                                fontSize: '12px',
                              }}
                            />
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: '6px 0 0' }}>
                              Leave blank to accept owner day {activeQuotation.ownerPaymentDueDay} of each month. Range 1–28.
                            </p>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={paymentDueDateAccepted}
                              onChange={(e) => setPaymentDueDateAccepted(e.target.checked)}
                              style={{ marginTop: '2px' }}
                            />
                            <span style={{ color: '#e8e0d0', fontSize: '11px', lineHeight: 1.4 }}>
                              I agree to the payment due date
                            </span>
                          </label>
                        </>
                      )}
                      </>
                      )}
                      <button
                        type="button"
                        onClick={handleCancelQuotation}
                        disabled={quotationActionLoading}
                        style={{
                          marginTop: '10px',
                          background: 'rgba(239,68,68,0.12)',
                          border: '1px solid rgba(239,68,68,0.45)',
                          color: '#ef4444',
                          borderRadius: '8px',
                          padding: '7px 12px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: quotationActionLoading ? 'not-allowed' : 'pointer',
                          opacity: quotationActionLoading ? 0.7 : 1,
                        }}
                      >
                        {quotationActionLoading ? 'Cancelling...' : 'Cancel Quotation'}
                      </button>
                      </>
                    )}
                    {hireMode === 'contract' && activeQuotation?.id && (
                      <button
                        type="button"
                        onClick={() => setShowQuotationTermsSnapshot((prev) => !prev)}
                        style={{ marginTop: '10px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '7px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        {showQuotationTermsSnapshot ? 'Hide quotation terms snapshot' : 'View quotation terms snapshot'}
                      </button>
                    )}
                    {hireMode === 'contract' && activeQuotation?.id && showQuotationTermsSnapshot && (
                      <div style={{ marginTop: '10px', background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '8px', padding: '10px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' }}>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Legal terms version</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>{activeQuotation.legalTermsVersion || '—'}</p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Accepted at</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                              {activeQuotation.clientTermsAcceptedAt ? new Date(activeQuotation.clientTermsAcceptedAt).toLocaleString('en-IN') : 'Not accepted yet'}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Billing cycle</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>{activeQuotation.billingCycle || '—'}</p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Billing basis</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                              {billingBasisChipLabel(activeQuotation.billingBasis)}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Rate / cycle</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>₹{Number(activeQuotation.fixedRatePerCycle || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Guaranteed hours / cycle</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>{activeQuotation.guaranteedHoursPerCycle || '—'}</p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Contract total</p>
                            <p style={{ color: '#c9a84c', fontSize: '12px', margin: '2px 0 0', fontWeight: 700 }}>₹{Number(activeQuotation.contractTotal || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Payment due day</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                              {activeQuotation.ownerPaymentDueDay != null
                                ? `${activeQuotation.ownerPaymentDueDay} of each month`
                                : '—'}
                            </p>
                          </div>
                          <div>
                            <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>Agreed payment due day</p>
                            <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '2px 0 0' }}>
                              {activeQuotation.paymentDueDayAgreed != null
                                ? `${activeQuotation.paymentDueDayAgreed} of each month`
                                : '—'}
                            </p>
                          </div>
                        </div>
                        <div style={{ marginTop: '8px' }}>
                          <button
                            type="button"
                            onClick={() => { sessionStorage.setItem('returnTab', activeTab); navigate('/terms'); }}
                            style={{ background: 'transparent', border: 'none', padding: 0, color: '#c9a84c', fontSize: '11px', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            Open platform Terms & Conditions
                          </button>
                        </div>
                      </div>
                    )}
                    {contractQuoteError && (
                      <p className="mb-0 mt-2 text-[11px] text-red-400">{contractQuoteError}</p>
                    )}
                  {hireMode !== 'contract'
                    && hireMode !== 'site_work'
                    && Array.isArray(selectedMachine.attachments)
                    && selectedMachine.attachments.length > 0 && (
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', color: '#8896a8', fontSize: '12px', marginBottom: '6px' }}>{t('bookingAttachment')}</label>
                      <select
                        value={selectedAttachment ?? ''}
                        onChange={(e) => {
                          const next = e.target.value || null;
                          if (!next) {
                            if (bookingType === 'hourly') {
                              const b = Number(bucketOnlyHours);
                              const a = Number(attachmentUsageHours);
                              const total = (Number.isFinite(b) && b > 0 ? b : 0)
                                + (Number.isFinite(a) && a > 0 ? a : 0);
                              const minH = selectedMachine?.minimumHours || 3;
                              setQuantity(total > 0 ? Math.max(minH, total) : minH);
                            }
                            setBucketOnlyHours(0);
                            setSelectedAttachment(null);
                            setAttachmentUsageHours('');
                            setAttachmentRatePerCycle('');
                            setAttachmentRateAutoSuggestion(null);
                            return;
                          }
                          setSelectedAttachment(next);
                          if (bookingType === 'hourly') {
                            const minAtt = readAttachmentMinHours(selectedMachine, next);
                            const q = Number(quantity) || 0;
                            const existingBucket = Number(bucketOnlyHours);
                            const hasBucket = Number.isFinite(existingBucket) && existingBucket > 0;
                            if (minAtt > 0) {
                              setAttachmentUsageHours(minAtt);
                              if (!hasBucket) {
                                setBucketOnlyHours(q >= minAtt ? Math.max(0, q - minAtt) : 0);
                              }
                            } else if (!hasBucket) {
                              setBucketOnlyHours(q > 0 ? q : 0);
                              setAttachmentUsageHours('');
                            } else {
                              setAttachmentUsageHours('');
                            }
                          }
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.6)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'; }}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '10px 14px',
                          background: 'rgba(0,0,0,0.35)',
                          border: '1px solid rgba(201,168,76,0.3)',
                          borderRadius: '8px',
                          color: '#C9A84C',
                          fontSize: '13px',
                          outline: 'none',
                          cursor: 'pointer',
                          colorScheme: 'dark',
                        }}
                      >
                        <option value="" style={{ background: '#0a1628', color: '#C9A84C' }}>{t('bookingAttachmentNone')}</option>
                        {selectedMachine.attachments.map((att) => {
                          const id = String(att);
                          return (
                            <option key={id} value={id} style={{ background: '#0a1628', color: '#C9A84C' }}>
                              {ATTACHMENT_LABEL_BY_ID[id] || id}
                            </option>
                          );
                        })}
                      </select>
                      {selectedAttachment ? (
                        <div style={{ marginTop: '10px' }}>
                          {attachmentDurationBlocked ? (
                            <p style={{
                              color: '#FFB74D',
                              fontSize: '11px',
                              margin: 0,
                              lineHeight: 1.4,
                            }}
                            >
                              {attachmentDurationBlocked}
                            </p>
                          ) : (
                            <>
                              <label style={{ display: 'block', color: '#8896a8', fontSize: '12px', marginBottom: '6px' }}>
                                {bookingType === 'hourly'
                                  ? `${ATTACHMENT_LABEL_BY_ID[selectedAttachment] || selectedAttachment} hours`
                                  : 'Attachment usage'}
                              </label>
                              <DurationHmPicker
                                idPrefix="spot-attachment-usage"
                                allowEmpty
                                value={attachmentUsageHours}
                                minHours={attachmentMinRequired > 0 ? attachmentMinRequired : 0}
                                maxHours={bookingType === 'hourly'
                                  ? ATTACHMENT_HOURS_SANITY_MAX
                                  : attachmentUsageMaxHours}
                                onChange={setAttachmentUsageHours}
                              />
                              <p style={{ color: '#8896a8', fontSize: '10px', margin: '6px 0 0', lineHeight: 1.35 }}>
                                {bookingType === 'hourly'
                                  ? 'Required. Attachment surcharge is charged only for this usage time. Total booking hours = bucket-only + attachment.'
                                  : 'Required. Cannot exceed total booked job duration. Attachment surcharge is charged only for this usage time.'}
                              </p>
                            </>
                          )}
                        </div>
                      ) : null}
                      {!attachmentDurationBlocked && attachmentMinHoursUiNotice ? (
                        <p style={{
                          color: attachmentMinHoursUiNotice.satisfied ? '#4CAF50' : '#FFB74D',
                          fontSize: '11px',
                          margin: '6px 0 0',
                          lineHeight: 1.4,
                        }}>
                          {attachmentMinHoursUiNotice.message}
                        </p>
                      ) : null}
                      {!attachmentDurationBlocked
                        && attachmentUsageHoursError
                        && !attachmentMinHoursUiNotice ? (
                        <p style={{ color: '#e94560', fontSize: '11px', margin: '6px 0 0', lineHeight: 1.4 }}>
                          {attachmentUsageHoursError}
                        </p>
                      ) : null}
                      {!attachmentDurationBlocked
                        && attachmentUsageHoursError
                        && attachmentMinHoursUiNotice
                        && attachmentUsageHoursError !== attachmentMinHoursUiNotice.message ? (
                          <p style={{ color: '#e94560', fontSize: '11px', margin: '6px 0 0', lineHeight: 1.4 }}>
                            {attachmentUsageHoursError}
                          </p>
                        ) : null}
                    </div>
                  )}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', color: '#8896a8', fontSize: '12px', marginBottom: '6px' }}>{t('bookingWorkDescription')}</label>
                    <textarea
                      value={workDescription}
                      onChange={(e) => setWorkDescription(e.target.value.slice(0, 1000))}
                      onBlur={() => setSiteFieldTouched((prev) => ({ ...prev, work: true }))}
                      placeholder={t('bookingWorkDescriptionPlaceholder')}
                      maxLength={1000}
                      rows={3}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 12px',
                        background: 'rgba(0,0,0,0.35)',
                        border: `1px solid ${(siteFieldTouched.work || siteBookAttempted) && workDescriptionError ? 'rgba(233,69,96,0.55)' : 'rgba(201,168,76,0.2)'}`,
                        borderRadius: '8px',
                        color: '#e8e0d0',
                        fontSize: '13px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                    {(siteFieldTouched.work || siteBookAttempted) && workDescriptionError ? (
                      <p style={{ color: '#e94560', fontSize: '11px', margin: '6px 0 0', lineHeight: 1.4 }}>
                        {workDescriptionError}
                      </p>
                    ) : null}
                  </div>
                  <div style={{ marginBottom: '15px', padding: '12px', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.15)' }}>
                    <p style={{ color: '#c9a84c', fontSize: '12px', fontWeight: '700', margin: '0 0 4px' }}>{t('siteLocationTitle')}</p>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 10px', lineHeight: 1.35 }}>
                      Required — address, 6-digit pincode, and GPS via Use Current Location.
                    </p>
                    <button
                      type="button"
                      disabled={siteGpsLoading}
                      onClick={() => {
                        setSiteFieldTouched((prev) => ({ ...prev, gps: true }));
                        handleUseCurrentLocation();
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        marginBottom: '10px',
                        background: siteGpsLoading ? 'rgba(201,168,76,0.08)' : 'rgba(201,168,76,0.12)',
                        color: '#c9a84c',
                        border: `1px solid ${(siteFieldTouched.gps || siteBookAttempted) && siteGpsRequiredError ? 'rgba(233,69,96,0.55)' : 'rgba(201,168,76,0.35)'}`,
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: siteGpsLoading ? 'wait' : 'pointer',
                        opacity: siteGpsLoading ? 0.7 : 1,
                      }}
                    >
                      {siteGpsLoading ? t('siteGpsLoading') : t('useCurrentLocation')}
                    </button>
                    {siteGpsError && (
                      <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 10px', lineHeight: 1.35 }}>{siteGpsError}</p>
                    )}
                    {(siteFieldTouched.gps || siteBookAttempted) && siteGpsRequiredError ? (
                      <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 10px', lineHeight: 1.35 }}>
                        {siteGpsRequiredError}
                      </p>
                    ) : null}
                    <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '4px' }}>{t('useSavedAddress')}</label>
                    <select
                      value={selectedSavedAddressId}
                      onChange={(e) => handleSelectSavedAddress(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 12px',
                        marginBottom: '10px',
                        background: '#030810',
                        border: '1px solid rgba(201,168,76,0.35)',
                        borderRadius: '8px',
                        color: '#C9A84C',
                        fontSize: '13px',
                        outline: 'none',
                        cursor: 'pointer',
                        colorScheme: 'dark',
                      }}
                    >
                      <option value="" style={{ background: '#030810', color: '#C9A84C' }}>{t('useSavedAddress')}</option>
                      {savedAddresses.map((row) => {
                        const id = String(row?.id || '');
                        if (!id) return null;
                        return (
                          <option key={id} value={id} style={{ background: '#030810', color: '#C9A84C' }}>
                            {savedAddressOptionLabel(row)}
                          </option>
                        );
                      })}
                    </select>
                    <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '4px' }}>{t('siteAddressLabel')}</label>
                    <input
                      type="text"
                      value={siteAddress}
                      onChange={handleSiteAddressChange}
                      onBlur={() => setSiteFieldTouched((prev) => ({ ...prev, address: true }))}
                      placeholder={t('siteAddressLabel')}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 12px',
                        marginBottom: (siteFieldTouched.address || siteBookAttempted) && siteAddressError ? '4px' : '10px',
                        background: 'rgba(0,0,0,0.35)',
                        border: `1px solid ${(siteFieldTouched.address || siteBookAttempted) && siteAddressError ? 'rgba(233,69,96,0.55)' : 'rgba(201,168,76,0.2)'}`,
                        borderRadius: '8px',
                        color: '#e8e0d0',
                        fontSize: '13px',
                      }}
                    />
                    {(siteFieldTouched.address || siteBookAttempted) && siteAddressError ? (
                      <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 10px', lineHeight: 1.4 }}>
                        {siteAddressError}
                      </p>
                    ) : null}
                    <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '4px' }}>{t('sitePincodeLabel')}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={sitePincode}
                      onChange={handleSitePincodeChange}
                      onBlur={() => setSiteFieldTouched((prev) => ({ ...prev, pincode: true }))}
                      placeholder={t('sitePincodeLabel')}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 12px',
                        background: 'rgba(0,0,0,0.35)',
                        border: `1px solid ${(siteFieldTouched.pincode || siteBookAttempted || String(sitePincode || '').length > 0) && sitePincodeError ? 'rgba(233,69,96,0.55)' : 'rgba(201,168,76,0.2)'}`,
                        borderRadius: '8px',
                        color: '#e8e0d0',
                        fontSize: '13px',
                      }}
                    />
                    {(siteFieldTouched.pincode || siteBookAttempted || String(sitePincode || '').length > 0) && sitePincodeError ? (
                      <p style={{ color: '#e94560', fontSize: '11px', margin: '6px 0 0', lineHeight: 1.4 }}>
                        {sitePincodeError}
                      </p>
                    ) : null}
                  </div>
                  <div style={{ borderRadius: '8px', padding: '10px', display: 'flex', justifyContent: 'space-between', marginBottom: '12px', border: walletBalance != null && walletBalance >= advanceAmt ? '1px solid rgba(76,175,80,0.4)' : '1px solid rgba(233,69,96,0.4)', background: walletBalance != null && walletBalance >= advanceAmt ? 'rgba(76,175,80,0.08)' : 'rgba(233,69,96,0.08)' }}>
                    <span style={{ fontSize: '12px' }}>{t('walletLabel').replace('{{amount}}', (walletBalance ?? 0).toLocaleString('en-IN'))}</span>
                    <span style={{ fontWeight: '700', color: walletBalance != null && walletBalance >= advanceAmt ? '#4CAF50' : '#e94560' }}>{walletBalance != null && walletBalance >= advanceAmt ? t('walletStatusOk') : t('walletStatusLow')}</span>
                  </div>
                  {(clientKycStatus === 'not_submitted' || clientKycStatus === 'pending' || clientKycStatus === 'rejected') ? (
                    <div style={{ margin: '0 0 12px', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(255,152,0,0.35)', background: 'rgba(255,152,0,0.08)' }}>
                      <p style={{ margin: '0 0 8px', color: '#FF9800', fontSize: isMobile ? '11px' : '12px', fontWeight: 600, lineHeight: 1.4 }}>
                        {clientKycBookingBlockMessage}
                      </p>
                      <button
                        type="button"
                        onClick={goToKycTab}
                        style={{ background: 'transparent', border: '1px solid rgba(255,152,0,0.5)', color: '#FF9800', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        {t('kycGoToTab')} →
                      </button>
                    </div>
                  ) : null}
                  {hireMode === 'contract' ? (
                    activeQuotation?.status === 'converted' ? (
                      (quotationArrivalSuccess || activeQuotation.mobilizationArrivalConfirmedAt) ? (
                        <button
                          type="button"
                          style={{ ...s.bookNowBtn, opacity: 0.85, cursor: 'default' }}
                          disabled
                        >
                          ✅ Arrival confirmed — Contract active
                        </button>
                      ) : (
                        <button
                          type="button"
                          style={{
                            ...s.bookNowBtn,
                            opacity: quotationArrivalLoading ? 0.7 : 1,
                          }}
                          disabled={quotationArrivalLoading}
                          onClick={handleConfirmQuotationArrival}
                        >
                          {quotationArrivalLoading ? 'Confirming arrival...' : 'Confirm Machine Arrival'}
                        </button>
                      )
                    ) : activeQuotation?.status === 'mobilization_confirmed' ? (
                      <button
                        type="button"
                        style={{ ...s.bookNowBtn, opacity: 0.85, cursor: 'default' }}
                        disabled
                      >
                        Confirm Machine Arrival →
                      </button>
                    ) : activeQuotation?.status === 'awaiting_mobilization'
                      || activeQuotation?.status === 'payment_collected'
                      || esignPaySuccess ? (
                      <button
                        type="button"
                        style={{ ...s.bookNowBtn, opacity: 0.85, cursor: 'default' }}
                        disabled
                      >
                        Machine dispatched — awaiting arrival at site
                      </button>
                    ) : activeQuotation?.status === 'client_esign' ? (
                      <button
                        type="button"
                        style={{
                          ...s.bookNowBtn,
                          opacity: (!esignTermsAccepted || quotationActionLoading || clientKycBookingBlocked) ? 0.7 : 1,
                        }}
                        disabled={!esignTermsAccepted || quotationActionLoading || clientKycBookingBlocked}
                        onClick={handleSignAndPayAdvance}
                        title={clientKycBookingBlocked ? clientKycBookingBlockMessage : undefined}
                      >
                        {quotationActionLoading ? 'Signing & paying...' : 'Sign & Pay Advance →'}
                      </button>
                    ) : activeQuotation?.status === 'owner_review' || activeQuotation?.status === 'admin_review' ? (
                      <button
                        type="button"
                        style={{ ...s.bookNowBtn, opacity: 0.85, cursor: 'default' }}
                        disabled
                      >
                        {activeQuotation.status === 'owner_review'
                          ? 'Awaiting owner response — 1 hour'
                          : 'Owner accepted — admin review'}
                      </button>
                    ) : (
                      <button
                        style={{
                          ...s.bookNowBtn,
                          opacity: (contractQuoteLoading || quotationActionLoading || clientKycBookingBlocked
                            || !siteDetailsValid
                            || (activeQuotation?.status === 'quoted' && !quotationTermsAccepted)
                            || (activeQuotation?.status === 'quoted' && activeQuotation?.ownerPaymentDueDay != null && !paymentDueDateAccepted)) ? 0.7 : 1,
                        }}
                        onClick={handleBook}
                        disabled={
                          contractQuoteLoading
                          || quotationActionLoading
                          || clientKycBookingBlocked
                          || !siteDetailsValid
                          || (activeQuotation?.status === 'quoted' && !quotationTermsAccepted)
                          || (activeQuotation?.status === 'quoted' && activeQuotation?.ownerPaymentDueDay != null && !paymentDueDateAccepted)
                        }
                        title={
                          clientKycBookingBlocked
                            ? clientKycBookingBlockMessage
                            : (!siteDetailsValid ? 'Complete required site details first' : undefined)
                        }
                      >
                        {contractQuoteLoading || quotationActionLoading
                          ? (activeQuotation ? 'Sending to owner...' : 'Requesting quotation...')
                          : activeQuotation?.status === 'quoted'
                            ? 'Accept terms & send to owner'
                            : 'Request quotation'}
                      </button>
                    )
                  ) : hireMode === 'site_work' ? (
                    <button
                      type="button"
                      style={{
                        ...s.bookNowBtn,
                        opacity: (siteWorkSubmitting || clientKycBookingBlocked || !siteWorkType || !noWaterClauseAccepted || !siteDetailsValid) ? 0.7 : 1,
                      }}
                      onClick={handleBook}
                      disabled={siteWorkSubmitting || clientKycBookingBlocked || !siteWorkType || !noWaterClauseAccepted || !siteDetailsValid}
                      title={
                        clientKycBookingBlocked
                          ? clientKycBookingBlockMessage
                          : (!siteWorkType
                            ? 'Select a site work type first'
                            : (!noWaterClauseAccepted
                              ? 'Accept the no-water clause first'
                              : (!siteDetailsValid ? 'Complete required site details first' : undefined)))
                      }
                    >
                      {siteWorkSubmitting ? 'Sending site work request...' : 'Request Site Work (25% advance)'}
                    </button>
                  ) : walletBalance != null && walletBalance >= advanceAmt ? (
                    <button
                      style={{
                        ...s.bookNowBtn,
                        opacity: (clientKycBookingBlocked || Boolean(attachmentMinHoursError) || !siteDetailsValid) ? 0.7 : 1,
                      }}
                      onClick={handleBook}
                      disabled={clientKycBookingBlocked || Boolean(attachmentMinHoursError) || !siteDetailsValid}
                      title={
                        clientKycBookingBlocked
                          ? clientKycBookingBlockMessage
                          : (attachmentMinHoursError
                            || (!siteDetailsValid ? 'Complete required site details first' : undefined))
                      }
                    >
                      {t('bookPayAdvance').replace('{{amount}}', advanceAmt.toLocaleString('en-IN'))}
                    </button>
                  ) : clientKycBookingBlocked ? (
                    <button
                      type="button"
                      style={{ width: '100%', padding: '12px', background: 'rgba(255,152,0,0.1)', color: '#FF9800', border: '1px solid rgba(255,152,0,0.4)', borderRadius: '10px', fontSize: '12px', cursor: 'pointer' }}
                      onClick={goToKycTab}
                    >
                      {t('kycGoToTab')} →
                    </button>
                  ) : (
                    <button style={{ width: '100%', padding: '12px', background: 'rgba(233,69,96,0.1)', color: '#e94560', border: '1px solid rgba(233,69,96,0.4)', borderRadius: '10px', fontSize: '12px', cursor: 'pointer' }} onClick={openRechargeForBooking}>
                      {t('rechargeShortfall').replace('{amount}', (advanceAmt - (walletBalance ?? 0)).toLocaleString('en-IN'))}
                    </button>
                  )}
                    </>
                  )}
                </div>
                <div className="box-border w-full min-w-0 md:mt-0" style={{ ...s.detailCard, padding: isMobile ? '12px' : '20px' }}>
                  <h3 style={s.calcTitle}>{t('machineDetailsTitle')}</h3>
                  <div>
                    {[
                      { label: t('machineIdLabel'), val: selectedMachine.code || selectedMachine.id },
                      { label: t('type'), val: selectedMachine.type },
                      { label: t('location'), val: selectedMachine.location },
                      { label: t('fuelLevel'), val: fuelDisplay(selectedMachine.fuel, selectedMachine.fuelCapacity) },
                      { label: t('minBooking'), val: `${selectedMachine?.minimumHours || 3} ${t('hrsShort')}` },
                      { label: t('condition'), val: selectedMachine.condition + '%' },
                      { label: t('operator'), val: selectedMachine.operator },
                      { label: t('experience'), val: t('yearsExperience').replace('{n}', selectedMachine.operatorExp) },
                      { label: t('rating'), val: selectedMachine.operatorRating ? '⭐ ' + selectedMachine.operatorRating : t('ratingNotAvailable') },
                    ].map((d, i) => (
                      <div key={i} className="flex flex-wrap items-start justify-between gap-2 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <span className="shrink-0 text-xs text-[#8896a8]">{d.label}</span>
                        <span className="break-words text-right text-xs font-semibold text-slate-200">{d.val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {bookingStep === 2 && !selectedMachine && (
              <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                <p style={{ fontSize: '48px', margin: '0 0 12px' }}>{String.fromCodePoint(0x1F69C)}</p>
                <p style={{ color: '#8896a8', marginBottom: '16px' }}>{t('bookingSelectMachineFirst')}</p>
                <button type="button" style={s.goBtn} onClick={() => setBookingStep(1)}>{t('bookingStepChoose')}</button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'mybookings' && (
          <div className="client-tab-panel w-full min-w-0 overflow-x-hidden">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {[
                { id: 'schedule', labelKey: 'subTabSchedule' },
                { id: 'history', labelKey: 'subTabHistory' },
                { id: 'analytics', labelKey: 'subTabAnalytics' },
              ].map(({ id, labelKey }) => {
                const active = myBookingsTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setMyBookingsTab(id)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${active ? '#c9a84c' : 'rgba(201,168,76,0.35)'}`,
                      background: active ? 'rgba(201,168,76,0.2)' : 'rgba(0,0,0,0.2)',
                      color: active ? '#c9a84c' : '#8896a8',
                      cursor: 'pointer',
                      fontWeight: active ? 700 : 500,
                      fontSize: '12px',
                    }}
                  >
                    {t(labelKey)}
                  </button>
                );
              })}
            </div>
            {myBookingsTab === 'schedule' && (
              <>
            {cancelledCredit && (
              <div style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: '10px', padding: '12px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                <p style={{ color: '#4CAF50', fontSize: '12px', margin: 0 }}>{t('cancelCreditBanner')
                  .replace('{{machine}}', cancelledCredit.machine)
                  .replace('{{amount}}',
                    cancelledCredit.credited.toLocaleString('en-IN'))}</p>
                <button style={{ background: 'transparent', border: 'none', color: '#8896a8', cursor: 'pointer', fontSize: '16px' }} onClick={() => setCancelledCredit(null)}>X</button>
              </div>
            )}
            <div style={{ ...s.cardRow, gridTemplateColumns: gridCols(2, 3, 4), marginBottom: '20px' }}>
              {[
                { icon: String.fromCodePoint(0x1F4CB), val: activeBookings.filter(b => !isBookingCancelled(b)).length.toString(), label: t('totalBookingsText') },
                { icon: String.fromCodePoint(0x2705), val: activeBookings.filter(b => b.owner_approved && !isBookingCancelled(b)).length.toString(), label: t('dispatched') },
                { icon: String.fromCodePoint(0x23F3), val: activeBookings.filter((b) => b.booking_type !== 'contract' && isBookingPendingDispatch(b)).length.toString(), label: t('pendingDispatch') },
                { icon: String.fromCodePoint(0x1F4B0), val: '₹' + activeBookings.filter(b => !isBookingCancelled(b)).reduce((a,b) => a + (b.advance_paid || b.advancePaid || 0), 0).toLocaleString('en-IN'), label: t('totalAdvanceLabel') },
              ].map((c, i) => (
                <div key={i} style={s.card}><p style={s.cardIcon}>{c.icon}</p><h3 style={s.cardVal}>{c.val}</h3><p style={s.cardLbl}>{c.label}</p></div>
              ))}
            </div>
            {activeBookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <p style={{ fontSize: '48px', margin: '0 0 12px' }}>{String.fromCodePoint(0x1F4CB)}</p>
                <p style={{ color: '#8896a8', marginBottom: '20px' }}>{t('noBookingsYet')}</p>
                <button style={s.goBtn} onClick={() => { setActiveTab('newbooking'); setBookingStep(1); }}>{t('bookMachineAction')}</button>
              </div>
            ) : (
              <div>
                <h3 style={{ color: '#c9a84c', fontSize: '14px', margin: '0 0 14px' }}>{t('bookingScheduleSummary')}</h3>
                {groupedBookingsSchedule.map(({ month, dayGroups }) => (
                  <ClientMonthAccordion
                    key={month}
                    month={month}
                    dayGroups={dayGroups}
                    isMobile={isMobile}
                    bookingGridCols={gridCols(2, 3, 4)}
                    s={s}
                    handleCancelRequest={handleCancelRequest}
                    t={t}
                    machineLookup={machineLookup}
                    onSubmitFeedback={handleSubmitBookingFeedback}
                    feedbackSubmittingId={feedbackSubmittingId}
                    onBookingRefresh={refreshClientBookings}
                    walletBalance={walletBalance}
                    onRecharge={openRechargeForBooking}
                    onDownloadInvoice={handleDownloadGstInvoice}
                    onBookAgain={handleBookAgain}
                    walletUser={CLIENT}
                    pendingTerminations={pendingTerminations}
                    onRequestTermination={(b) => {
                      setTerminationType('client');
                      setTerminationError('');
                      setTerminationModal(b);
                    }}
                    onRequestOwnerDefault={(b) => {
                      setOwnerDefaultType('no_show');
                      setOwnerDefaultBreakdownDays('');
                      setOwnerDefaultError('');
                      setOwnerDefaultSuccess('');
                      setOwnerDefaultModal(b);
                    }}
                    onOpenArrivalMilestone={(b) => {
                      setArrivalMilestoneError('');
                      setArrivalPhotoFile(null);
                      setArrivalMilestoneModal(b);
                    }}
                    arrivalMilestoneBusyId={arrivalMilestoneBusyId}
                  />
                ))}
                {cancelledBookingsList.length > 0 && (
                  <div style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                    <button
                      type="button"
                      onClick={() => setCancelledSectionOpen((prev) => !prev)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        padding: isMobile ? '10px 12px' : '12px 16px',
                        borderRadius: '10px',
                        border: '1px solid rgba(233,69,96,0.3)',
                        background: 'rgba(233,69,96,0.08)',
                        color: '#e94560',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 700,
                      }}
                    >
                      <span>{t('bookingClosed')} ({cancelledBookingsList.length})</span>
                      <span style={{ fontSize: '16px' }}>
                        {cancelledSectionOpen ? String.fromCodePoint(0x25B2) : String.fromCodePoint(0x25BC)}
                      </span>
                    </button>
                    {cancelledSectionOpen && (
                      <div style={{ marginTop: '12px' }}>
                        {cancelledBookingsSchedule.map(({ month, dayGroups }) => (
                          <ClientMonthAccordion
                            key={`cancelled-${month}`}
                            month={month}
                            dayGroups={dayGroups}
                            isMobile={isMobile}
                            bookingGridCols={gridCols(2, 3, 4)}
                            s={s}
                            handleCancelRequest={handleCancelRequest}
                            t={t}
                            machineLookup={machineLookup}
                            onSubmitFeedback={handleSubmitBookingFeedback}
                            feedbackSubmittingId={feedbackSubmittingId}
                            onBookingRefresh={refreshClientBookings}
                            walletBalance={walletBalance}
                            onRecharge={openRechargeForBooking}
                            walletUser={CLIENT}
                            pendingTerminations={pendingTerminations}
                            onRequestTermination={(b) => {
                              setTerminationType('client');
                              setTerminationError('');
                              setTerminationModal(b);
                            }}
                            onRequestOwnerDefault={(b) => {
                              setOwnerDefaultType('no_show');
                              setOwnerDefaultBreakdownDays('');
                              setOwnerDefaultError('');
                              setOwnerDefaultSuccess('');
                              setOwnerDefaultModal(b);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {bookingsHasMore && (
                  <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
                    <button
                      style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '8px 14px', cursor: bookingsLoadingMore ? 'wait' : 'pointer', opacity: bookingsLoadingMore ? 0.7 : 1 }}
                      disabled={bookingsLoadingMore}
                      onClick={loadMoreClientBookings}
                    >
                      {bookingsLoadingMore ? t('loading') : t('loadMoreBookings')}
                    </button>
                  </div>
                )}
              </div>
            )}
              </>
            )}
            {myBookingsTab === 'history' && (
            <div style={s.tableCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{ color: '#c9a84c', margin: 0, fontSize: '14px' }}>{t('bookingReportTitle')}</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={{ ...s.downloadBtn, opacity: reportRows.length > 0 ? 1 : 0.5 }}
                    disabled={reportRows.length === 0}
                    onClick={handleDownloadBookingReport}
                  >
                    Report PDF
                  </button>
                  <button
                    type="button"
                    style={{
                      ...s.downloadBtn,
                      opacity: activeBookings.some((b) => !isBookingCancelled(b)) ? 1 : 0.5,
                    }}
                    disabled={!activeBookings.some((b) => !isBookingCancelled(b))}
                    onClick={handleDownloadBookingHistoryPdf}
                  >
                    {String.fromCodePoint(0x1F4E5)} {t('downloadHistoryPdf')}
                  </button>
                </div>
              </div>
              {reportRows.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {[
                    { id: 'all', labelKey: 'filterAll' },
                    { id: 'completed', labelKey: 'filterCompleted' },
                    { id: 'cancelled', labelKey: 'filterCancelled' },
                    { id: 'pending', labelKey: 'filterPending' },
                    { id: 'disputed', labelKey: 'filterDisputed' },
                  ].map(({ id, labelKey }) => {
                    const active = reportStatusFilter === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setReportStatusFilter(id)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: `1px solid ${active ? '#c9a84c' : 'rgba(201,168,76,0.35)'}`,
                          background: active ? 'rgba(201,168,76,0.2)' : 'rgba(0,0,0,0.2)',
                          color: active ? '#c9a84c' : '#8896a8',
                          cursor: 'pointer',
                          fontWeight: active ? 700 : 500,
                          fontSize: '12px',
                        }}
                      >
                        {t(labelKey)}
                      </button>
                    );
                  })}
                </div>
              )}
              {reportRows.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', textAlign: 'center', margin: '20px 0' }}>{t('noBookingsAccount')}</p>
              ) : filteredReportRows.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '12px', textAlign: 'center', margin: '20px 0' }}>{t('clientAnalyticsNoData')}</p>
              ) : (
                <>
                  {groupedReportMonths.map(({ month, rows, total }) => {
                    const latestMonth = groupedReportMonths[0]?.month;
                    const isLatest = month === latestMonth;
                    const isOpen = isLatest ? !closedReportMonths.has(month) : closedReportMonths.has(month);
                    return (
                    <div
                      key={month}
                      style={{ marginBottom: '12px', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', overflow: 'hidden' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setClosedReportMonths((prev) => {
                            const next = new Set(prev);
                            if (next.has(month)) next.delete(month);
                            else next.add(month);
                            return next;
                          });
                        }}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '8px',
                          width: '100%',
                          padding: isMobile ? '12px 14px' : '14px 18px',
                          background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', minWidth: 0 }}>
                          <span style={{ color: '#c9a84c', fontWeight: 700, fontSize: '15px' }}>{month}</span>
                          <span style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '2px 10px', borderRadius: '20px', fontSize: '11px' }}>
                            {t('bookingsCount').replace('{count}', rows.length)}
                          </span>
                          <span style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 600 }}>
                            {t('monthTotalLabel')}: ₹{total.toLocaleString('en-IN')}
                          </span>
                        </div>
                        <span style={{ color: '#c9a84c', fontSize: '14px' }}>{isOpen ? '▼' : '▶'}</span>
                      </button>
                      {isOpen ? (
                        <div style={{ padding: '10px' }}>
                  <div className="flex w-full flex-col gap-3 md:hidden">
                    {rows.map((row) => {
                      const canInvoice = isGstInvoiceEligible(row.raw);
                      return (
                        <Card
                          key={row.id}
                          className="w-full border-de-gold/25 bg-gradient-to-br from-[#0f2040] to-[#0a1628] shadow-none"
                        >
                          <CardContent className="space-y-3 p-4">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <span className="break-words text-xs font-bold text-[#c9a84c]">{row.id}</span>
                              <span
                                className="rounded-full px-2 py-0.5 text-[10px]"
                                style={{
                                  background: row.status === t('statusCompleted') ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)',
                                  border: `1px solid ${row.status === t('statusCompleted') ? '#4CAF50' : '#FF9800'}`,
                                  color: row.status === t('statusCompleted') ? '#4CAF50' : '#FF9800',
                                }}
                              >
                                {row.status}
                              </span>
                            </div>
                            <p className="m-0 break-words text-[10px] text-[#8896a8]">{row.date} · {row.machine}</p>
                            <p className="m-0 break-words text-xs text-slate-200">
                              {t('totalWithGst').replace('{{total}}', row.total.toLocaleString('en-IN')).replace('{{gst}}', row.gst.toLocaleString('en-IN'))}
                            </p>
                            {canInvoice ? (
                              <button type="button" className="w-full" style={s.downloadBtn} onClick={() => handleDownloadGstInvoice(row.raw)}>{t('gstPdfBtn')}</button>
                            ) : (
                              <p className="m-0 text-[10px] text-[#8896a8]">{t('invoiceNotAvailableCancelled')}</p>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  <div className="hidden w-full md:block">
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ ...s.table, minWidth: '720px' }}>
                    <thead>
                      <tr>
                        {[t('tableId'), t('date'), t('machine'), t('type'), t('tableHrs'), t('tableBase'), t('tableGst'), t('total'), t('status'), t('invoiceCol')].map((h) => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const canInvoice = isGstInvoiceEligible(row.raw);
                        return (
                          <tr key={row.id} style={s.tr}>
                            <td style={{ ...s.td, color: '#c9a84c', fontSize: '11px' }}>{row.id}</td>
                            <td style={s.td}>{row.date}</td>
                            <td style={s.td}><span style={s.machineTag}>{row.machine}</span></td>
                            <td style={s.td}>{row.type}</td>
                            <td style={s.td}>{formatHoursHm(row.hours ?? 0)}</td>
                            <td style={s.td}>₹{row.baseAmt.toLocaleString('en-IN')}</td>
                            <td style={s.td}>₹{row.gst.toLocaleString('en-IN')}</td>
                            <td style={{ ...s.td, color: '#c9a84c', fontWeight: '700' }}>₹{row.total.toLocaleString('en-IN')}</td>
                            <td style={s.td}>
                              <span style={{ background: row.status === t('statusCompleted') ? 'rgba(76,175,80,0.15)' : 'rgba(255,152,0,0.15)', border: `1px solid ${row.status === t('statusCompleted') ? '#4CAF50' : '#FF9800'}`, color: row.status === t('statusCompleted') ? '#4CAF50' : '#FF9800', padding: '3px 8px', borderRadius: '20px', fontSize: '10px' }}>
                                {row.status}
                              </span>
                            </td>
                            <td style={s.td}>
                              {canInvoice ? (
                                <button type="button" style={s.downloadBtn} onClick={() => handleDownloadGstInvoice(row.raw)}>{t('gstPdfBtn')}</button>
                              ) : (
                                <span style={{ color: '#8896a8', fontSize: '10px' }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                  </div>
                        </div>
                      ) : null}
                    </div>
                    );
                  })}
                </>
              )}
            </div>
            )}
            {myBookingsTab === 'analytics' && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
                <h3 style={{ color: '#c9a84c', margin: 0, fontSize: '16px', fontWeight: 700 }}>{t('clientAnalyticsTitle')}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    { months: 3, labelKey: 'analytics3Months' },
                    { months: 6, labelKey: 'analytics6Months' },
                    { months: 12, labelKey: 'analytics12Months' },
                  ].map(({ months, labelKey }) => {
                    const active = analyticsMonths === months;
                    return (
                      <button
                        key={months}
                        type="button"
                        onClick={() => setAnalyticsMonths(months)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: `1px solid ${active ? '#c9a84c' : 'rgba(201,168,76,0.35)'}`,
                          background: active ? 'rgba(201,168,76,0.2)' : 'rgba(0,0,0,0.2)',
                          color: active ? '#c9a84c' : '#8896a8',
                          cursor: 'pointer',
                          fontWeight: active ? 700 : 500,
                          fontSize: '12px',
                        }}
                      >
                        {t(labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ ...s.cardRow, gridTemplateColumns: gridCols(2, 3, 6), marginBottom: '16px' }}>
                {analyticsLoading ? (
                  <div style={{ ...s.card, gridColumn: '1 / -1' }}>
                    <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
                  </div>
                ) : analyticsError ? (
                  <div style={{ ...s.card, gridColumn: '1 / -1' }}>
                    <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 10px' }}>{analyticsError}</p>
                    <button type="button" style={s.downloadBtn} onClick={loadClientAnalytics}>
                      {t('retryLabel')}
                    </button>
                  </div>
                ) : (
                  [
                    { icon: '💰', val: fmtClientInr(clientAnalytics?.summary?.total_spend), label: t('clientAnalyticsSpend') },
                    { icon: '⏱️', val: formatHoursHm(clientAnalytics?.summary?.total_hours ?? 0), label: t('clientAnalyticsHours') },
                    { icon: '📋', val: `${clientAnalytics?.summary?.total_bookings ?? 0}`, label: t('clientAnalyticsBookings') },
                    { icon: '🚜', val: `${clientAnalytics?.summary?.machines_used ?? 0}`, label: t('clientAnalyticsMachines') },
                    { icon: '📊', val: fmtClientInr(clientAnalytics?.summary?.avg_spend_per_booking), label: t('clientAnalyticsAvgSpend') },
                    { icon: '📄', val: String(analyticsInvoiceCount), label: t('gstInvoices') },
                  ].map((c, i) => (
                    <div key={i} style={s.card}>
                      <p style={s.cardIcon}>{c.icon}</p>
                      <h3 style={{ ...s.cardVal, fontSize: isMobile ? '14px' : '18px' }}>{c.val}</h3>
                      <p style={s.cardLbl}>{c.label}</p>
                    </div>
                  ))
                )}
              </div>

              <div style={{ ...s.tableCard, marginBottom: '16px' }}>
                <h3 style={{ ...s.tableTitle, marginBottom: '12px' }}>📈 {t('clientAnalyticsMonthlyTrend')}</h3>
                {analyticsLoading ? (
                  <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('loading')}</p>
                ) : analyticsError ? (
                  <p style={{ color: '#e94560', fontSize: '12px', margin: 0 }}>{analyticsError}</p>
                ) : analyticsTrendSeries.length === 0 || Number(clientAnalytics?.summary?.total_bookings || 0) === 0 ? (
                  <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>{t('clientAnalyticsNoData')}</p>
                ) : (
                  <div ref={clientChartWrapRef} className="w-full min-w-0" style={{ height: CLIENT_CHART_HEIGHT }}>
                    <BarChart
                      width={clientChartWidth}
                      height={CLIENT_CHART_HEIGHT}
                      data={analyticsTrendSeries}
                      margin={{ top: 12, right: 12, left: 4, bottom: 4 }}
                      barCategoryGap="20%"
                    >
                      <defs>
                        <linearGradient id="clientSpendBarGoldGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={CLIENT_CHART_GOLD_LIGHT} />
                          <stop offset="100%" stopColor={CLIENT_CHART_GOLD} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={CLIENT_CHART_GRID} strokeDasharray="4 4" vertical={false} />
                      <XAxis dataKey="month" tick={{ fill: CLIENT_CHART_AXIS, fontSize: 11 }} axisLine={{ stroke: CLIENT_CHART_GRID }} tickLine={false} />
                      <YAxis
                        tick={{ fill: CLIENT_CHART_AXIS, fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatClientChartSpend}
                        domain={[0, analyticsMaxSpend * 1.1]}
                      />
                      <Tooltip content={<ClientAnalyticsTooltip bookingsLabel={t('clientAnalyticsBookings')} />} />
                      <Bar dataKey="spend" fill="url(#clientSpendBarGoldGrad)" radius={[8, 8, 0, 0]} maxBarSize={56} isAnimationActive={false} />
                    </BarChart>
                  </div>
                )}
              </div>

              {!analyticsLoading && !analyticsError && topAnalyticsMachines.length > 0 && (
                <div style={{ ...s.tableCard, marginBottom: '16px' }}>
                  <h3 style={{ ...s.tableTitle, marginBottom: '12px' }}>🚜 {t('clientAnalyticsTopMachines')}</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ ...s.table, minWidth: '640px' }}>
                      <thead>
                        <tr>
                          {[t('machine'), t('analyticsBookings'), t('clientAnalyticsSpend'), t('clientAnalyticsHours')].map((h) => (
                            <th key={h} style={s.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {topAnalyticsMachines.map((row) => {
                          const machineLabel = row.machine_name
                            ? `${row.machine_id} · ${row.machine_name}`
                            : (row.machine_id || '—');
                          return (
                            <tr key={row.machine_id || machineLabel} style={s.tr}>
                              <td style={{ ...s.td, color: '#c9a84c', fontWeight: 700 }}>{machineLabel}</td>
                              <td style={s.td}>{row.bookings_count ?? 0}</td>
                              <td style={{ ...s.td, color: '#c9a84c', fontWeight: 700 }}>{fmtClientInr(row.total_spend)}</td>
                              <td style={s.td}>{formatHoursHm(row.total_hours ?? 0)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        )}
        {activeTab === 'tracking' && (
          <div className="client-tab-panel box-border w-full min-w-0 overflow-x-hidden" style={{ background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: isMobile ? '16px' : '20px' }}>
            {trackingBooking ? (
              <>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <h3 style={{ color: '#c9a84c', margin: 0 }}>{t('liveMachineLocation')}</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                    <span style={{
                      background: trackingBooking.status === 'Dispatched'
                        ? 'rgba(76,175,80,0.15)'
                        : trackingBooking.status === 'Extension_Pending'
                          ? 'rgba(255,152,0,0.15)'
                          : 'rgba(33,150,243,0.15)',
                      border: `1px solid ${trackingBooking.status === 'Dispatched' ? '#4CAF50' : trackingBooking.status === 'Extension_Pending' ? '#FF9800' : '#2196F3'}`,
                      color: trackingBooking.status === 'Dispatched' ? '#4CAF50' : trackingBooking.status === 'Extension_Pending' ? '#FF9800' : '#2196F3',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                    >
                      {String(trackingBooking.status || '').replace(/_/g, ' ')}
                    </span>
                    {bookingNeedsLiveSync(trackingBooking) && (
                      <span style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid #4CAF50', color: '#4CAF50', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{t('gpsLiveLabel')}</span>
                    )}
                  </div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '8px', padding: '14px', border: '1px solid rgba(201,168,76,0.15)', marginBottom: '14px' }}>
                  <p style={{ color: '#e8e0d0', fontSize: '13px', fontWeight: 700, margin: '0 0 8px' }}>
                    {String.fromCodePoint(0x1F69C)} {trackingBooking.machines?.machine_id || trackingBooking.machines?.name || t('machine')}
                  </p>
                  <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 6px' }}>
                    {t('bookingRefLabel')}: <span style={{ color: '#c9a84c' }}>{trackingBooking.booking_ref || '—'}</span>
                  </p>
                  <p style={{ color: '#e8e0d0', fontSize: '12px', margin: '0 0 6px', lineHeight: 1.4 }}>
                    {String.fromCodePoint(0x1F4CD)} {trackingBooking.site_address || t('siteLocationNotAvailable')}
                  </p>
                  {Number.isFinite(Number(trackingBooking.site_lat)) && Number.isFinite(Number(trackingBooking.site_lng)) && (
                    <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 10px' }}>
                      {Number(trackingBooking.site_lat).toFixed(5)}°, {Number(trackingBooking.site_lng).toFixed(5)}°
                    </p>
                  )}
                  {Number.isFinite(Number(trackingBooking.site_lat)) && Number.isFinite(Number(trackingBooking.site_lng)) && (
                    <a
                      href={`https://www.google.com/maps?q=${encodeURIComponent(trackingBooking.site_lat)},${encodeURIComponent(trackingBooking.site_lng)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        padding: '10px 14px',
                        background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
                        color: '#0a1628',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '12px',
                        textDecoration: 'none',
                      }}
                    >
                      {t('navigateToSite')}
                    </a>
                  )}
                </div>
                <div className="grid w-full grid-cols-2 gap-2.5 md:grid-cols-3">
                  {[
                    { icon: '⏱️', label: t('hmrToday'), val: `${(runningElapsedMs(trackingBooking) / 3600000).toFixed(1)} ${t('hrsUnit')}` },
                    {
                      icon: '⛽',
                      label: t('fuel'),
                      val: trackingBooking.machines?.fuel_level != null
                        ? fuelDisplay(trackingBooking.machines.fuel_level, trackingBooking.machines?.fuel_capacity_litres)
                        : '--',
                      note: t('fuelLastOperatorUpdate'),
                    },
                    { icon: String.fromCodePoint(0x1F477), label: t('operatorLabel') || 'Operator', val: trackingBooking.operator?.name || '--' },
                  ].map((item, i) => (
                    <div key={i} className="box-border w-full min-w-0" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <p style={{ fontSize: '18px', margin: '0 0 4px' }}>{item.icon}</p>
                      <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 2px' }}>{item.label}</p>
                      <p style={{ color: '#c9a84c', fontWeight: '700', fontSize: '13px', margin: 0 }}>{item.val}</p>
                      {item.note && (
                        <p style={{ color: '#556070', fontSize: '9px', margin: '4px 0 0' }}>{item.note}</p>
                      )}
                    </div>
                  ))}
                </div>
                <p style={{ color: '#556070', fontSize: '10px', textAlign: 'center', margin: '12px 0 0' }}>{t('trackingUpdatedEvery5s')}</p>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: isMobile ? '24px 12px' : '40px 20px' }}>
                <p style={{ fontSize: '40px', margin: '0 0 12px' }}>{String.fromCodePoint(0x1F69C)}</p>
                <p style={{ color: '#e8e0d0', fontSize: '15px', fontWeight: 700, margin: '0 0 8px' }}>{t('noMachineRunning')}</p>
                <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 16px', lineHeight: 1.5 }}>
                  {t('trackingEmptyHint')}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab('mybookings')}
                  style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
                >
                  {t('myBookings')} →
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'wallet' && (
          <div className="client-tab-panel client-wallet-grid w-full min-w-0 overflow-x-hidden">
            <div style={{
              background: 'linear-gradient(135deg, #0d1f3c, #091525)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: 20,
              padding: isMobile ? '28px 20px' : '28px 24px',
              marginBottom: 12,
              position: 'relative',
              overflow: 'hidden',
            }}
            >
              <div style={{
                position: 'absolute',
                width: 140,
                height: 140,
                top: -40,
                right: -40,
                borderRadius: '50%',
                background: 'rgba(201,168,76,0.04)',
                pointerEvents: 'none',
              }}
              />
              <div style={{
                position: 'absolute',
                width: 80,
                height: 80,
                top: -20,
                right: -20,
                borderRadius: '50%',
                background: 'rgba(201,168,76,0.06)',
                pointerEvents: 'none',
              }}
              />
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#8896a8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Available Balance
                  </div>
                  <button
                    type="button"
                    onClick={() => setWalletVisible((v) => !v)}
                    aria-label={walletVisible ? 'Hide wallet balance' : 'Show wallet balance'}
                    aria-pressed={walletVisible}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.06)',
                      border: 'none',
                      color: '#c9a84c',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 13,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    👁
                  </button>
                </div>
                <div style={{
                  fontSize: 38,
                  fontWeight: 700,
                  color: '#C9A84C',
                  letterSpacing: '-0.02em',
                  marginBottom: 4,
                  wordBreak: 'break-word',
                  fontVariantNumeric: 'tabular-nums',
                }}
                >
                  {walletBalance === null ? '₹...' : walletVisible ? fmtClientInr(walletBalance) : '₹ ••••••'}
                </div>
                <div style={{ fontSize: 12, color: '#8896a8', marginBottom: 24 }}>
                  {clientDisplayName}{CLIENT.phone ? ` · ${CLIENT.phone}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => walletRechargeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    style={{
                      flex: 1,
                      padding: 12,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, #C9A84C, #a8893e)',
                      border: 'none',
                      color: '#0a1628',
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: 'pointer',
                      minHeight: 44,
                    }}
                  >
                    + {t('addMoney') === 'addMoney' ? 'Add Money' : t('addMoney')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPhoneChangeModal(true)}
                    style={{
                      padding: '12px 16px',
                      borderRadius: 12,
                      background: 'transparent',
                      border: '1px solid rgba(201,168,76,0.3)',
                      color: '#C9A84C',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      minHeight: 44,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Change Number
                  </button>
                </div>
              </div>
            </div>

            <div
              ref={walletRechargeRef}
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 20,
                padding: 20,
                marginBottom: 12,
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box',
              }}
            >
              <WalletRechargePanel
                t={t}
                userId={CLIENT.id || CLIENT.user_id}
                userName={clientName}
                userEmail={CLIENT.email || 'contact@developmentexpress.in'}
                userPhone={CLIENT.phone || '+918408000084'}
                rechargeAmt={rechargeAmt}
                setRechargeAmt={setRechargeAmt}
                onBalanceUpdated={handleWalletCredited}
                isMobile={walletCompact}
              />
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 20,
              padding: 20,
              minWidth: 0,
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
            >
              <ClientTransactionHistory
                transactions={filteredTransactions}
                t={t}
                compact={walletCompact}
                hasMore={txHasMore}
                loadingMore={txLoadingMore}
                onLoadMore={loadMoreClientTransactions}
                filterControls={(
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#8896a8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Transactions
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {[
                        { id: 'all', label: t('filterAll') },
                        { id: 'credit', label: t('filterCredit') },
                        { id: 'debit', label: t('filterDebit') },
                      ].map((opt) => {
                        const active = transactionFilter === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setTransactionFilter(opt.id)}
                            style={{
                              padding: '8px 14px',
                              borderRadius: 8,
                              border: `1px solid ${active ? '#c9a84c' : 'rgba(201,168,76,0.35)'}`,
                              background: active ? 'rgba(201,168,76,0.2)' : 'rgba(0,0,0,0.2)',
                              color: active ? '#c9a84c' : '#8896a8',
                              cursor: 'pointer',
                              fontWeight: active ? 700 : 500,
                              fontSize: 12,
                              minHeight: 36,
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {activeTab === 'kyc' && (
          <ClientKycForm
            authToken={localStorage.getItem('developmentexpress_token') || ''}
            apiBaseUrl={API_BASE_URL}
            onStatusChange={setClientKycStatus}
          />
        )}

        {activeTab === 'alerts' && (
          <div className="client-tab-panel w-full min-w-0 overflow-x-hidden">
            <PushNotificationToggle />
            <div style={s.tableCard}>
              <h3 style={s.tableTitle}>{String.fromCodePoint(0x1F514)} {t('allAlerts')}</h3>
              {clientAlerts.length === 0 ? (
                <p style={{ color: '#8896a8', fontSize: '13px', textAlign: 'center', padding: '24px 12px' }}>{t('noAlertsNow')}</p>
              ) : (
                clientAlerts.map((a, i) => (
                  <div
                    key={i}
                    role={a.tab ? 'button' : undefined}
                    tabIndex={a.tab ? 0 : undefined}
                    onClick={a.tab ? () => setActiveTab(a.tab) : undefined}
                    onKeyDown={a.tab ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(a.tab); } } : undefined}
                    style={{
                      ...s.alertRow,
                      borderLeft: `3px solid ${a.color}`,
                      marginBottom: '8px',
                      ...(a.tab ? { cursor: 'pointer' } : {}),
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{a.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ color: '#e8e0d0', fontSize: '13px', margin: '0 0 3px' }}>{a.msg}</p>
                      <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{a.time}</p>
                    </div>
                  </div>
                ))
              )}
              <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p style={{ color: '#8896a8', fontSize: '11px', margin: 0, lineHeight: 1.5 }}>{t('directContactProhibitedText')}</p>
              </div>
            </div>
            <div style={{ height: '1px', background: 'rgba(201,168,76,0.25)', margin: '18px 0' }} />
            <NotificationCenter
              authToken={localStorage.getItem('developmentexpress_token') || ''}
              apiEndpoint="/api/bookings/notifications"
              title={t('notificationCenter')}
            />
          </div>
        )}

        {activeTab === 'terms' && (
          <div className="client-tab-panel w-full min-w-0 overflow-x-hidden">
            <div style={{ ...s.tableCard, marginBottom: '15px' }}>
              <h3 style={s.tableTitle}>{String.fromCodePoint(0x1F4DC)} {t('tacTitle')}</h3>
              <p style={{ color: '#8896a8', fontSize: '12px', margin: '0 0 10px', lineHeight: 1.5 }}>
                {t('tacIntro')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                {[
                  { label: t('termsOfService') !== 'termsOfService' ? t('termsOfService') : 'Terms of Service', href: '/terms' },
                  { label: t('tacPrivacy'), href: '/privacy' },
                  { label: 'Delete account', href: '/delete-account' },
                  { label: t('tacRefund'), href: '/refund-policy' },
                  { label: t('tacCancellation'), href: '/cancellation-policy' },
                  { label: t('tacServiceDelivery'), href: '/service-delivery-policy' },
                  { label: 'Grievance Redressal', href: '/grievance' },
                ].map((item) => (
                  <div
                    key={item.href}
                    onClick={() => { sessionStorage.setItem('returnTab', activeTab); navigate(item.href); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { sessionStorage.setItem('returnTab', activeTab); navigate(item.href); } }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '10px', padding: '12px' }}>
                      <p style={{ color: '#c9a84c', fontWeight: 700, fontSize: '13px', margin: 0 }}>{item.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="client-tab-panel w-full min-w-0 overflow-x-hidden">
            <div style={{ ...s.tableCard, marginBottom: '15px' }}>
              <h3 style={s.tableTitle}>{String.fromCodePoint(0x1F198)} {t('contactCompany')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <a href="tel:+918408000084" style={{ textDecoration: 'none' }}>
                  <div style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer' }}>
                    <p style={{ fontSize: '28px', margin: '0 0 8px' }}>{String.fromCodePoint(0x1F4DE)}</p>
                    <p style={{ color: '#4CAF50', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('callNow')}</p>
                    <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>+91-8408000084</p>
                  </div>
                </a>
                <a href="https://wa.me/918408000084" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                  <div style={{ background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer' }}>
                    <p style={{ fontSize: '28px', margin: '0 0 8px' }}>{String.fromCodePoint(0x1F4AC)}</p>
                    <p style={{ color: '#25D366', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('whatsapp')}</p>
                    <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>+91-8408000084</p>
                  </div>
                </a>
                <a href="mailto:om.chavan2026@zohomail.in" style={{ textDecoration: 'none' }}>
                  <div style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer' }}>
                    <p style={{ fontSize: '28px', margin: '0 0 8px' }}>{String.fromCodePoint(0x1F4E7)}</p>
                    <p style={{ color: '#c9a84c', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('emailLabel')}</p>
                    <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>om.chavan2026@zohomail.in</p>
                  </div>
                </a>
                <div style={{ background: 'rgba(21,101,192,0.1)', border: '1px solid rgba(21,101,192,0.3)', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                  <p style={{ fontSize: '28px', margin: '0 0 8px' }}>{String.fromCodePoint(0x1F4CD)}</p>
                  <p style={{ color: '#1565C0', fontWeight: '700', fontSize: '14px', margin: '0 0 4px' }}>{t('officeLabel')}</p>
                  <p style={{ color: '#8896a8', fontSize: '12px', margin: 0 }}>Karad, Satara - 415110</p>
                </div>
              </div>
            </div>
            <div style={s.tableCard}>
              <h3 style={s.tableTitle}>{String.fromCodePoint(0x1F4CB)} {t('clientPoliciesTitle')}</h3>
              {[
                { label: t('policyTopicBooking'), val: t('supportPolicyBooking') },
                { label: t('policyTopicCancellation'), val: t('supportPolicyCancellation') },
                { label: t('policyTopicContact'), val: t('supportPolicyContact') },
                { label: t('policyTopicGst'), val: t('supportPolicyGst') },
                { label: t('policyTopicHours'), val: t('supportPolicyHours') },
              ].map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap' }}>
                  <span style={{ color: '#8896a8', fontSize: '12px' }}>{d.label}</span>
                  <span style={{ color: '#e8e0d0', fontWeight: '600', fontSize: '12px', textAlign: 'right', maxWidth: '62%' }}>{d.val}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'account' && (
          <div className="client-tab-panel w-full min-w-0 overflow-x-hidden">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(() => {
                const tr = (key, fallback) => {
                  const value = t(key);
                  return value && value !== key ? value : fallback;
                };
                const ACCOUNT_HUB = [
                  {
                    id: 'kyc',
                    icon: '🪪',
                    label: tr('kycVerification', 'KYC Verification'),
                    desc: tr('kycVerificationDesc', 'Identity documents and verification status'),
                  },
                  {
                    id: 'alerts',
                    icon: '🔔',
                    label: tr('alertsAndNotifications', 'Alerts'),
                    desc: tr('alertsDesc', 'Booking updates and compliance alerts'),
                    badge: clientAlerts.length > 0
                      ? (clientAlerts.length > 99 ? '99+' : clientAlerts.length)
                      : null,
                  },
                  {
                    id: 'terms',
                    icon: '📋',
                    label: tr('policiesAndTerms', 'Policies & Terms'),
                    desc: tr('policiesDesc', 'Terms of service, privacy and refund policies'),
                  },
                  {
                    id: 'support',
                    icon: '💬',
                    label: tr('support', 'Support'),
                    desc: tr('supportDesc', 'Help centre and contact us'),
                  },
                ];
                return ACCOUNT_HUB.map((item) => (
                  <div
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleClientNavTab(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClientNavTab(item.id);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      padding: '16px 18px',
                      borderRadius: 14,
                      border: '1px solid rgba(255,255,255,0.07)',
                      background: 'rgba(255,255,255,0.02)',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                      boxSizing: 'border-box',
                      width: '100%',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                  >
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 12,
                        background: 'rgba(201,168,76,0.08)',
                        border: '1px solid rgba(201,168,76,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#e8e0d0' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: 12, color: '#8896a8', marginTop: 3 }}>
                        {item.desc}
                      </div>
                    </div>
                    {item.badge ? (
                      <div
                        style={{
                          minWidth: 20,
                          height: 20,
                          borderRadius: 10,
                          background: '#e94560',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0 6px',
                          flexShrink: 0,
                        }}
                      >
                        {item.badge}
                      </div>
                    ) : null}
                    <div style={{ color: '#8896a8', fontSize: 16, flexShrink: 0 }}>→</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}
      </div>
      </div>

      {showQuotationResult && contractQuote && (
        <div style={s.modalOverlay} onClick={() => setShowQuotationResult(false)}>
          <div
            style={{
              ...s.modal,
              width: isMobile ? '100%' : '430px',
              maxHeight: '90dvh',
              overflowY: 'auto',
              userSelect: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <div>
                <h3 style={{ ...s.modalTitle, textAlign: 'left', margin: 0 }}>Contract Quotation</h3>
                <p style={{ color: '#c9a84c', fontSize: '13px', fontWeight: 700, margin: '6px 0 0' }}>
                  {activeQuotation?.quotationRef || activeQuotation?.id || '—'}
                </p>
                {activeQuotation?.id && (
                  <span
                    style={{
                      display: 'inline-block',
                      marginTop: '8px',
                      background: 'rgba(201,168,76,0.12)',
                      border: '1px solid rgba(201,168,76,0.5)',
                      borderRadius: '8px',
                      color: '#C9A84C',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '6px 12px',
                    }}
                  >
                    {billingBasisChipLabel(activeQuotation.billingBasis || billingBasis)}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowQuotationResult(false)}
                aria-label="Close quotation"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(201,168,76,0.4)',
                  color: '#c9a84c',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  fontSize: '20px',
                  lineHeight: 1,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
            <div style={s.modalDetail}>
              {renderContractQuoteBreakdown()}
            </div>
            {activeQuotation?.status === 'quoted' && (
              <>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={quotationTermsAccepted}
                    onChange={(e) => setQuotationTermsAccepted(e.target.checked)}
                    style={{ marginTop: '2px' }}
                  />
                  <span style={{ color: '#e8e0d0', fontSize: '11px', lineHeight: 1.4 }}>
                    I accept the contract hire terms and authorize sending this quotation to the machine owner for review.
                  </span>
                </label>
                {activeQuotation.ownerPaymentDueDay != null && (
                  <>
                    <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginTop: '10px' }}>
                      Propose alternate day (optional)
                      <input
                        type="number"
                        min="1"
                        max="28"
                        step="1"
                        value={clientPaymentDueDayProposal}
                        onChange={(e) => setClientPaymentDueDayProposal(e.target.value)}
                        placeholder={`${activeQuotation.ownerPaymentDueDay} (owner day)`}
                        style={{
                          marginTop: '6px',
                          width: '100%',
                          borderRadius: '8px',
                          border: '1px solid rgba(201,168,76,0.3)',
                          background: '#020617',
                          color: '#e8e0d0',
                          padding: '7px 10px',
                          fontSize: '12px',
                        }}
                      />
                      <p style={{ color: '#8896a8', fontSize: '10px', margin: '6px 0 0' }}>
                        Leave blank to accept owner day {activeQuotation.ownerPaymentDueDay} of each month. Range 1–28.
                      </p>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={paymentDueDateAccepted}
                        onChange={(e) => setPaymentDueDateAccepted(e.target.checked)}
                        style={{ marginTop: '2px' }}
                      />
                      <span style={{ color: '#e8e0d0', fontSize: '11px', lineHeight: 1.4 }}>
                        I agree to the payment due date
                      </span>
                    </label>
                  </>
                )}
                {contractQuoteError ? (
                  <p style={{ color: '#ef5350', fontSize: '11px', margin: '10px 0 0' }}>{contractQuoteError}</p>
                ) : null}
                <button
                  type="button"
                  style={{
                    ...s.bookNowBtn,
                    marginTop: '14px',
                    marginBottom: 0,
                    opacity: (quotationActionLoading || clientKycBookingBlocked
                      || !quotationTermsAccepted
                      || (activeQuotation.ownerPaymentDueDay != null && !paymentDueDateAccepted)) ? 0.7 : 1,
                  }}
                  onClick={handleAcceptTermsAndSendQuotation}
                  disabled={
                    quotationActionLoading
                    || clientKycBookingBlocked
                    || !quotationTermsAccepted
                    || (activeQuotation.ownerPaymentDueDay != null && !paymentDueDateAccepted)
                  }
                  title={clientKycBookingBlocked ? clientKycBookingBlockMessage : undefined}
                >
                  {quotationActionLoading ? 'Sending to owner...' : 'Accept terms & send to owner'}
                </button>
                {activeQuotation?.status === 'quoted' && (
                  <button
                    type="button"
                    onClick={handleCancelQuotation}
                    disabled={quotationActionLoading}
                    style={{
                      marginTop: 8, width: '100%', padding: '10px 16px',
                      borderRadius: 8, border: '1px solid rgba(233,69,96,0.4)',
                      background: 'rgba(233,69,96,0.08)', color: '#e94560',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {quotationActionLoading ? 'Cancelling...' : 'Cancel Quotation'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showPayment && selectedMachine && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '95%' : '430px' }}>
            <h3 style={s.modalTitle}>{t('confirmBookingAction')}</h3>
            <div style={s.modalDetail}>
              {[
                { label: t('machine'), val: selectedMachine.name },
                {
                  label: t('bookingTypeLabel'),
                  val: hireMode === 'contract'
                    ? 'CONTRACT'
                    : bookingType.toUpperCase(),
                },
                ...(hireMode === 'contract' && contractQuote ? [
                  {
                    label: 'Booking Advance (15% of A)',
                    val: '₹' + Number(displayedQuote.hireAdvance || 0).toLocaleString('en-IN'),
                    gold: true,
                  },
                  ...(displayedQuote.mobilizationCollected ? [{
                    label: 'Mobilization Charge (100% of B)',
                    val: '₹' + Number(displayedQuote.mobilizationTotal || 0).toLocaleString('en-IN'),
                    gold: true,
                  }] : []),
                  {
                    label: 'Total Payable Now',
                    val: '₹' + advanceAmt.toLocaleString('en-IN'),
                    gold: true,
                  },
                ] : [
                  { label: t('confirmAdvanceLabel'), val: '₹' + advanceAmt.toLocaleString('en-IN'), gold: true },
                ]),
                { label: t('confirmWalletAfterLabel'), val: '₹' + ((walletBalance ?? 0) - advanceAmt).toLocaleString('en-IN'), green: true },
              ].map((r, i) => (
                <div key={i} style={s.modalRow}>
                  <span style={{ color: '#8896a8' }}>{r.label}</span>
                  <span style={{ color: r.gold ? '#c9a84c' : r.green ? '#4CAF50' : '#e8e0d0', fontWeight: r.gold || r.green ? '700' : '400' }}>{r.val}</span>
                </div>
              ))}
            </div>
            <p style={{ color: '#FF9800', fontSize: '11px', textAlign: 'center', marginBottom: '15px' }}>
              {hireMode === 'contract'
                ? 'Total Payable Now will be deducted from your wallet.'
                : t('advanceDeductedFromWallet')}
            </p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: '10px' }}>
              <button style={{ ...s.cancelBtn, width: isMobile ? '100%' : undefined, minHeight: isMobile ? '44px' : undefined }} onClick={closePaymentModal} disabled={bookingSubmitting}>{t('cancelText')}</button>
              <button
                style={{ ...s.confirmBtn, width: isMobile ? '100%' : undefined, minHeight: isMobile ? '44px' : undefined }}
                onClick={handleConfirmBooking}
                disabled={bookingSubmitting || clientKycBookingBlocked}
                title={clientKycBookingBlocked ? clientKycBookingBlockMessage : undefined}
              >
                {bookingSubmitting ? t('processingLabel') : t('confirmBookingAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRecharge && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '95%' : '430px' }}>
            <h3 style={s.modalTitle}>{t('walletRechargeTitle')}</h3>
            <p style={{ color: '#8896a8', textAlign: 'center', marginBottom: '15px' }}>{t('currentWalletBalance').replace('{{amount}}', (walletBalance ?? 0).toLocaleString('en-IN'))}</p>
            <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 10px', textAlign: 'center' }}>{t('walletRechargeMinHint')}</p>
            <WalletRechargePanel
              t={t}
              userId={CLIENT.id || CLIENT.user_id}
              userName={clientName}
              userEmail={CLIENT.email || 'contact@developmentexpress.in'}
              userPhone={CLIENT.phone || '+918408000084'}
              rechargeAmt={rechargeAmt}
              setRechargeAmt={setRechargeAmt}
              onBalanceUpdated={handleWalletCredited}
              isMobile={isMobile}
            />
            <div style={{ display: 'flex', marginTop: '12px' }}>
              <button type="button" style={{ ...s.cancelBtn, width: isMobile ? '100%' : undefined, minHeight: isMobile ? '44px' : undefined }} onClick={() => setShowRecharge(false)}>{t('cancelText')}</button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && cancelBooking && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '95%' : '430px' }}>
            <h3 style={{ ...s.modalTitle, color: '#e94560' }}>{t('cancelBookingPrompt')}</h3>
            <div style={s.modalDetail}>
              <div style={s.modalRow}><span style={{ color: '#8896a8' }}>{t('machineLabel')}</span><span style={{ color: '#c9a84c' }}>{getBookingMachineCode(cancelBooking, machineLookup)}</span></div>
              <div style={{ ...s.modalRow, borderBottom: 'none' }}><span style={{ color: '#4CAF50', fontWeight: '700' }}>{t('walletCreditFull')}</span><span style={{ color: '#4CAF50', fontWeight: '700', fontSize: '16px' }}>+ ₹{Number(cancelBooking.advancePaid || cancelBooking.advance_paid || 0).toLocaleString('en-IN')}</span></div>
            </div>
            <p style={{ color: '#8896a8', fontSize: '11px', textAlign: 'center', margin: '10px 0 15px' }}>{t('escrowNote')}</p>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: '10px' }}>
              <button style={{ ...s.cancelBtn, width: isMobile ? '100%' : undefined, minHeight: isMobile ? '44px' : undefined }} onClick={() => setShowCancelModal(false)} disabled={cancelSubmitting}>{t('cancelText')}</button>
              <button style={{ ...s.confirmBtn, background: 'linear-gradient(135deg, #c0392b, #e74c3c)', width: isMobile ? '100%' : undefined, minHeight: isMobile ? '44px' : undefined, opacity: cancelSubmitting ? 0.7 : 1 }} onClick={handleConfirmCancel} disabled={cancelSubmitting}>{cancelSubmitting ? t('loading') : t('confirmCancel')}</button>
            </div>
          </div>
        </div>
      )}

      <PhoneChangeModal
        isOpen={showPhoneChangeModal}
        onClose={() => setShowPhoneChangeModal(false)}
        userRole={CLIENT.role || 'client'}
        authToken={localStorage.getItem('developmentexpress_token') || ''}
        onPhoneChanged={handleClientPhoneChanged}
      />

      {arrivalMilestoneModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1200,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
        >
          <div style={{
            width: '100%',
            maxWidth: '430px',
            background: '#030810',
            border: '1px solid rgba(201,168,76,0.35)',
            borderRadius: '12px',
            padding: '18px',
          }}
          >
            <h3 style={{ color: '#c9a84c', margin: '0 0 8px', fontSize: '15px' }}>
              Confirm Machine Arrival
            </h3>
            <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 14px' }}>
              Booking #{arrivalMilestoneModal.booking_ref || arrivalMilestoneModal.id}
            </p>
            <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '12px' }}>
              Site arrival photo (required)
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setArrivalPhotoFile(e.target.files?.[0] || null)}
                style={{ display: 'block', marginTop: '6px', width: '100%', color: '#e8e0d0', fontSize: '12px' }}
              />
            </label>
            {arrivalMilestoneError && (
              <p style={{ color: '#e94560', fontSize: '11px', margin: '0 0 10px' }}>{arrivalMilestoneError}</p>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => {
                  setArrivalMilestoneModal(null);
                  setArrivalPhotoFile(null);
                  setArrivalMilestoneError('');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#8896a8',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={Boolean(arrivalMilestoneBusyId)}
                onClick={handleConfirmArrivalMilestone}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                  border: 'none',
                  color: '#030810',
                  borderRadius: '8px',
                  fontWeight: 800,
                  cursor: arrivalMilestoneBusyId ? 'wait' : 'pointer',
                  opacity: arrivalMilestoneBusyId ? 0.7 : 1,
                }}
              >
                {arrivalMilestoneBusyId ? t('loading') : 'Confirm Arrival'}
              </button>
            </div>
          </div>
        </div>
      )}

      {terminationModal && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '95%' : '430px', background: '#030810', border: '1px solid rgba(233,69,96,0.35)' }}>
            <h3 style={{ ...s.modalTitle, color: '#ff9aa8' }}>Request contract termination</h3>
            <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '10px' }}>
              Termination type
              <select
                value={terminationType}
                onChange={(e) => setTerminationType(e.target.value)}
                style={{
                  width: '100%',
                  marginTop: '6px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: '#0a1628',
                  color: '#e8e0d0',
                }}
              >
                <option value="client">Client</option>
                <option value="mutual">Mutual</option>
              </select>
            </label>
            {(() => {
              const fee = estimateTerminationFee(terminationModal, terminationType);
              return (
                <div style={{
                  marginBottom: '10px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(0,0,0,0.35)',
                  fontSize: '12px',
                  color: '#e8e0d0',
                }}
                >
                  {terminationType === 'mutual' ? (
                    <p style={{ margin: 0 }}>Estimated fee: ₹0 (no early-termination fee)</p>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 4px' }}>
                        Est. remaining cycles: {fee.remaining} (fee capped at {fee.feeCycles} × 25%)
                      </p>
                      <p style={{ margin: 0, fontWeight: 700, color: '#ff9aa8' }}>
                        Est. fee: ₹{fee.net.toLocaleString('en-IN')}
                        {' '}(₹{fee.gross.toLocaleString('en-IN')} + GST ₹{fee.gst.toLocaleString('en-IN')})
                      </p>
                    </>
                  )}
                </div>
              );
            })()}
            <p style={{ color: '#FFB74D', fontSize: '11px', margin: '0 0 12px' }}>
              15-day notice period applies
            </p>
            {terminationError && (
              <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 10px' }}>{terminationError}</p>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                style={s.cancelBtn}
                disabled={terminationSubmitting}
                onClick={() => setTerminationModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={terminationSubmitting}
                onClick={handleInitiateTermination}
                style={{
                  ...s.confirmBtn,
                  background: 'rgba(80,20,30,0.7)',
                  border: '1px solid rgba(233,69,96,0.5)',
                  color: '#ff9aa8',
                  opacity: terminationSubmitting ? 0.7 : 1,
                }}
              >
                {terminationSubmitting ? 'Submitting…' : 'Confirm request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {ownerDefaultModal && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '95%' : '430px', background: '#030810', border: '1px solid rgba(201,120,132,0.35)' }}>
            <h3 style={{ ...s.modalTitle, color: '#c97884' }}>Report Owner Default</h3>
            <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '10px' }}>
              Default type
              <select
                value={ownerDefaultType}
                onChange={(e) => setOwnerDefaultType(e.target.value)}
                style={{
                  width: '100%',
                  marginTop: '6px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: '#0a1628',
                  color: '#e8e0d0',
                }}
              >
                <option value="no_show">No-show</option>
                <option value="abandonment">Abandonment</option>
                <option value="breakdown">Breakdown</option>
              </select>
            </label>
            {ownerDefaultType === 'breakdown' && (
              <label style={{ display: 'block', color: '#8896a8', fontSize: '11px', marginBottom: '10px' }}>
                Breakdown days
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={ownerDefaultBreakdownDays}
                  onChange={(e) => setOwnerDefaultBreakdownDays(e.target.value)}
                  style={{
                    width: '100%',
                    marginTop: '6px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: '#0a1628',
                    color: '#e8e0d0',
                    boxSizing: 'border-box',
                  }}
                />
              </label>
            )}
            {ownerDefaultError && (
              <p style={{ color: '#e94560', fontSize: '12px', margin: '0 0 10px' }}>{ownerDefaultError}</p>
            )}
            {ownerDefaultSuccess && (
              <p style={{ color: '#4CAF50', fontSize: '12px', margin: '0 0 10px', fontWeight: 700 }}>
                {ownerDefaultSuccess}
              </p>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                style={s.cancelBtn}
                disabled={ownerDefaultSubmitting}
                onClick={() => setOwnerDefaultModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={ownerDefaultSubmitting || Boolean(ownerDefaultSuccess)}
                onClick={handleReportOwnerDefault}
                style={{
                  ...s.confirmBtn,
                  background: 'rgba(90,24,32,0.55)',
                  border: '1px solid rgba(201,120,132,0.45)',
                  color: '#c97884',
                  opacity: ownerDefaultSubmitting ? 0.7 : 1,
                }}
              >
                {ownerDefaultSubmitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBlacklistWarning && (
        <div style={s.modalOverlay}>
          <div style={{ ...s.modal, width: isMobile ? '95%' : '430px' }}>
            <h3 style={{ ...s.modalTitle, color: '#e94560' }}>{t('blacklistWarning')}</h3>
            <div style={{ background: 'rgba(233,69,96,0.1)', border: '1px solid rgba(233,69,96,0.4)', borderRadius: '10px', padding: '16px', marginBottom: '18px' }}>
              <p style={{ color: '#e94560', fontWeight: '700', fontSize: '13px', margin: '0 0 8px', textAlign: 'center' }}>{t('directContactProhibitedText')}</p>
              {[t('blacklistWarnClientMachine'), t('blacklistWarnBookingsCancelled'), t('blacklistWarnWalletFrozen'), t('blacklistWarnLegalAction')].map((item, i) => (
                <p key={i} style={{ color: '#e94560', fontSize: '12px', margin: '4px 0' }}>X {item}</p>
              ))}
            </div>
            <button style={s.confirmBtn} onClick={() => setShowBlacklistWarning(false)}>{t('understoodAction')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { display: 'flex', height: '100svh', maxHeight: '100svh', overflow: 'hidden', background: '#050d1a', fontFamily: 'Arial, sans-serif', color: '#fff', width: '100%' },
  containerMobile: { position: 'relative', display: 'block', minHeight: '100svh', width: '100%', maxWidth: '100vw', overflowX: 'hidden', background: '#050d1a', fontFamily: 'Arial, sans-serif', color: '#fff' },
  scrollShell: { position: 'relative', zIndex: 10, display: 'flex', flex: '1 1 0', flexDirection: 'column', minWidth: 0, minHeight: 0, width: '100%', overflow: 'hidden' },
  mainMobile: { display: 'block', width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
  sidebar: { position: 'sticky', top: 0, alignSelf: 'flex-start', width: '230px', height: '100dvh', maxHeight: '100dvh', background: 'linear-gradient(180deg, #0f2040 0%, #0a1628 100%)', borderRight: '1px solid rgba(201,168,76,0.2)', padding: '20px 15px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0, minHeight: 0, overflow: 'hidden' },
  sidebarNavScroll: { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', marginRight: '-4px', paddingRight: '4px' },
  sidebarLogo: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  sidebarHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' },
  sidebarHeaderBrand: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 },
  sidebarToggleBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.1)', color: '#c9a84c', cursor: 'pointer', flexShrink: 0 },
  sidebarOpenFab: { position: 'fixed', left: 12, top: 'calc(18px + env(safe-area-inset-top, 0px))', zIndex: 200, background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: '#C9A84C' },
  logoCircle: { width: '38px', height: '38px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: '#0a1628', fontSize: '13px', flexShrink: 0 },
  logoTitle: { color: '#c9a84c', fontWeight: '700', fontSize: '12px', margin: 0 },
  logoSub: { color: '#8896a8', fontSize: '10px', margin: 0 },
  divider: { height: '1px', background: 'rgba(201,168,76,0.15)', margin: '8px 0' },
  nav: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: '#8896a8', cursor: 'pointer', fontSize: '12px', width: '100%', textAlign: 'left' },
  navActive: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.6)', background: 'linear-gradient(to right, rgba(201,168,76,0.25), rgba(201,168,76,0.05))', boxShadow: '0 0 24px rgba(201,168,76,0.2)', color: '#c9a84c', cursor: 'pointer', fontSize: '12px', width: '100%', textAlign: 'left', fontWeight: '700' },
  walletBox: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '10px', padding: '12px', textAlign: 'center', margin: '5px 0' },
  walletLabel: { color: '#8896a8', fontSize: '9px', margin: '0 0 4px', letterSpacing: '1px' },
  walletAmount: { color: '#c9a84c', fontSize: '18px', fontWeight: '700', margin: '0 0 4px' },
  rechargeBtn: { background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', width: '100%' },
  warningBox: { background: 'rgba(233,69,96,0.08)', border: '1px solid rgba(233,69,96,0.25)', borderRadius: '8px', padding: '8px', textAlign: 'center', cursor: 'pointer', margin: '4px 0' },
  warningText: { color: '#e94560', fontSize: '10px', margin: 0, fontWeight: '600' },
  logoutBtn: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.3)', background: 'rgba(233,69,96,0.08)', color: '#e94560', cursor: 'pointer', fontSize: '12px', width: '100%', marginTop: 'auto' },
  sidebarFooter: { color: 'rgba(201,168,76,0.4)', fontSize: '9px', textAlign: 'center', marginTop: '8px', letterSpacing: '1px' },
  main: { flex: '1 1 0', minWidth: 0, minHeight: 0, overflowY: 'scroll', overflowX: 'hidden', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '18px', borderBottom: '1px solid rgba(201,168,76,0.15)' },
  pageTitle: { color: '#c9a84c', fontWeight: '700', margin: '0 0 4px' },
  pageDate: { color: '#8896a8', fontSize: '11px', margin: 0 },
  clientBadge: { display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', padding: '8px 14px', borderRadius: '20px', color: '#c9a84c', fontSize: '12px' },
  onlineDot: { width: '7px', height: '7px', background: '#4CAF50', borderRadius: '50%', display: 'inline-block' },
  infoBanner: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '10px', padding: '10px 12px', display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '18px' },
  cardRow: { display: 'grid', gap: '12px', marginBottom: '18px' },
  card: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '16px', textAlign: 'center' },
  cardIcon: { fontSize: '22px', margin: '0 0 6px' },
  cardVal: { color: '#c9a84c', fontSize: '18px', fontWeight: '700', margin: '0 0 4px' },
  cardLbl: { color: '#8896a8', fontSize: '11px', margin: 0 },
  sectionTitle: { color: '#c9a84c', marginBottom: '12px', fontSize: '14px' },
  machineCard: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '12px' },
  machineHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' },
  machineName: { color: '#c9a84c', fontWeight: '700', fontSize: '14px', margin: '0 0 3px' },
  machineType: { color: '#8896a8', fontSize: '11px', margin: '0 0 2px' },
  availBadge: { padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: '600', flexShrink: 0 },
  fuelRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' },
  fuelLabel: { color: '#8896a8', fontSize: '11px', width: '40px', flexShrink: 0 },
  fuelBarBg: { flex: 1, height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px' },
  fuelBarFill: { height: '100%', borderRadius: '3px' },
  rateCardBox: { background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px', padding: '10px', marginBottom: '10px' },
  rateCardTitle: { color: 'rgba(201,168,76,0.7)', fontSize: '10px', letterSpacing: '1px', margin: '0 0 8px' },
  gstNote: { color: 'rgba(201,168,76,0.4)', fontSize: '9px', margin: '6px 0 0' },
  operatorBox: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  operatorAvatar: { width: '32px', height: '32px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0a1628', fontWeight: '700', fontSize: '12px', flexShrink: 0 },
  operatorName: { color: '#e8e0d0', fontSize: '12px', fontWeight: '600', margin: '0 0 2px' },
  operatorExp: { color: '#8896a8', fontSize: '10px', margin: 0 },
  selectBtn: { width: '100%', padding: '10px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  goBtn: { background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', padding: '11px 28px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  calcCard: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '20px' },
  calcTitle: { color: '#c9a84c', margin: '0 0 16px', fontSize: '15px', fontWeight: '700' },
  selectedBox: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '12px', marginBottom: '15px' },
  selLabel: { color: '#8896a8', fontSize: '10px', margin: '0 0 4px', letterSpacing: '1px' },
  selName: { color: '#c9a84c', fontWeight: '700', fontSize: '14px', margin: '0 0 2px' },
  bookNowBtn: { width: '100%', padding: '13px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '800', cursor: 'pointer', marginBottom: '8px' },
  detailCard: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '20px' },
  tableCard: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '18px', marginBottom: '20px' },
  tableTitle: { color: '#c9a84c', margin: '0 0 14px', fontSize: '14px' },
  alertRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' },
  downloadBtn: { background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 10px', textAlign: 'left', color: 'rgba(201,168,76,0.7)', fontSize: '10px', letterSpacing: '1px', borderBottom: '1px solid rgba(201,168,76,0.15)', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid rgba(255,255,255,0.04)' },
  td: { padding: '10px', fontSize: '12px', color: '#e8e0d0', whiteSpace: 'nowrap' },
  machineTag: { background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 999, padding: 'calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 16px)', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
  modal: { background: 'linear-gradient(135deg, #0f2040, #0a1628)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '16px', padding: '24px', boxShadow: '0 30px 80px rgba(0,0,0,0.8)', maxHeight: 'min(90dvh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 32px))', overflowY: 'auto', WebkitOverflowScrolling: 'touch', margin: 'auto 0' },
  modalTitle: { color: '#c9a84c', fontSize: '16px', margin: '0 0 16px', textAlign: 'center' },
  modalDetail: { background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '12px', marginBottom: '12px' },
  modalRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '13px', color: '#e8e0d0' },
  cancelBtn: { flex: 1, padding: '11px', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#8896a8', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  confirmBtn: { flex: 1, padding: '11px', background: 'linear-gradient(135deg, #a07830, #e2c97e)', color: '#0a1628', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
};

export default ClientDashboard;







